import { createContext, useCallback, useContext, useEffect, useState } from "react";

const fr = {
  nav: {
    airport: "Transferts aéroport", services: "Services", fleet: "Flotte", business: "Business", drivers: "Chauffeurs",
    cities: "Villes", faq: "FAQ", track: "Suivi", book: "Réserver", book_ride: "Réserver un trajet", guides: "Guides",
  },
  bar: {
    one_way: "Aller simple", round_trip: "Aller-retour", hourly: "À l'heure",
    available: "Chauffeurs disponibles", from: "Départ", to: "Destination", date: "Date", time: "Heure",
    from_ph: "Adresse de départ", to_ph: "Adresse d'arrivée",
    hourly_note: "Votre chauffeur reste à disposition, de 2 à 24 heures.",
    submit: "Voir les véhicules",
    ticker: "En direct — Alexandre arrive dans 8 min · EQE · GE 512 408",
    missing: "Adresse manquante", missing_desc: "Indiquez une adresse de départ et de destination.",
  },
  addr: {
    loading: "Recherche d'adresses…",
    empty: "Aucune suggestion — vous pouvez continuer avec votre adresse.",
    unavailable: "Suggestions indisponibles — votre adresse libre reste acceptée.",
    attribution: "© OpenStreetMap contributors / Photon",
  },
  hero: {
    img_alt: "Berline MILES sur les quais de Genève",
    t1: "Votre chauffeur privé,", t2: "sans place à l'imprévu.",
    sub: "Réservez votre trajet, suivez votre chauffeur, retrouvez chaque information au même endroit. Le prix est confirmé avant le départ — nous nous occupons du reste.",
    sub_short: "Là où chaque kilomètre semble différent.",
  },
  manifesto: {
    eyebrow: "Manifeste MILES",
    t1: "Votre trajet.", t2: "Notre organisation.",
    p: "Une réservation suffit à tout déclencher : chauffeur confirmé, vol suivi, itinéraire réel, lien de suivi à partager.",
    cta: "Voir comment ça fonctionne",
    chapters: [
      ["Vous réservez.", "Deux adresses, un horaire, un véhicule. Le prix fixe est affiché avant de payer — la confirmation est immédiate."],
      ["Vous suivez.", "Chauffeur, véhicule, plaque, arrivée estimée : tout est visible sur votre lien, minute par minute."],
      ["Vous arrivez.", "Retard de vol absorbé, point de rencontre clair, reçu prêt. Rien à gérer."],
    ],
    photo_alt: "Berline MILES garée devant un hôtel suisse",
    photo_label: "Genève — Centre → Aéroport GVA",
    photo_status: "Départ confirmé",
    photo_price: "CHF 74.– · prix fixe",
    card_note: "Chauffeur confirmé avant le départ",
  },
  homeFleet: {
    eyebrow: "La flotte MILES",
    h2a: "Quatre véhicules.", h2b: "Un choix simple.",
    p: "Chaque catégorie répond à un usage : trajet du quotidien, déplacement d'affaires, grande arrivée, groupe. Le prix exact s'affiche avant tout paiement.",
    cta: "Explorer toute la flotte",
    items: {
      standard: { name: "Standard Class", model: "Tesla Model Y", desc: "Le même soin MILES, au prix le plus accessible — pour les trajets du quotidien." },
      van: { name: "VAN", model: "Mercedes Classe V", desc: "Pour voyager à plusieurs — skis et bagages compris — sans compliquer l'organisation." },
      first: { name: "First Class", model: "Mercedes Classe S", desc: "Plus d'espace et de calme, quand le trajet compte davantage." },
      business: { name: "Business Class", model: "Mercedes EQE ou Classe E", desc: "Pour les trajets du quotidien, les rendez-vous et les transferts aéroport." },
    },
  },
  statuses: {
    booked: "Confirmée", driver_assigned: "Chauffeur confirmé", driver_on_the_way: "En route",
    driver_arrived: "Arrivé", on_board: "À bord", completed: "Terminé", cancelled: "Annulée",
  },
  homeTrack: {
    eyebrow: "Le suivi MILES", h2: "Vous savez toujours ce qui se passe.", cta: "Ouvrir un suivi en direct",
    chip: "Chauffeur en route", arrive: "Alexandre arrive dans 8 min.",
    car: "Mercedes EQE · Noir obsidienne", call: "Appeler", message: "Message",
    eta: "Arrivée estimée", caption: "Le système de statuts",
    notif_t: "MILES · maintenant", notif_b: "Votre chauffeur arrivera dans environ 8 min.",
  },
  expect: {
    eyebrow: "Concret", h2a: "Vous savez à quoi", h2b: "vous attendre.",
    p: "Chaque course MILES suit le même fil — de la réservation au reçu. Voici ce fil, tel quel.",
    items: [
      ["Prix confirmé avant de réserver", "Le tarif affiché est le tarif payé."],
      ["Confirmation immédiate", "Résumé, numéro de course, lien de suivi."],
      ["Chauffeur confirmé", "Prénom, photo, véhicule, plaque."],
      ["Trajet suivi en direct", "Position, statuts, arrivée estimée."],
      ["Vol surveillé", "Retard ? La prise en charge s'ajuste."],
      ["Une vraie personne vous répond", "L'équipe MILES, à Genève — pas un chatbot."],
    ],
  },
  interlude: {
    eyebrow: "De ville à ville", h2a: "Genève → Milan.", h2b: "Quatre heures à vous.",
    cta: "Réserver un longue distance",
  },
  homeAirport: {
    eyebrow: "Transferts aéroport", h2a: "Votre vol bouge.", h2b: "Votre prise en charge aussi.",
    p: "Indiquez votre numéro de vol à la réservation : en cas de retard, la prise en charge est ajustée et votre chauffeur informé. Sans rien vous demander.",
    cta: "Voir l'expérience aéroport",
    route: "Zurich → Genève", delay: "Retard +22 min",
    l1: "Atterrissage prévu", l2: "Atterrissage réel", l3: "Prise en charge",
    adjusted: "Votre prise en charge a été ajustée.",
    meeting: "Alexandre vous attendra — Arrivées 2", meeting_l: "Point de rencontre",
  },
  homeBiz: {
    eyebrow: "MILES Business", h2: "Tous les trajets de l'entreprise. Une seule organisation.",
    p: "Réservez pour un collaborateur, un dirigeant ou un invité — sans compte pour lui. Suivez chaque course en parallèle. Une facture consolidée à la fin du mois.",
    demo: "Demander une démo", explore: "Explorer le produit",
    today: "Aujourd'hui — Maison Démo SA", new: "Nouvelle réservation",
    side: ["Overview", "Bookings", "Passengers", "Invoices", "Team"],
    for: ["Pour moi", "Collaborateur", "Invité"],
    noacct: "Le passager n'a besoin d'aucun compte",
    ride_status: ["En route", "Confirmé", "Chauffeur confirmé"],
    inv_t: "Facture INV-2026-047 prête", inv_b: "96 trajets consolidés · export comptable",
  },
  citiesCta: {
    eyebrow: "Couverture", h2a: "Genève. Lausanne. Zurich.", h2b: "Bâle. Berne. Et demain, plus loin.",
    book: "Réserver un trajet", cities: "Voir la couverture",
  },
  footer: {
    tag: "MILES organise vos trajets avec chauffeur en Suisse — de la réservation à l'arrivée.",
    book: "Réserver", business: "MILES Business",
    cols: ["Produit", "Entreprises", "Société"],
    links: [
      ["Nos services", "Réserver", "Transferts aéroport", "Flotte & tarifs", "Villes & couverture"],
      ["MILES Business", "Dashboard — démo", "Hôtels & conciergerie"],
      ["Chauffeurs", "FAQ", "Contact", "Guides MILES"],
    ],
    legal: ["Conditions", "Confidentialité", "Cookies"],
    copy: "© 2026 MILES — Tous droits réservés",
  },
  bk: {
    steps: ["Trajet", "Véhicule", "Voyageur", "Paiement"],
    questions: ["Où et quand partez-vous ?", "Choisissez votre véhicule", "Pour qui est ce trajet ?", "Finalisez votre réservation"],
    help: "Besoin d’aide ?", route_step_sub: "Adresse, horaire et passagers — le trajet prend forme immédiatement.",
    pax: "Voyageurs", pax_one: "passager", pax_many: "passagers", bags: "Bagages", bag_one: "bagage", bag_many: "bagages",
    pax_summary: "{p} passager(s) · {b} bagage(s)", swap_route: "Inverser le trajet",
    flight_context: "Vous voyagez en avion ?", flight_context_sub: "Nous suivrons votre vol et adapterons la prise en charge si nécessaire.",
    trip_card_title: "Votre trajet", trip_card_sub: "Il se construit au fur et à mesure.", trip_summary_pending: "Ajoutez le départ et la destination",
    edit: "Modifier", cta_vehicles: "Voir les véhicules", cta_traveler: "Choisir ce véhicule", cta_payment: "Aller au paiement",
    vehicle_step_sub: "Prix fixe. Chauffeur inclus. Aucun frais caché.",
    multi_hint: "Besoin de plusieurs véhicules ? Sélectionnez-les tous et ajustez les quantités.",
    vehicles_word: "véhicules",
    units_title: "Un lien de suivi par véhicule",
    payment_sub: "Le montant affiché est le prix confirmé de votre trajet.", pay_t: "Paiement sécurisé", final_pay: "Réserver pour {p}",
    promo_label: "Code promo", promo_ph: "Entrez votre code", promo_apply: "Appliquer", promo_remove: "Retirer",
    promo_invalid: "Code invalide ou expiré.", promo_total: "Total après remise",
    paid_t: "Paiement confirmé", paid_b: "Votre réservation MILES est créée.",
    add_request: "Ajouter une demande particulière", fname: "Prénom", lname: "Nom", view_booking: "Voir ma réservation",
    done_sub: "Nous vous écrivons dès que votre chauffeur est confirmé.",
    trust_items: ["Prix fixe confirmé avant paiement", "Chauffeur vérifié et identifié avant le départ", "Une vraie personne vous répond"],
    options_title: "Options utiles", option_child_seat: "Siège enfant", option_meet_greet: "Pancarte nominative",
    options_note: "Ces demandes sont transmises à l'équipe MILES et confirmées avant le départ.",
    terms_text: "J'accepte les conditions de réservation MILES.", terms_view: "Voir les conditions", terms_hide: "Masquer les conditions",
    terms_items: ["Le prix affiché est confirmé avant le paiement.", "Vos coordonnées servent à coordonner le trajet et envoyer le suivi.", "Les demandes particulières sont confirmées par MILES avant le départ.", "Un vol renseigné est suivi pour ajuster la prise en charge."],
    cta_notes: ["Itinéraire et prix réels calculés instantanément.", "Prix fixe, chauffeur inclus, aucun frais caché.", "Vos informations servent uniquement à coordonner le trajet.", "Vous recevrez immédiatement confirmation et lien de suivi."],
    back: "Retour", next: "Continuer", to_payment: "Passer au paiement",
    secure: "Prix fixe confirmé avant paiement",
    from: "Départ", to: "Destination",
    from_ph: "Ex. Rue du Rhône 24, Genève", to_ph: "Ex. Aéroport Genève GVA",
    diff: "Choisissez deux adresses différentes.",
    duration: "Durée", schedule_t: "Départ & retour",
    flight_t: "Vol — nous suivons votre avion",
    flight_ph: "N° de vol — ex. LX 318", terminal_ph: "Terminal — optionnel",
    flight_note: "En cas de retard, nous ajustons l'heure de prise en charge. L'attente à l'aéroport est incluse.",
    date: "Date", time: "Heure", return_t: "Trajet retour",
    passengers: "Passagers", luggage: "Bagages",
    notes: "Notes & demandes particulières",
    notes_ph: "Siège enfant, arrêt intermédiaire, consigne spécifique…",
    est_loading: "Calcul de l'estimation…",
    est_line: "{km} km · environ {min} min · prix fixe garanti",
    est_hourly: "{h} h de mise à disposition · départ {from}",
    est_unavailable: "Estimation indisponible — revenez à l'étape précédente.",
    info_note: "Aucun compte nécessaire. Vos informations servent uniquement à cette course. Vous réservez pour quelqu'un d'autre ? Indiquez ses coordonnées ici.",
    firstname: "Prénom", lastname: "Nom", email: "E-mail",
    phone_ph: "Téléphone — ex. +41 79 123 45 67",
    card: "Carte bancaire",
    secure_note: "Paiement chiffré. Le montant n'est débité qu'à la confirmation — jamais de supplément après la course.",
    pay_loading: "Chargement du paiement sécurisé…",
    pay_methods_note: "Paiement sécurisé traité par SumUp — MILES ne voit jamais vos données de carte.",
    pay_online: "En ligne", pay_online_h: "Carte bancaire — paiement immédiat et sécurisé (SumUp)",
    pay_card_board: "Carte à bord", pay_card_board_h: "Terminal de paiement auprès du chauffeur",
    pay_cash: "Espèces", pay_cash_h: "Règlement auprès du chauffeur",
    pay_card_note: "Réglez par carte bancaire auprès de votre chauffeur (terminal SumUp). Votre réservation est confirmée dès maintenant.",
    pay_cash_note: "Réglez en espèces auprès de votre chauffeur. Votre réservation est confirmée dès maintenant.",
    final_board: "Confirmer la réservation — {p} à bord",
    booked_t: "Réservation confirmée", booked_b: "Règlement à bord auprès de votre chauffeur.",
    pay: "Payer {p}", paying: "Paiement en cours…",
    pay_err_t: "Paiement impossible", pay_err_d: "Vérifiez vos informations et réessayez.", pay_declined: "Le paiement a été refusé. Vérifiez vos informations ou utilisez une autre carte.",
    pay_pending_t: "Paiement en cours de vérification", pay_pending_b: "Votre banque finalise l'autorisation. Vous recevrez l'e-mail de confirmation de la course {code} dès validation.",
    duration_km: "{h} h de mise à disposition · {km} km inclus", duration_km_short: "{km} km inclus", from_hourly: "Lieu de prise en charge",
    hourly_km_note: "{km} km inclus · au-delà : {rate}/km",
    est_err: "Estimation indisponible",
    confirm_title: "C'est réservé.", code_prefix: "Réservation",
    l_ride: "Trajet", l_dt: "Date & heure", l_vehicle: "Véhicule", l_amount: "Montant",
    l_flight: "Vol suivi", l_pax: "Passagers",
    paid: "payé", return_word: "retour",
    hourly_line: "Mise à disposition {h} h — {from}",
    email_note: "Un e-mail de confirmation part à l'instant. Dès que votre chauffeur est confirmé, vous recevez son prénom, son véhicule et sa plaque par e-mail.",
    track_cta: "Suivre mon trajet", again: "Réserver un autre trajet",
    demand_sent: "Demande {t} envoyée", demand_desc: "Confirmation par e-mail en moins de 2 minutes.",
    rail_title: "Votre trajet", rail_from: "Adresse de départ", rail_to: "Adresse d'arrivée",
    rail_hourly: "{h} h de mise à disposition",
    rail_type: "Type", rail_date: "Date", rail_time: "Heure", rail_pax: "Voyageurs", rail_flight: "Vol suivi",
    rail_route: "Itinéraire", rail_datetime: "Départ", rail_real: "Route réelle", rail_vehicle: "Véhicule",
    rail_price_note: "Le prix fixe s'affiche au choix du véhicule — avant tout paiement.",
    rail_fixed: "Prix fixe", price_guaranteed: "Garanti",
    fixed_price: "Prix fixe", route_map_alt: "Aperçu de l'itinéraire", route_map_pending: "Itinéraire en attente", route_map_ready: "Prêt à partir",
    vehicle_route: "{min} min de route", vehicle_select: "Choisir", vehicle_selected: "Sélectionné",
    for_me: "Pour moi", for_guest: "Pour un invité",
    guest_note: "Le passager recevra le lien de suivi et les informations du chauffeur. Vous gardez le contrôle de la réservation et du paiement.",
    retry_estimate: "Recalculer le prix", est_unavailable_hint: "Vérifiez l'adresse ou réessayez. Vous pouvez aussi revenir à l'étape Trajet.",
    hourly_word: "Mise à disposition",
    types: { one_way: "Aller simple", round_trip: "Aller-retour", hourly: "À l'heure" },
  },
  veh: {
    pax: "{n} passagers", bags: "{n} bagages",
    standard: { name: "Standard Class", model: "Tesla Model Y ou équivalent", editorial: "Le même chauffeur et le même suivi que toutes les classes MILES — au prix le plus accessible.", features: ["Prix accessible", "100 % électrique", "Silencieux & récent"] },
    business: { name: "Business Class", model: "Mercedes EQE ou Classe E", editorial: "Pour les trajets du quotidien, les rendez-vous et les transferts aéroport.", features: ["Wi-Fi à bord", "Eau & chargeurs", "Accueil pancarte aéroport"] },
    first: { name: "First Class", model: "Mercedes Classe S ou équivalent", editorial: "Plus d'espace et de calme, quand le trajet compte davantage.", features: ["Attente 60 min incluse", "Sièges confort arrière", "Presse du jour"] },
    van: { name: "VAN", model: "Mercedes Classe V ou équivalent", editorial: "Pour voyager à plusieurs sans compliquer l'organisation.", features: ["6 passagers", "Grand coffre", "Idéal groupes & skis"] },
  },
  fleet: {
    eyebrow: "La flotte MILES", h1a: "Le bon véhicule,", h1b: "pour chaque trajet.",
    sub: "Quatre catégories claires, des véhicules récents et impeccables, et le même niveau de finition à chaque course. Le prix exact s'affiche dans le parcours de réservation, avant tout paiement.",
    trust: ["Prix fixe garanti", "Annulation gratuite", "Chauffeurs vérifiés MILES"],
    glance_eyebrow: "Vue d'ensemble", glance_h2a: "Quatre classes.", glance_h2b: "Un choix simple.",
    cat: "Catégorie", from: "dès CHF {n}.–", book: "Réserver ce véhicule",
    models_t: "Modèles illustrés", onboard_t: "À bord", ideal_t: "Idéal pour",
    price_note: "Prix exact affiché avant tout paiement.",
    classes: {
      standard: {
        tagline: "L'essentiel MILES, au juste prix.",
        editorial: "Silencieuse et récente, avec le même chauffeur et le même suivi que toutes les classes MILES : le quotidien, bien organisé.",
        models: ["Tesla Model Y", "Tesla Model 3 ou équivalent"],
        onboard: ["Habitacle silencieux 100 % électrique", "Chargeurs USB-C", "Climatisation bi-zone"],
        ideal: ["Trajets du quotidien", "Transferts aéroport", "Ville à ville"],
        wait: "45 min", hourly: "dès CHF 60.–/h",
        interior_alt: "Berline Standard Class MILES", interior_caption: "Sur la route — Lausanne → Genève",
      },
      business: {
        tagline: "Le standard des trajets d'affaires.",
        editorial: "Ponctuelle et silencieuse, avec Wi-Fi et chargeurs : le travail continue entre deux rendez-vous — ou jusqu'à l'aéroport.",
        models: ["Mercedes EQE", "Mercedes Classe E", "BMW Série 5 ou équivalent"],
        onboard: ["Wi-Fi à bord", "Eau minérale & chargeurs USB-C", "Accueil pancarte en aéroport", "Habitacle silencieux, climatisation bi-zone"],
        ideal: ["Trajets d'affaires", "Transferts aéroport", "Ville à ville"],
        wait: "45 min", hourly: "dès CHF 85.–/h",
        interior_alt: "Berline MILES Business sur route de nuit", interior_caption: "Sur la route — Genève → Zurich",
      },
      first: {
        badge: "Signature MILES",
        tagline: "Quand le trajet compte davantage.",
        editorial: "Habitacle arrière généreux, sièges inclinables, chauffeur senior : pour les arrivées qui comptent et les longues distances.",
        models: ["Mercedes Classe S", "BMW Série 7 ou équivalent"],
        onboard: ["Attente 60 min incluse en aéroport", "Sièges confort arrière inclinables", "Boissons fraîches & presse du jour", "Chauffeur MILES expérimenté"],
        ideal: ["Arrivées qui comptent", "Longue distance", "Occasions spéciales"],
        wait: "60 min", hourly: "dès CHF 100.–/h",
        interior_alt: "Habitacle arrière First Class", interior_caption: "Habitacle First Class",
      },
      van: {
        tagline: "L'espace, sans compromis.",
        editorial: "Six vraies places et un coffre qui prend skis et bagages : équipes, familles et événements voyagent ensemble, simplement.",
        models: ["Mercedes Classe V", "Mercedes EQV ou équivalent"],
        onboard: ["6 passagers en configuration réelle", "Skis, vélos & matériel volumineux", "Deux rangées de sièges confort", "Idéal transferts de groupe"],
        ideal: ["Groupes & familles", "Séjours ski", "Événements & roadshows"],
        wait: "45 min", hourly: "dès CHF 95.–/h",
        interior_alt: "Intérieur spacieux du VAN MILES", interior_caption: "Deux rangées, six places",
      },
    },
    specs: { pax: "Passagers", bags: "Bagages", wait: "Attente aéroport incluse", hourly: "Mise à disposition" },
    included_eyebrow: "Le standard MILES", included_h2a: "Inclus dans", included_h2b: "chaque course.",
    included: [
      ["Prix fixe garanti", "Le prix affiché à la réservation est le prix payé. Trafic ou imprévus : rien ne change."],
      ["Suivi de vol & attente ajustée", "Numéro de vol renseigné ? La prise en charge suit l'atterrissage réel, pas l'horaire prévu."],
      ["Annulation gratuite", "Jusqu'à 1 h avant un trajet simple, 24 h pour une mise à disposition ou un longue distance."],
      ["Chauffeurs vérifiés MILES", "Sélection rigoureuse, formation continue, discrétion absolue — le même standard sur chaque course."],
      ["Véhicules récents & impeccables", "Une flotte récente, entretenue et inspectée avant chaque prise de service."],
      ["Une vraie personne vous répond", "L'équipe MILES est joignable avant, pendant et après votre trajet — pas un chatbot."],
    ],
    compare_eyebrow: "Comparatif", compare_h2a: "Les trois classes,", compare_h2b: "côte à côte.",
    compare_rows: { models: "Modèles illustrés", pax: "Passagers", bags: "Bagages", wait: "Attente aéroport incluse", hourly: "Mise à disposition", from: "Trajet dès", ideal: "Idéal pour" },
    note: "Modèles présentés à titre illustratif. Vous réservez une catégorie : un véhicule de cette catégorie, ou supérieure, est garanti.",
    final_eyebrow: "Prêt à partir", final_h2a: "Le prix exact,", final_h2b: "avant tout paiement.",
    final_p: "Entrez votre trajet : l'estimation réelle s'affiche immédiatement — sans compte, sans engagement.",
    final_cta: "Estimer mon trajet",
  },
  sv: {
    eyebrow: "Services MILES", h1a: "Un chauffeur,", h1b: "pour chaque situation.",
    sub: "Sept services, un seul standard : prix fixe affiché avant paiement, chauffeur vérifié, suivi en direct. Choisissez celui qui correspond à votre trajet.",
    card_cta: "Découvrir", back: "Tous les services",
    inc_eyebrow: "Inclus", inc_h2a: "Tout est inclus.", inc_h2b: "Par principe.",
    steps_eyebrow: "Le déroulé", steps_h2a: "Comment", steps_h2b: "ça se passe.",
    price_t: "Tarification", fleet_link: "Explorer la flotte",
    final_h2: "Une hésitation ? Le prix exact s'affiche en 30 secondes.",
    final_p: "Entrez votre trajet dans la réservation : l'estimation réelle apparaît immédiatement, sans compte et sans engagement.",
    final_cta: "Estimer mon trajet",
    items: {
      airport: { name: "Transferts aéroport", desc: "GVA, ZRH, BSL : accueil au terminal, attente incluse, vol suivi si vous nous le confiez." },
      p2p: { name: "Trajet direct", desc: "D'un point A à un point B, au prix affiché. La simplicité d'une course, le sérieux d'un chauffeur privé." },
      hourly: { name: "Mise à disposition", desc: "Un chauffeur et un véhicule rien qu'à vous, de 2 à 24 heures. Rendez-vous enchaînés, sans une attente." },
      long: { name: "Longue distance", desc: "Genève–Zurich, Lausanne–Milan : le confort du train, sans les quais ni les horaires." },
      corporate: { name: "MILES Business", desc: "Comptes entreprise, réservation pour un tiers, facturation consolidée mensuelle." },
      hotels: { name: "Hôtels & Conciergerie", desc: "Votre réception réserve pour vos clients en moins d'une minute, avec suivi partagé." },
      events: { name: "Événements & Groupes", desc: "Flottes coordonnées, navettes invités, coordinateur dédié le jour J." },
    },
  },
  homeSvc: {
    eyebrow: "Nos services", h2a: "Sept services.", h2b: "Un seul standard.",
    cta: "Explorer tous les services",
  },
  svc: {
    p2p: {
      name: "Trajet direct",
      h1a: "D'un point A à un point B,", h1b: "sans le moindre détour.",
      sub: "Vous entrez deux adresses, vous voyez le prix fixe immédiatement, votre chauffeur arrive à l'heure. Rien de plus — et c'est exactement l'idée.",
      usps: ["Prix fixe calculé en direct", "Chauffeur vérifié MILES", "Suivi en direct partageable"],
      included: [
        ["Prix verrouillé à la réservation", "Le montant affiché est le montant débité. Trafic ou détour : il ne bouge plus."],
        ["Prise en charge à l'adresse exacte", "Devant votre porte, votre bureau ou votre hôtel — pas au coin de la rue."],
        ["Suivi en direct", "Position du chauffeur, statut du trajet et heure d'arrivée estimée sur votre lien personnel."],
        ["Un reçu clair, tout de suite", "Dès la fin de la course, votre reçu est disponible sur votre lien de suivi."],
      ],
      steps: [
        ["Vous réservez", "Deux adresses, une date, une heure. Trente secondes, sans compte."],
        ["Votre chauffeur est confirmé", "Prénom, photo, véhicule et plaque : vous savez qui vient avant qu'il n'arrive."],
        ["Vous suivez", "Il approche ? Vous le voyez. Partagez le suivi à qui vous attend."],
        ["Vous arrivez", "Dépose au plus près, reçu sur votre lien de suivi, rien à signer."],
      ],
      price_v: "dès CHF 90.–", price_note: "Prix exact calculé sur votre itinéraire, affiché avant tout paiement.",
      final_cta: "Estimer mon trajet",
    },
    hourly: {
      name: "Mise à disposition",
      h1a: "Un chauffeur à vous,", h1b: "à l'heure.",
      sub: "Rendez-vous qui s'enchaînent, shopping, roadshow, journée de visites : votre chauffeur et votre véhicule restent disponibles entre chaque arrêt, de 2 à 24 heures.",
      usps: ["De 2 à 24 heures", "Arrêts illimités", "Le même chauffeur toute la journée"],
      included: [
        ["Zéro attente entre les rendez-vous", "Votre chauffeur vous attend sur place, voiture prête, climatisation réglée."],
        ["Itinéraire libre", "Le programme change ? Vous ajustez en cours de journée, sans nouvelle réservation."],
        ["Discrétion garantie", "Vos rendez-vous et vos conversations restent confidentiels — engagement signé."],
        ["Facturation claire à l'heure", "Un tarif horaire fixe par catégorie, connu avant de réserver."],
      ],
      steps: [
        ["Définissez la durée", "Lieu de départ, date, heure de début et nombre d'heures."],
        ["Votre chauffeur se présente", "Ponctuel, briefé sur votre première destination."],
        ["Enchaînez vos arrêts", "Chaque étape se règle à voix haute ou depuis le lien de suivi."],
        ["Terminez quand vous voulez", "La course se clôt au dernier arrêt, reçu détaillé sur votre lien de suivi."],
      ],
      price_v: "dès CHF 85.–/h", price_note: "Tarif horaire par catégorie : Business 85, VAN 95, First 100 CHF/h.",
      final_cta: "Réserver une mise à disposition",
    },
    long: {
      name: "Longue distance",
      h1a: "La ville d'à côté,", h1b: "sans les contraintes.",
      sub: "Genève–Zurich, Lausanne–Milan, Montreux–Lyon : porte à porte, à votre heure, avec vos bagages et votre calme. Le train sans les quais, l'avion sans l'aéroport.",
      usps: ["Porte à porte", "Départ à votre heure", "Prix fixe quelle que soit la circulation"],
      included: [
        ["Départ quand vous voulez", "5 h du matin ou 23 h : c'est vous qui fixez l'heure, pas un horaire."],
        ["Travail ou repos à bord", "Wi-Fi, chargeurs, silence ou conversation : le trajet devient du temps utile."],
        ["Pauses à la demande", "Café, photo, imprévu : votre chauffeur s'arrête où vous voulez."],
        ["Aller-retour optimisé", "Le retour dans la journée bénéficie d'un tarif ajusté."],
      ],
      steps: [
        ["Indiquez les deux villes", "Adresses exactes, date, heure — le prix fixe s'affiche tout de suite."],
        ["Choisissez votre véhicule", "Business pour le rendez-vous, VAN pour la famille et les bagages."],
        ["Voyagez", "Votre chauffeur gère route, trafic et péages. Vous, vous arrivez."],
        ["Retrouvez le reçu", "Clair, détaillé, disponible dès l'arrivée — prêt pour la comptabilité."],
      ],
      price_v: "dès CHF 120.–", price_note: "Prix fixe calculé sur la distance réelle, affiché avant tout paiement.",
      final_cta: "Estimer ma longue distance",
    },
    hotels: {
      name: "Hôtels & Conciergerie",
      h1a: "Le service chauffeur", h1b: "de votre établissement.",
      sub: "Conciergerie, réception, guest relations : réservez un chauffeur pour vos clients en moins d'une minute, suivez la course en direct et recevez une facture consolidée.",
      usps: ["Réservation en moins d'une minute", "Suivi partagé avec le client", "Facturation mensuelle consolidée"],
      included: [
        ["Vos clients n'ont rien à installer", "Ils reçoivent chauffeur, véhicule et lien de suivi par e-mail — sans compte ni application."],
        ["À l'image de votre maison", "Chauffeurs présentés avec soin, véhicules impeccables, accueil à la hauteur de votre standard."],
        ["Un interlocuteur dédié", "Un contact MILES pour votre équipe, qui connaît vos courses."],
        ["Zéro gestion administrative", "Les courses du mois regroupées sur une facture unique, export comptable inclus."],
      ],
      steps: [
        ["Onboarding express", "Nous créons votre espace et formons votre équipe en 30 minutes."],
        ["Réservez pour un client", "Nom, téléphone, trajet : c'est parti, même à 4 h du matin."],
        ["Le client est informé", "Prénom, photo, plaque et suivi en direct, dans sa langue."],
        ["Tout est tracé", "Historique complet, reçus et facture mensuelle centralisés."],
      ],
      price_v: "Sur devis", price_note: "Tarifs partenaires selon volume — réponse sous 24 h ouvrées.",
      final_cta: "Parler à l'équipe Business",
    },
    events: {
      name: "Événements & Groupes",
      h1a: "Vos invités,", h1b: "tous à l'heure.",
      sub: "Mariages, séminaires, lancements, transferts de délégations : nous coordonnons les véhicules, les chauffeurs et les horaires pendant que vous gérez l'événement.",
      usps: ["Flotte coordonnée", "Coordinateur dédié", "Navettes et rotations planifiées"],
      included: [
        ["Un plan de transport complet", "Points de prise en charge, rotations, rotations de véhicules : tout est orchestré à l'avance."],
        ["VAN et berlines combinés", "Groupes en Classe V, dirigeants en First Class — chacun son niveau de service."],
        ["Un coordinateur sur place", "Le jour J, un référent MILES gère les chauffeurs et absorbe les imprévus."],
        ["Suivi centralisé", "Chaque course visible en temps réel, pour vous comme pour vos invités."],
      ],
      steps: [
        ["Parlez-nous de l'événement", "Date, lieux, nombre d'invités, contraintes : nous répondons sous 24 h."],
        ["Recevez le plan", "Véhicules, chauffeurs, horaires et budget — un document clair, un seul prix."],
        ["Le jour J", "Les véhicules tournent, le coordinateur gère, vous respirez."],
        ["Une seule facture", "Toutes les courses consolidées, prêtes pour votre comptabilité."],
      ],
      price_v: "Sur devis", price_note: "Chaque événement est chiffré sur mesure — réponse sous 24 h ouvrées.",
      final_cta: "Demander un devis",
    },
  },
  bz: {
    auth: {
      panel_eyebrow: "MILES Business",
      panel_h1a: "Tous les trajets de votre organisation,", panel_h1b: "au même endroit.",
      panel_points: ["Réservation pour vos invités en quelques instants", "Facture consolidée mensuelle, PDF prêt pour la compta", "Rôles Admin, Booker et Finance"],
      panel_footer: "MILES",
      back: "Retour",
      login_t: "Connexion Business", login_s: "Accédez à l'espace de votre organisation.",
      signup_t: "Créer votre espace Business", signup_s: "Votre organisation, vos courses, vos factures — en quelques minutes.",
      org: "Nom de l'organisation", name: "Votre nom complet", email: "E-mail professionnel", password: "Mot de passe",
      password_hint: "8 caractères minimum",
      submit_login: "Se connecter", submit_signup: "Créer l'espace",
      to_signup: "Pas encore d'espace ? Créer un compte", to_login: "Déjà un compte ? Se connecter",
      err_generic: "Une erreur est survenue. Réessayez.",
    },
    nav: { overview: "Vue d'ensemble", new_ride: "Nouvelle course", rides: "Courses", passengers: "Passagers", invoices: "Factures", team: "Équipe", settings: "Paramètres" },
    roles: { admin: "Admin", booker: "Booker", finance: "Finance" },
    shell: { hello: "Bonjour, {name}", logout: "Déconnexion", back_site: "Retour au site", demo_badge: "Démo", new_ride_cta: "Nouvelle course" },
    ov: {
      today: "Aujourd'hui", today_sub: "courses du jour", live: "En cours", live_sub: "courses live",
      upcoming: "À venir", upcoming_sub: "réservations", spend: "Dépenses du mois", spend_sub: "toutes courses",
      recent: "Réservations récentes", view_all: "Tout voir", series_t: "Dépenses — 6 derniers mois",
      empty: "Aucune course pour l'instant.", rides_word: "courses", next_t: "Prochaine course",
    },
    nr: {
      t: "Nouvelle course", s: "Réservez pour un passager — il suit sa course via un lien, sans compte.",
      passenger: "Passager", saved: "Enregistré", new: "Nouveau", choose: "Choisir un passager…",
      from: "Départ", to: "Destination", date: "Date", time: "Heure", duration: "Durée",
      type_one: "Trajet simple", type_hourly: "À l'heure",
      vehicle: "Véhicule", cc: "Centre de coût", cc_none: "Aucun",
      flight: "N° de vol (facultatif)", notes: "Instructions (facultatif)",
      fname: "Prénom", lname: "Nom", phone: "Téléphone", email: "E-mail (facultatif)",
      estimating: "Calcul du prix…", estimate_hint: "Entrez le trajet pour voir les véhicules et le prix fixe.",
      submit: "Confirmer la course", done_t: "Course créée.", done_s: "Partagez ce lien de suivi au passager :",
      copy: "Copier le lien", copied: "Lien de suivi copié", again: "Nouvelle course", view_rides: "Voir les courses",
      map_caption: "{km} km · ~{min} min", price_t: "Prix fixe",
      err: "Impossible de créer la course. Vérifiez les adresses.",
    },
    rd: {
      t: "Courses", filters: { all: "Toutes", live: "En cours", upcoming: "À venir", done: "Terminées", cancelled: "Annulées" },
      cancelled_badge: "Annulée", cancel: "Annuler", cancel_yes: "Confirmer", cancel_done_t: "Course annulée",
      share: "Copier le lien de suivi", copied_t: "Lien copié", track: "Ouvrir le suivi",
      search_ph: "Rechercher (passager, code, trajet)…", route_t: "Trajet", contact_t: "Passager",
      flight_t: "Vol", notes_t: "Instructions", cc_t: "Centre de coût", by_t: "Réservé par", created_t: "Créée le",
      timeline_t: "Historique", open_track: "Ouvrir le suivi", rebook: "Rebooker",
      empty: "Aucune course dans ce filtre.", err: "Action impossible pour le moment.",
    },
    ps: {
      t: "Passagers", s: "Votre carnet de passagers — réservez pour eux en un clic.",
      add: "Ajouter un passager", trips: "{n} courses", book: "Réserver", del: "Supprimer",
      empty: "Aucun passager enregistré. Ajoutez votre premier passager.", save: "Enregistrer",
      saved_t: "Passager ajouté", updated_t: "Passager mis à jour", search_ph: "Rechercher un passager…",
      edit: "Modifier", edit_t: "Modifier le passager", err: "Impossible d'enregistrer le passager.",
    },
    inv: {
      t: "Factures", s: "Consolidation mensuelle automatique de vos courses.",
      trips: "{n} courses", en_cours: "En cours", emise: "Émise", payee: "Payée",
      mark_paid: "Marquer payée", mark_issued: "Repasser en émise",
      empty: "Aucune facture pour l'instant.", lines_t: "Courses du mois", export_csv: "Exporter les lignes en CSV",
      err: "Action impossible pour le moment.",
    },
    tm: {
      t: "Équipe", s: "Les membres de votre organisation et leurs rôles.",
      add: "Ajouter un membre", role: "Rôle", del: "Retirer", you: "vous",
      save: "Ajouter", added_t: "Membre ajouté", err: "Impossible d'ajouter ce membre.",
    },
    st: {
      t: "Paramètres", org_t: "Organisation", org_name: "Nom de l'organisation", billing: "E-mail de facturation",
      cc_t: "Centres de coûts", cc_ph: "Ex. Direction", cc_empty: "Aucun centre de coût — ajoutez-en pour suivre les dépenses par équipe.",
      toggles_t: "Préférences",
      toggles: {
        monthly_billing: ["Facturation mensuelle consolidée", "Une seule facture par mois"],
        whatsapp: ["Notifications WhatsApp aux passagers", "En plus des SMS et e-mails"],
        flight_tracking: ["Suivi de vol automatique", "Ajustement en cas de retard"],
        require_cost_center: ["Centre de coût obligatoire", "Référence interne à chaque réservation"],
      },
      save: "Enregistrer", saved_t: "Paramètres enregistrés", err: "Enregistrement impossible.",
    },
    common: { cancel: "Annuler" },
  },
  tr: {
    eyebrow: "Suivi — {code}",
    booked_by: "Réservé pour vous par",
    eta_live: "Arrivée estimée dans ~{min} min",
    pos_stale: "Dernière position il y a {min} min",
    copy: {
      booked: ["Nous préparons votre trajet.", "Votre chauffeur est en cours de confirmation — vous recevez un e-mail dès qu'il est officiel.", "Départ à l'heure prévue"],
      driver_assigned: ["Votre chauffeur est confirmé.", "Prénom, véhicule et plaque sont ci-dessous. Vous recevrez un e-mail dès son départ vers vous.", "Départ à l'heure prévue"],
      driver_on_the_way: ["Votre chauffeur est en route.", "Suivez sa progression en direct sur la carte.", "En route"],
      driver_arrived: ["Votre chauffeur vous attend.", "Il est au point de rencontre convenu.", "Il est là"],
      on_board: ["Vous êtes à bord.", "Itinéraire optimisé en fonction du trafic. Bon trajet.", "Arrivée estimée recalculée en continu"],
      completed: ["Vous êtes arrivé.", "Merci d'avoir voyagé avec MILES. Votre reçu est disponible ci-dessous.", "Terminé"],
      cancelled: ["Cette réservation a été annulée.", "Aucun chauffeur ne sera envoyé. Pour toute question, contactez-nous à info@gomiles.ch.", "Annulée"],
    },
    pickup_label: "Prise en charge", arrival_label: "Arrivée estimée", completed_label: "Course terminée",
    remaining: "19 min restantes",
    your_driver: "Votre chauffeur", driver_alt: "{name}, chauffeur MILES",
    call_driver: "Appeler le chauffeur", message_driver: "Message au chauffeur",
    pending: "Nous confirmons votre chauffeur — prénom, photo, véhicule et plaque apparaîtront ici automatiquement.",
    meeting: "Point de rencontre : {pickup} — devant l'entrée principale{terminal}.",
    terminal_join: ", ",
    timeline_t: "Statuts du trajet", notif_t: "Notifications envoyées",
    flight_t: "Vol suivi",
    flight_note: "En cas de retard, nous ajustons la prise en charge à l'heure réelle d'atterrissage.",
    support_t: "Une question ?", support_b: "Une vraie personne vous répond",
    share: "Partager le suivi", share_t: "Lien de suivi copié", share_d: "Partagez-le à qui doit suivre ce trajet.",
    receipt: "Reçu — {code}", paid_badge: "Payée",
    waiting_line: "Attente & prise en charge", included: "inclus", total: "Total TTC",
    download: "Télécharger le reçu", pdf_t: "Reçu PDF", pdf_d: "Téléchargement disponible prochainement.",
    rebook: "Réserver à nouveau",
    hourly_dest: "Mise à disposition",
    stops_label: "Arrêts",
    nf_eyebrow: "Lien de suivi", nf_t: "Réservation introuvable",
    nf_b: "Vérifiez le lien reçu par e-mail, ou contactez le support.",
    nf_cta: "Contacter le support",
    notifs: {
      booked: ["E-mail", "C'est réservé. Votre trajet est confirmé."],
      driver_assigned: ["E-mail", "Votre chauffeur est confirmé — détails ci-dessous."],
      driver_on_the_way: ["E-mail", "Votre chauffeur est en route vers vous."],
      driver_arrived: ["E-mail", "Votre chauffeur vous attend au point de rencontre."],
      on_board: ["Suivi", "Bon trajet — l'arrivée estimée se met à jour en direct."],
      completed: ["Suivi", "Votre reçu est disponible ci-dessous."],
    },
  },
  te: {
    eyebrow: "Suivi MILES", h1: "Suivre mon trajet.",
    p: "Entrez le code de votre réservation — il figure dans votre confirmation, au format ML-XXXXX. Aucun compte nécessaire.",
    ph: "ML-XXXXX", submit: "Suivre",
  },
  ap: {
    eyebrow: "Transferts aéroport", img_alt: "Vol au-dessus des nuages", h1a: "L'aéroport, géré", h1b: "à votre place.",
    sub: "Accueil au terminal, attente incluse, point de rencontre clair. Et si vous nous confiez votre numéro de vol à la réservation, la prise en charge suit l'atterrissage réel.",
    trust: ["Attente 45 min incluse", "Accueil pancarte au terminal", "Numéro de vol facultatif"],
    note_t: "Votre numéro de vol ?",
    flight_note: "Il est facultatif. Indiquez-le à la réservation : nous suivons l'atterrissage et ajustons l'heure de prise en charge en cas de retard — sans rien vous demander.",
    h2: "Nos aéroports, au cordeau.",
    airports: [
      ["GVA", "Genève Aéroport", "Accueil au terminal 1, zone arrivées. Attente 45 min incluse.", "15 min du centre"],
      ["ZRH", "Zurich Aéroport", "Point de rencontre Arrival 2. Attente 45 min incluse.", "12 min du centre"],
      ["BSL", "EuroAirport Bâle", "Sortie côté suisse, pancarte nominative. Attente incluse.", "15 min du centre"],
    ],
    flow_eyebrow: "Le déroulé", flow_h2: "De la porte d'embarquement à la portière.", flow_img_alt: "Accueil MILES à l'aéroport",
    flow: [
      ["Vous réservez", "Adresse, date, heure — et votre numéro de vol si vous le souhaitez. C'est facultatif, mais pratique."],
      ["Votre chauffeur vous attend", "Prénom, photo, plaque et point de rencontre exact, envoyés par e-mail."],
      ["Vous montez", "Bagages portés, itinéraire prêt, bouteille d'eau fraîche."],
      ["Vous arrivez", "Le reçu vous attend sur votre lien de suivi."],
    ],
    band: "Un vol bientôt ? On s'occupe du reste.", band_cta: "Réserver un transfert",
  },
  biz: {
    eyebrow: "MILES Business", h1a: "Tous vos trajets.", h1b: "Une seule organisation.",
    sub: "Réservez pour vos collaborateurs, vos dirigeants ou vos invités. Suivez chaque trajet en parallèle. Une seule facture, une fois par mois.",
    cta1: "Créer votre espace", cta2: "Se connecter",
    shot_alt: "Le dashboard MILES Business en conditions réelles",
    feat_eyebrow: "L'espace, en vrai",
    f1: {
      tag: "Réserver", t: "Une course pour un tiers, en quelques instants.",
      d: "Passager enregistré ou invité saisi à la volée : vous voyez le prix fixe et le tracé du trajet avant de confirmer. Le passager reçoit un lien de suivi — aucun compte, aucune application.",
      points: ["Prix fixe calculé en direct sur le trajet réel", "Carte itinéraire avant confirmation", "Lien de suivi à partager au passager"],
      alt: "Nouvelle course avec carte itinéraire et choix du véhicule",
    },
    f2: {
      tag: "Piloter", t: "Toutes vos courses, en un coup d'œil.",
      d: "Statuts en direct, recherche instantanée, annulation et rebooking en un clic. Chaque course s'ouvre en détail : passager, historique, lien de suivi.",
      points: ["Statuts en direct, rafraîchis automatiquement", "Recherche par passager, code ou trajet", "Rebooker une course passée en un clic"],
      alt: "Détail d'une course avec historique et actions",
    },
    f3: {
      tag: "Facturer", t: "La facturation qui se fait toute seule.",
      d: "Les courses se consolident automatiquement par mois. PDF prêt pour la comptabilité avec TVA, export CSV des lignes, et suivi des paiements.",
      points: ["Facture mensuelle consolidée, automatique", "PDF avec TVA 8,1 % + export CSV", "Statuts émise / payée"],
      alt: "Factures mensuelles avec lignes de courses détaillées",
    },
    roles_eyebrow: "Les rôles", roles_h2a: "Chacun", roles_h2b: "son écran.",
    roles: [
      ["Admin", "Vue complète : réservation, équipe, paramètres, facturation. C'est vous qui tenez les clés."],
      ["Booker", "Réception, concierge, assistant·e : réserver et suivre les courses. Rien d'autre à gérer."],
      ["Finance", "Vue d'ensemble et factures. La comptabilité récupère ses PDF, point."],
    ],
    flow_eyebrow: "Le workflow", flow_h2: "Réserver pour quelqu'un, en quatre gestes.",
    flow: [
      ["Choisir le passager", "Un collaborateur enregistré, ou un invité saisi en 10 secondes."],
      ["Définir le trajet", "Adresses enregistrées, vol suivi, véhicule adapté."],
      ["Envoyer", "Le passager reçoit tout par e-mail — sans compte."],
      ["Suivre en parallèle", "Vous voyez la course avancer depuis votre dashboard."],
    ],
    flow_cta: "Créer un espace",
    proof: [
      ["2 min", "pour réserver pour un tiers, passager sans compte inclus"],
      ["1 facture", "consolidée par mois, prête pour la comptabilité"],
      ["0 note de frais", "taxi éparpillée dans les boîtes mail"],
    ],
    proof_note: "Tarifs négociés possibles · Réservations récurrentes · Multi-utilisateurs",
    final_eyebrow: "Prêt en 2 minutes",
    final_h2a: "Essayez la démo.", final_h2b: "Puis créez la vôtre.",
    final_p: "L'espace Maison Démo SA est pré-rempli de données d'exemple. Votre espace, lui, démarre vide — et vous appartient.",
    final_cta1: "Parler à notre équipe", final_cta2: "Créer mon espace",
  },
  dash: {
    hello: "Bonjour, Maison Démo SA", demo: "Mode démo — données d'exemple", quit: "Quitter la démo",
    new: "Nouvelle réservation",
    nav: ["Vue d'ensemble", "Réservations", "Trajets", "Passagers", "Factures", "Équipe", "Paramètres"],
    plan: "Plan Business · 4 utilisateurs",
    stats: [["Aujourd'hui", "trajets"], ["En cours", "courses live"], ["À venir", "réservations"], ["Dépenses du mois", "toutes équipes"]],
    recent: "Réservations récentes", view_all: "Tout voir",
    activity: "Activité récente",
    activity_items: [
      ["Chauffeur confirmé", "Yanis B. — Zurich → ZRH", "il y a 12 min"],
      ["Course terminée", "Sofia M. — Montreux → GVA", "il y a 48 min"],
      ["Facture générée", "INV-2026-047 — juillet", "il y a 2 h"],
      ["Nouveau passager", "Antoine D. ajouté par la réception", "hier"],
    ],
    quick: "Réserver pour un passager",
    all_b: "Toutes les réservations", all_b_sub: "{n} trajets — cliquez pour suivre",
    chart_t: "Trajets par jour — 7 derniers jours", live_t: "Courses en cours & à venir",
    hourly_short: "Mise à disp.",
    invoices_t: "Factures", invoices_s: "Facturation mensuelle consolidée",
    inv_trips: "{n} trajets", inv_paid: "Payée", inv_pending: "En attente",
    pdf_t: "Export PDF", pdf_d: "{n} — téléchargement disponible prochainement.",
    team_t: "Utilisateurs & rôles",
    team: [
      ["Nathalie Perrot", "Admin — direction", "Tous droits"],
      ["Réception Hôtel", "Conciergerie", "Réservation pour clients"],
      ["Marc Olivier", "Office manager", "Réservation + factures"],
      ["Laura Chappuis", "Assistante exécutive", "Réservation uniquement"],
    ],
    settings_t: "Paramètres de l'organisation",
    settings: [
      ["Facturation mensuelle consolidée", "Une seule facture par mois"],
      ["Notifications WhatsApp aux passagers", "En plus des SMS et e-mails"],
      ["Suivi de vol automatique", "Ajustement en cas de retard"],
      ["Centres de coûts obligatoires", "Référence interne à chaque réservation"],
    ],
    book_for: "Réserver",
  },
  dr: {
    eyebrow: "Les chauffeurs MILES", h1a: "Des professionnels que l'on connaît", h1b: "par leur prénom.",
    sub: "Pas de gants blancs ni de cérémonie. Des gens ponctuels, discrets et précis, qui considèrent chaque course comme un travail bien fait.",
    trust: ["100 % vérifiés, formés et assurés", "Évalués après chaque course", "Prénom, photo et plaque communiqués avant le départ"],
    moments_eyebrow: "L'expérience MILES", moments_h2a: "Le service,", moments_h2b: "sans la cérémonie.",
    moments: [
      ["L'accueil", "Portière ouverte, bagages rangés, itinéraire confirmé. Vous n'avez rien à expliquer."],
      ["Le trajet", "Votre tempo, votre ambiance : conversation ou silence, musique ou calme. Le chauffeur s'adapte, pas l'inverse."],
      ["L'arrivée", "Dépose au plus près de l'entrée, aide aux bagages, reçu déjà envoyé."],
    ],
    steps_eyebrow: "La confirmation", steps_h2a: "Votre chauffeur,", steps_h2b: "identifié avant le départ.",
    steps_sub: "Pas d'inconnu au bord du trottoir : vous savez qui vient, dans quelle voiture, et quand il arrive.",
    steps: [
      ["Réservation confirmée", "Prix fixe validé, trajet verrouillé. Votre course entre immédiatement dans le système MILES."],
      ["Confirmation", "En réservation immédiate, votre chauffeur est confirmé en quelques minutes. En réservation planifiée, au plus tard 1 h avant le départ."],
      ["Présentation", "Vous recevez prénom, photo, véhicule, couleur et plaque — par e-mail et sur votre lien de suivi."],
      ["Contact direct", "Un doute sur le point de rencontre ? Appelez ou écrivez à votre chauffeur depuis le suivi, sans échanger vos numéros."],
    ],
    h2a: "Ce que nous exigeons.", h2b: "À chaque course.",
    commitments: [
      ["Sélectionnés un par un", "Entretien, vérification des antécédents, essai de conduite : la plupart des candidatures s'arrêtent en route."],
      ["Formés au service MILES", "Ponctualité, présentation, discrétion, connaissance des aéroports et des hôtels. La technique, on la laisse à l'app."],
      ["Discrets par principe", "Conversations, itinéraires, passagers : ce qui se passe à bord reste à bord. Chaque chauffeur signe un engagement de confidentialité."],
      ["Locaux avant tout", "Terminaux de GVA et ZRH, palaces, stations de ski, routes de montagne : votre chauffeur connaît le terrain, pas seulement le GPS."],
      ["Évalués à chaque course", "Feedback systématique après chaque trajet. Un chauffeur dont la qualité baisse est accompagné — puis remplacé si besoin."],
      ["Payés à leur juste valeur", "Des chauffeurs bien payés arrivent à l'heure, souriants, et restent. C'est aussi simple que ça."],
    ],
    band_eyebrow: "Rejoindre MILES",
    band_t: "Vous êtes chauffeur professionnel ?",
    band_s: "Rejoignez un réseau qui vous respecte — et vous paie correctement.",
    band_cta: "Devenir chauffeur MILES",
    band_alt: "Découvrir la flotte",
  },
  ct: {
    eyebrow: "Villes & couverture", h1a: "La Suisse, d'abord.", h1b: "Puis l'Europe.",
    sub: "MILES opère dans les grandes villes suisses et leurs aéroports, sur les corridors longue distance et vers les stations alpines — avec le même prix fixe, affiché avant tout paiement.",
    trust: ["Prix fixe garanti", "Chauffeurs locaux vérifiés", "Estimation instantanée, sans compte"],
    cities_eyebrow: "Les villes", cities_h2a: "Cinq bases,", cities_h2b: "toute la Suisse.",
    airport_from: "Transfert {code} dès {price}",
    book: "Réserver depuis {city}",
    cities: [
      ["Genève", "Transport privé Genève", ["Centre → Aéroport GVA — 15 min", "Genève → Lausanne — 45 min", "Genève → Verbier — 2 h"], "GVA", "CHF 90.–"],
      ["Lausanne", "Chauffeur privé Lausanne", ["Centre → Aéroport GVA — 60 min", "Lausanne → Zurich — 2 h 40", "Ouchy → Montreux — 30 min"], "GVA", "CHF 205.–"],
      ["Zurich", "Chauffeur aéroport Zurich", ["Centre → Aéroport ZRH — 15 min", "Zurich → Bâle — 1 h 10", "Zurich → Lucerne — 45 min"], "ZRH", "CHF 90.–"],
      ["Bâle", "Transport privé Bâle", ["Centre → EuroAirport BSL — 15 min", "Bâle → Zurich — 1 h 10", "Bâle → Berne — 1 h 15"], "BSL", "CHF 90.–"],
      ["Berne", "Chauffeur privé Berne", ["Berne → Aéroport ZRH — 2 h", "Berne → Aéroport GVA — 2 h 05", "Berne → Interlaken — 50 min"], "ZRH", "CHF 430.–"],
    ],
    ld_eyebrow: "Corridors longue distance", ld_h2a: "Ville à ville,", ld_h2b: "au prix fixe.",
    ld_note: "Prix Business Class estimés sur la distance routière réelle. Le prix exact s'affiche dans la réservation, avant tout paiement.",
    corridors: [
      ["Genève", "Zurich", "279 km", "≈ 3 h 20", "dès CHF 905.–"],
      ["Genève", "Milan", "320 km", "≈ 3 h 55", "dès CHF 1 040.–"],
      ["Lausanne", "Lyon", "213 km", "≈ 2 h 30", "dès CHF 695.–"],
      ["Genève", "Lyon", "150 km", "≈ 1 h 55", "dès CHF 490.–"],
      ["Zurich", "Saint-Moritz", "203 km", "≈ 2 h 40", "dès CHF 665.–"],
    ],
    corridor_cta: "Estimer",
    ski_eyebrow: "Les Alpes", ski_h2a: "Direction", ski_h2b: "les stations.",
    ski_p: "Skis, bagages volumineux, routes de montagne : le VAN est fait pour ça. Départs depuis Genève, tarifs Business Class.",
    ski_img_alt: "Le VAN MILES, taillé pour les stations", ski_caption: "Skis & bagages — le VAN",
    ski: [
      ["Verbier", "161 km — dès CHF 525.–"],
      ["Zermatt (via Täsch)", "230 km — dès CHF 750.–"],
      ["Gstaad", "149 km — dès CHF 485.–"],
      ["Crans-Montana", "185 km — dès CHF 605.–"],
    ],
    ski_cta: "Réserver",
    city_pages: {
      back: "Toutes les villes", airport_t: "Transfert aéroport", routes_t: "Trajets populaires",
      routes_s: "Prix Business Class estimés sur la distance routière réelle, mesurée via OSRM.",
      places_t: "Quartiers & lieux desservis", estimate: "Estimer", from_word: "dès", others_t: "Aussi desservies",
      geneve: {
        tag: "Genève · Aéroport GVA",
        h1: "Transport privé à Genève",
        sub: "Chauffeur vérifié MILES à Genève et au départ de GVA : prise en charge rapide au centre, tarif fixe connu avant de monter, accueil pancarte au terminal.",
        airport_note: "Accueil pancarte au Terminal 1, zone arrivées. Attente 45 min incluse.",
        route_labels: [["Centre → Aéroport GVA", "≈ 15 min"], ["Genève → Lausanne", "≈ 50 min"], ["Genève → Montreux", "≈ 1 h 10"], ["Genève → Verbier", "≈ 2 h 05"]],
        places: ["Vieille Ville", "Rive Gauche", "Cologny", "Carouge", "PaleXpo", "ONU — quartier international", "Hôtels du Léman"],
      },
      lausanne: {
        tag: "Lausanne · via GVA",
        h1: "Chauffeur privé à Lausanne",
        sub: "Transferts Lausanne ↔ Genève Aéroport toute la journée, trajets ville à ville et mises à disposition. Prix fixe, suivi en direct, reçu immédiat.",
        airport_note: "Dépose et prise en charge GVA avec attente incluse — 63 km de Riviera lémanique, sans le moindre quai.",
        route_labels: [["Centre → Aéroport GVA", "≈ 60 min"], ["Lausanne → Zurich", "≈ 2 h 40"], ["Lausanne → Montreux", "≈ 30 min"]],
        places: ["Ouchy", "Gare CFF", "Le Flon", "EPFL & UNIL", "Lavaux", "Beau-Rivage Palace"],
      },
      zurich: {
        tag: "Zurich · Aéroport ZRH",
        h1: "Chauffeur privé à Zurich",
        sub: "De la Bahnhofstrasse à ZRH en un quart d'heure : accueil au terminal Arrival 2, attente incluse, tarif fixe affiché avant paiement.",
        airport_note: "Point de rencontre Arrival 2. Attente 45 min incluse.",
        route_labels: [["Centre → Aéroport ZRH", "≈ 15 min"], ["Zurich → Bâle", "≈ 1 h 10"], ["Zurich → Lucerne", "≈ 45 min"], ["Zurich → Saint-Moritz", "≈ 2 h 40"]],
        places: ["Bahnhofstrasse", "Zurich West", "Enge & Seefeld", "Kloten", "Zoug", "The Circle"],
      },
      bale: {
        tag: "Bâle · EuroAirport BSL",
        h1: "Transport privé à Bâle",
        sub: "EuroAirport côté suisse, sortie rapide, chauffeur qui connaît les trois frontières. Bâle, Zurich et Berne à prix fixe.",
        airport_note: "Sortie côté suisse, pancarte nominative. Attente incluse.",
        route_labels: [["Centre → EuroAirport BSL", "≈ 15 min"], ["Bâle → Zurich", "≈ 1 h 10"], ["Bâle → Berne", "≈ 1 h 15"]],
        places: ["Altstadt", "Messe Basel", "Saint-Louis (FR)", "Mulhouse (FR)", "Campus Novartis & Roche"],
      },
      berne: {
        tag: "Berne · via ZRH & GVA",
        h1: "Chauffeur privé à Berne",
        sub: "La capitale sans grand aéroport ? MILES connecte Berne à ZRH et GVA à heure fixe — et file vers l'Oberland sans changer de train.",
        airport_note: "Départs synchronisés sur vos vols ZRH ou GVA, marge d'attente incluse.",
        route_labels: [["Berne → Aéroport ZRH", "≈ 2 h"], ["Berne → Aéroport GVA", "≈ 2 h 05"], ["Berne → Interlaken", "≈ 50 min"]],
        places: ["Altstadt (UNESCO)", "Bundeshaus", "Kirchenfeld", "Wabern", "Belp", "Gurten"],
      },
    },
    final_eyebrow: "Votre trajet",
    final_h2a: "Le prix exact,", final_h2b: "en 30 secondes.",
    final_p: "Entrez vos deux adresses : l'estimation réelle — distance, durée, prix par catégorie — s'affiche immédiatement, sans compte.",
    final_cta: "Estimer mon trajet",
  },
  faq: {
    eyebrow: "FAQ", h1a: "Les réponses,", h1b: "sans détour.",
    sub: "Tout ce qu'on nous demande vraiment. Il manque une réponse ? Une vraie personne vous répond.",
    band: "Une question spécifique ?", band_contact: "Contacter le support", band_book: "Réserver",
    items: [
      ["Puis-je réserver sans compte ?", "Oui. Nom, téléphone, e-mail : c'est suffisant. Vous recevez immédiatement votre confirmation et votre lien de suivi personnel. Le compte MILES existe, mais il reste facultatif."],
      ["Puis-je réserver un aller-retour ?", "Oui. Choisissez « Aller-retour » à la première étape : vous indiquez la date et l'heure du retour, et nous planifions les deux courses. Le retour bénéficie d'un tarif ajusté."],
      ["Puis-je réserver une mise à disposition ?", "Oui, c'est le mode « À l'heure ». Vous choisissez le lieu de départ, la date, l'heure et la durée (2 h minimum). Le chauffeur et le véhicule restent à votre disposition."],
      ["Puis-je réserver pour quelqu'un d'autre ?", "Oui. Indiquez simplement le nom et le téléphone du passager à l'étape Informations. Il reçoit ses informations et son lien de suivi — sans avoir besoin de créer un compte."],
      ["Comment fonctionne le suivi ?", "Chaque réservation génère un lien de suivi personnel et sécurisé. Position du chauffeur, statut du trajet et heure d'arrivée estimée s'y mettent à jour en temps réel. Vous pouvez le partager à qui vous voulez."],
      ["Quand recevrai-je les informations de mon chauffeur ?", "Dès que votre chauffeur est confirmé — prénom, photo, véhicule, couleur et plaque. Pour une réservation immédiate, comptez quelques minutes. Pour une réservation planifiée, au plus tard 1 h avant le départ."],
      ["Que se passe-t-il si mon vol est retardé ?", "Indiquez votre numéro de vol à la réservation : nous suivons le vol et ajustons la prise en charge à l'atterrissage réel. L'attente à l'aéroport est incluse."],
      ["Puis-je modifier ou annuler une réservation ?", "Oui, gratuitement jusqu'à 1 h avant le départ pour un trajet simple, et jusqu'à 24 h pour une mise à disposition ou un longue distance. Depuis votre lien de suivi ou en contactant le support."],
      ["Comment fonctionne le paiement ?", "Le prix est fixé et affiché avant confirmation. Vous payez par carte bancaire de manière sécurisée au moment de la réservation. Le reçu est disponible immédiatement après la course."],
      ["Comment fonctionne MILES Business ?", "Un espace dédié pour votre organisation : réservez pour un collaborateur, un client ou un invité en quelques secondes, suivez tous les trajets en parallèle, et recevez une facture consolidée en fin de mois."],
      ["Comment fonctionne la facturation entreprise ?", "Toutes les courses du mois sont regroupées sur une seule facture, avec centres de coûts et références internes si vous le souhaitez. Export PDF et comptable en un clic."],
    ],
  },
  co: {
    eyebrow: "Support & Contact", h1a: "Des humains,", h1b: "pas un chatbot.",
    sub: "Une course en cours, une facture, un compte Business, un événement à coordonner — notre équipe répond vite, et bien.",
    rows: [["E-mail", "info@gomiles.ch"], ["Téléphone", "+41 79 195 76 06"]],
    topic_l: "Sujet",
    topics: ["Question sur une réservation", "MILES Business", "Devenir chauffeur", "Événement & groupes", "Autre demande"],
    name: "Nom complet", email: "E-mail", company: "Société — optionnel",
    message: "Décrivez votre besoin en quelques mots…",
    submit: "Envoyer", sending: "Envoi en cours…",
    note: "Réponse humaine — jamais de chatbot",
    ok_t: "C'est bien reçu.", ok_b: "Merci {name} — un conseiller MILES vous répond sous 2 h en journée.",
    toast_t: "Message envoyé", toast_d: "Une vraie personne vous répond sous 2 h en journée.",
    err_t: "Envoi impossible", err_d: "Réessayez ou écrivez à info@gomiles.ch",
  },
};

const en = {
  nav: {
    airport: "Airport transfers", services: "Services", fleet: "Fleet", business: "Business", drivers: "Chauffeurs",
    cities: "Cities", faq: "FAQ", track: "Tracking", book: "Book", book_ride: "Book a ride", guides: "Guides",
  },
  bar: {
    one_way: "One-way", round_trip: "Round trip", hourly: "By the hour",
    available: "Chauffeurs available", from: "Pickup", to: "Destination", date: "Date", time: "Time",
    from_ph: "Pickup address", to_ph: "Drop-off address",
    hourly_note: "Your chauffeur stays at your disposal, from 2 to 24 hours.",
    submit: "See vehicles",
    ticker: "Live — Alexandre arrives in 8 min · EQE · GE 512 408",
    missing: "Missing address", missing_desc: "Enter a pickup and a destination address.",
  },
  addr: {
    loading: "Searching addresses…",
    empty: "No suggestions — you can continue with your address.",
    unavailable: "Suggestions unavailable — your free-form address is still accepted.",
    attribution: "© OpenStreetMap contributors / Photon",
  },
  hero: {
    img_alt: "MILES sedan on the Geneva waterfront",
    t1: "Your private chauffeur,", t2: "with no room for surprises.",
    sub: "Book your ride, follow your chauffeur, find every detail in one place. The price is confirmed before departure — we take care of the rest.",
    sub_short: "Where every mile feels different.",
  },
  manifesto: {
    eyebrow: "The MILES manifesto",
    t1: "Your ride.", t2: "Our logistics.",
    p: "One booking sets everything in motion: a confirmed chauffeur, flight tracking, real routing, a tracking link to share.",
    cta: "See how it works",
    chapters: [
      ["You book.", "Two addresses, a time, a vehicle. The fixed price shows before you pay — confirmation is instant."],
      ["You track.", "Chauffeur, vehicle, plate, arrival time: everything stays visible on your link, minute by minute."],
      ["You arrive.", "Flight delay absorbed, clear meeting point, receipt ready. Nothing to manage."],
    ],
    photo_alt: "MILES sedan parked in front of a Swiss hotel",
    photo_label: "Geneva — Centre → Geneva Airport GVA",
    photo_status: "Departure confirmed",
    photo_price: "CHF 74.– · fixed price",
    card_note: "Chauffeur confirmed before departure",
  },
  homeFleet: {
    eyebrow: "The MILES fleet",
    h2a: "Four vehicles.", h2b: "One simple choice.",
    p: "Each category answers a use case: everyday ride, business travel, big arrival, group. The exact price shows before any payment.",
    cta: "Explore the full fleet",
    items: {
      standard: { name: "Standard Class", model: "Tesla Model Y", desc: "The same MILES care at the most accessible price — for everyday rides." },
      van: { name: "VAN", model: "Mercedes V-Class", desc: "For travelling together — skis and luggage included — without complicating the logistics." },
      first: { name: "First Class", model: "Mercedes S-Class", desc: "More space and quiet, when the ride matters more." },
      business: { name: "Business Class", model: "Mercedes EQE or E-Class", desc: "For everyday rides, meetings and airport transfers." },
    },
  },
  statuses: {
    booked: "Confirmed", driver_assigned: "Chauffeur confirmed", driver_on_the_way: "On the way",
    driver_arrived: "Arrived", on_board: "On board", completed: "Completed", cancelled: "Cancelled",
  },
  homeTrack: {
    eyebrow: "MILES tracking", h2: "You always know what's happening.", cta: "Open a live tracking",
    chip: "Driver on the way", arrive: "Alexandre arrives in 8 min.",
    car: "Mercedes EQE · Obsidian black", call: "Call", message: "Message",
    eta: "Estimated arrival", caption: "The status system",
    notif_t: "MILES · now", notif_b: "Your chauffeur will arrive in about 8 min.",
  },
  expect: {
    eyebrow: "Concrete", h2a: "You know what", h2b: "to expect.",
    p: "Every MILES ride follows the same thread — from booking to receipt. Here it is, as is.",
    items: [
      ["Price confirmed before booking", "The fare shown is the fare paid."],
      ["Instant confirmation", "Summary, ride number, tracking link."],
      ["Chauffeur confirmed", "First name, photo, vehicle, plate."],
      ["Ride tracked live", "Position, statuses, estimated arrival."],
      ["Flight monitored", "Delayed? The pickup adjusts."],
      ["A real person answers", "The MILES team, in Geneva — not a chatbot."],
    ],
  },
  interlude: {
    eyebrow: "City to city", h2a: "Geneva → Milan.", h2b: "Four hours to yourself.",
    cta: "Book a long-distance ride",
  },
  homeAirport: {
    eyebrow: "Airport transfers", h2a: "Your flight moves.", h2b: "So does your pickup.",
    p: "Add your flight number at booking: if you're delayed, the pickup is adjusted and your chauffeur informed. Without asking you for anything.",
    cta: "See the airport experience",
    route: "Zurich → Geneva", delay: "Delayed +22 min",
    l1: "Scheduled landing", l2: "Actual landing", l3: "Pickup",
    adjusted: "Your pickup has been adjusted.",
    meeting: "Alexandre will be waiting — Arrivals 2", meeting_l: "Meeting point",
  },
  homeBiz: {
    eyebrow: "MILES Business", h2: "Every company ride. One single organisation.",
    p: "Book for a colleague, an executive or a guest — no account needed for them. Track every ride in parallel. One consolidated invoice at the end of the month.",
    demo: "Request a demo", explore: "Explore the product",
    today: "Today — Maison Démo SA", new: "New booking",
    side: ["Overview", "Bookings", "Passengers", "Invoices", "Team"],
    for: ["For me", "Colleague", "Guest"],
    noacct: "The passenger doesn't need an account",
    ride_status: ["On the way", "Confirmed", "Chauffeur confirmed"],
    inv_t: "Invoice INV-2026-047 ready", inv_b: "96 consolidated rides · accounting export",
  },
  citiesCta: {
    eyebrow: "Coverage", h2a: "Geneva. Lausanne. Zurich.", h2b: "Basel. Bern. And tomorrow, beyond.",
    book: "Book a ride", cities: "See coverage",
  },
  footer: {
    tag: "MILES organises your chauffeured rides across Switzerland — from booking to arrival.",
    book: "Book", business: "MILES Business",
    cols: ["Product", "Business", "Company"],
    links: [
      ["Our services", "Book", "Airport transfers", "Fleet & fares", "Cities & coverage"],
      ["MILES Business", "Dashboard — demo", "Hotels & concierge"],
      ["Chauffeurs", "FAQ", "Contact", "MILES Guides"],
    ],
    legal: ["Terms", "Privacy", "Cookies"],
    copy: "© 2026 MILES — All rights reserved",
  },
  bk: {
    steps: ["Ride", "Vehicle", "Traveler", "Payment"],
    questions: ["Where and when are you leaving?", "Choose your vehicle", "Who is this ride for?", "Finalize your booking"],
    help: "Need help?", route_step_sub: "Address, schedule and travelers — your ride takes shape immediately.",
    pax: "Travelers", pax_one: "passenger", pax_many: "passengers", bags: "Luggage", bag_one: "bag", bag_many: "bags",
    pax_summary: "{p} passenger(s) · {b} bag(s)", swap_route: "Swap route",
    flight_context: "Are you traveling by plane?", flight_context_sub: "We will monitor your flight and adjust pickup if needed.",
    trip_card_title: "Your ride", trip_card_sub: "It takes shape as you book.", trip_summary_pending: "Add pickup and destination",
    edit: "Edit", cta_vehicles: "See vehicles", cta_traveler: "Choose this vehicle", cta_payment: "Go to payment",
    vehicle_step_sub: "Fixed price. Chauffeur included. No hidden fees.",
    multi_hint: "Need several vehicles? Select them all and adjust quantities.",
    vehicles_word: "vehicles",
    units_title: "One tracking link per vehicle",
    payment_sub: "The amount shown is the confirmed price of your ride.", pay_t: "Secure payment", final_pay: "Book for {p}",
    promo_label: "Promo code", promo_ph: "Enter your code", promo_apply: "Apply", promo_remove: "Remove",
    promo_invalid: "Invalid or expired code.", promo_total: "Total after discount",
    paid_t: "Payment confirmed", paid_b: "Your MILES booking has been created.",
    add_request: "Add a special request", fname: "First name", lname: "Last name", view_booking: "View my booking",
    done_sub: "We'll email you as soon as your chauffeur is confirmed.",
    trust_items: ["Fixed price confirmed before payment", "Chauffeur vetted and identified before departure", "A real person answers"],
    options_title: "Useful options", option_child_seat: "Child seat", option_meet_greet: "Name-board pickup",
    options_note: "These requests are sent to the MILES team and confirmed before departure.",
    terms_text: "I accept the MILES booking conditions.", terms_view: "View conditions", terms_hide: "Hide conditions",
    terms_items: ["The displayed price is confirmed before payment.", "Your details are used to coordinate the ride and send tracking.", "Special requests are confirmed by MILES before departure.", "A provided flight is monitored to adjust pickup."],
    cta_notes: ["Real route and pricing calculated instantly.", "Fixed price, chauffeur included, no hidden fees.", "Your details are only used to coordinate the ride.", "You immediately receive confirmation and tracking."],
    back: "Back", next: "Continue", to_payment: "Continue to payment",
    secure: "Fixed price confirmed before payment",
    from: "Pickup", to: "Destination",
    from_ph: "E.g. Rue du Rhône 24, Geneva", to_ph: "E.g. Geneva Airport GVA",
    diff: "Choose two different addresses.",
    duration: "Duration", schedule_t: "Departure & return",
    flight_t: "Flight — we track your plane",
    flight_ph: "Flight no. — e.g. LX 318", terminal_ph: "Terminal — optional",
    flight_note: "If you're delayed, we adjust the pickup time. Airport waiting time is included.",
    date: "Date", time: "Time", return_t: "Return trip",
    passengers: "Passengers", luggage: "Luggage",
    notes: "Notes & special requests",
    notes_ph: "Child seat, intermediate stop, special instructions…",
    est_loading: "Calculating estimate…",
    est_line: "{km} km · approx. {min} min · guaranteed fixed price",
    est_hourly: "{h} h at your disposal · pickup {from}",
    est_unavailable: "Estimate unavailable — go back to the previous step.",
    info_note: "No account needed. Your details are used only for this ride. Booking for someone else? Enter their details here.",
    firstname: "First name", lastname: "Last name", email: "Email",
    phone_ph: "Phone — e.g. +41 79 123 45 67",
    card: "Bank card",
    secure_note: "Encrypted payment. The amount is charged only upon confirmation — never any surcharge after the ride.",
    pay_loading: "Loading secure payment…",
    pay_methods_note: "Secure payment processed by SumUp — MILES never sees your card details.",
    pay_online: "Online", pay_online_h: "Card — instant secure payment (SumUp)",
    pay_card_board: "Card on board", pay_card_board_h: "Payment terminal with your chauffeur",
    pay_cash: "Cash", pay_cash_h: "Pay your chauffeur directly",
    pay_card_note: "Pay by card with your chauffeur (SumUp terminal). Your booking is confirmed right away.",
    pay_cash_note: "Pay in cash with your chauffeur. Your booking is confirmed right away.",
    final_board: "Confirm booking — {p} on board",
    booked_t: "Booking confirmed", booked_b: "Payment on board, with your chauffeur.",
    pay: "Pay {p}", paying: "Processing payment…",
    pay_err_t: "Payment failed", pay_err_d: "Check your details and try again.", pay_declined: "The payment was declined. Check your details or use another card.",
    pay_pending_t: "Verifying your payment", pay_pending_b: "Your bank is finalising the authorisation. You will receive the confirmation email for ride {code} as soon as it is approved.",
    duration_km: "{h} h disposal · {km} km included", duration_km_short: "{km} km included", from_hourly: "Pickup location",
    hourly_km_note: "{km} km included · beyond: {rate}/km",
    est_err: "Estimate unavailable",
    confirm_title: "It's booked.", code_prefix: "Booking",
    l_ride: "Ride", l_dt: "Date & time", l_vehicle: "Vehicle", l_amount: "Amount",
    l_flight: "Flight tracked", l_pax: "Passengers",
    paid: "paid", return_word: "return",
    hourly_line: "{h} h at your disposal — {from}",
    email_note: "A confirmation email goes out instantly. Once your chauffeur is confirmed, you receive their first name, vehicle and plate by email.",
    track_cta: "Track my ride", again: "Book another ride",
    demand_sent: "{t} request sent", demand_desc: "Confirmation by email in under 2 minutes.",
    rail_title: "Your ride", rail_from: "Pickup address", rail_to: "Drop-off address",
    rail_hourly: "{h} h at your disposal",
    rail_type: "Type", rail_date: "Date", rail_time: "Time", rail_pax: "Travelers", rail_flight: "Flight tracked",
    rail_route: "Route", rail_datetime: "Departure", rail_real: "Real route", rail_vehicle: "Vehicle",
    rail_price_note: "The fixed price appears when you choose a vehicle — before any payment.",
    rail_fixed: "Fixed price", price_guaranteed: "Guaranteed",
    fixed_price: "Fixed price", route_map_alt: "Route preview", route_map_pending: "Route pending", route_map_ready: "Ready to go",
    vehicle_route: "{min} min by road", vehicle_select: "Select", vehicle_selected: "Selected",
    for_me: "For me", for_guest: "For a guest",
    guest_note: "The passenger receives the tracking link and chauffeur details. You keep control of the booking and payment.",
    retry_estimate: "Recalculate price", est_unavailable_hint: "Check the address or try again. You can also return to the Ride step.",
    hourly_word: "Hourly disposal",
    types: { one_way: "One-way", round_trip: "Round trip", hourly: "By the hour" },
  },
  veh: {
    pax: "{n} passengers", bags: "{n} bags",
    standard: { name: "Standard Class", model: "Tesla Model Y or similar", editorial: "The same chauffeur and the same tracking as every MILES class — at the most accessible price.", features: ["Accessible price", "All-electric", "Quiet & recent"] },
    business: { name: "Business Class", model: "Mercedes EQE or E-Class", editorial: "For everyday rides, meetings and airport transfers.", features: ["Wi-Fi on board", "Water & chargers", "Airport name-board pickup"] },
    first: { name: "First Class", model: "Mercedes S-Class or similar", editorial: "More space and quiet, when the ride matters more.", features: ["60 min waiting included", "Comfort rear seats", "Daily press"] },
    van: { name: "VAN", model: "Mercedes V-Class or similar", editorial: "For travelling together without complicating the logistics.", features: ["6 passengers", "Large trunk", "Ideal for groups & skis"] },
  },
  fleet: {
    eyebrow: "The MILES fleet", h1a: "The right vehicle,", h1b: "for every journey.",
    sub: "Four clear categories, recent and spotless vehicles, and the same level of finish on every ride. The exact price appears in the booking flow, before any payment.",
    trust: ["Guaranteed fixed price", "Free cancellation", "MILES-verified chauffeurs"],
    glance_eyebrow: "Overview", glance_h2a: "Four classes.", glance_h2b: "A simple choice.",
    cat: "Category", from: "from CHF {n}.–", book: "Book this vehicle",
    models_t: "Illustrated models", onboard_t: "On board", ideal_t: "Ideal for",
    price_note: "Exact price shown before any payment.",
    classes: {
      standard: {
        tagline: "The MILES essentials, at the right price.",
        editorial: "Quiet and recent, with the same chauffeur and the same tracking as every MILES class: everyday travel, well organised.",
        models: ["Tesla Model Y", "Tesla Model 3 or similar"],
        onboard: ["Silent all-electric cabin", "USB-C chargers", "Dual-zone climate control"],
        ideal: ["Everyday rides", "Airport transfers", "City to city"],
        wait: "45 min", hourly: "from CHF 60.–/h",
        interior_alt: "MILES Standard Class sedan on the road", interior_caption: "On the road — Lausanne → Geneva",
      },
      business: {
        tagline: "The standard for business travel.",
        editorial: "Punctual and quiet, with Wi-Fi and chargers: work continues between meetings — or all the way to the airport.",
        models: ["Mercedes EQE", "Mercedes E-Class", "BMW 5 Series or similar"],
        onboard: ["Wi-Fi on board", "Mineral water & USB-C chargers", "Name-board airport pickup", "Silent cabin, dual-zone climate"],
        ideal: ["Business travel", "Airport transfers", "City to city"],
        wait: "45 min", hourly: "from CHF 85.–/h",
        interior_alt: "MILES Business sedan on the road at night", interior_caption: "On the road — Geneva → Zurich",
      },
      first: {
        badge: "MILES Signature",
        tagline: "When the ride matters more.",
        editorial: "A generous rear cabin, reclining seats, a senior chauffeur: for arrivals that matter and long distances.",
        models: ["Mercedes S-Class", "BMW 7 Series or similar"],
        onboard: ["60 min airport wait included", "Reclining rear comfort seats", "Chilled drinks & daily press", "Experienced MILES chauffeur"],
        ideal: ["Arrivals that matter", "Long distance", "Special occasions"],
        wait: "60 min", hourly: "from CHF 100.–/h",
        interior_alt: "First Class rear cabin", interior_caption: "First Class cabin",
      },
      van: {
        tagline: "Space, without compromise.",
        editorial: "Six real seats and a boot that takes skis and luggage: teams, families and events travel together, simply.",
        models: ["Mercedes V-Class", "Mercedes EQV or similar"],
        onboard: ["6 passengers in real configuration", "Skis, bikes & bulky gear", "Two rows of comfort seats", "Ideal for group transfers"],
        ideal: ["Groups & families", "Ski trips", "Events & roadshows"],
        wait: "45 min", hourly: "from CHF 95.–/h",
        interior_alt: "Spacious MILES VAN interior", interior_caption: "Two rows, six seats",
      },
    },
    specs: { pax: "Passengers", bags: "Luggage", wait: "Airport wait included", hourly: "Hourly hire" },
    included_eyebrow: "The MILES standard", included_h2a: "Included in", included_h2b: "every ride.",
    included: [
      ["Guaranteed fixed price", "The price shown at booking is the price you pay. Traffic or surprises: nothing changes."],
      ["Flight tracking & adjusted wait", "Flight number provided? Pickup follows the actual landing time, not the scheduled one."],
      ["Free cancellation", "Up to 1 h before a one-way ride, 24 h for hourly hire or long distance."],
      ["MILES-verified chauffeurs", "Rigorous selection, continuous training, absolute discretion — the same standard on every ride."],
      ["Recent, spotless vehicles", "A recent fleet, maintained and inspected before every shift."],
      ["A real person answers", "The MILES team is reachable before, during and after your ride — not a chatbot."],
    ],
    compare_eyebrow: "Comparison", compare_h2a: "The three classes,", compare_h2b: "side by side.",
    compare_rows: { models: "Illustrated models", pax: "Passengers", bags: "Luggage", wait: "Airport wait included", hourly: "Hourly hire", from: "Rides from", ideal: "Ideal for" },
    note: "Models shown for illustration. You book a category: a vehicle of that category, or higher, is guaranteed.",
    final_eyebrow: "Ready to go", final_h2a: "The exact price,", final_h2b: "before any payment.",
    final_p: "Enter your route: the real estimate appears instantly — no account, no commitment.",
    final_cta: "Estimate my ride",
  },
  sv: {
    eyebrow: "MILES Services", h1a: "One chauffeur,", h1b: "for every situation.",
    sub: "Seven services, one standard: fixed price shown before payment, verified chauffeur, live tracking. Pick the one that fits your journey.",
    card_cta: "Discover", back: "All services",
    inc_eyebrow: "Included", inc_h2a: "All included.", inc_h2b: "By principle.",
    steps_eyebrow: "The process", steps_h2a: "How it", steps_h2b: "works.",
    price_t: "Pricing", fleet_link: "Explore the fleet",
    final_h2: "Hesitating? The exact price shows in 30 seconds.",
    final_p: "Enter your route in the booking flow: the real estimate appears instantly, no account, no commitment.",
    final_cta: "Estimate my ride",
    items: {
      airport: { name: "Airport transfers", desc: "GVA, ZRH, BSL: terminal meet & greet, waiting time included, flight tracked if you share it." },
      p2p: { name: "Point-to-point ride", desc: "From A to B at the displayed price. The simplicity of a ride, the rigour of a private chauffeur." },
      hourly: { name: "Hourly hire", desc: "A chauffeur and a vehicle all to yourself, 2 to 24 hours. Back-to-back meetings, without a wait." },
      long: { name: "Long distance", desc: "Geneva–Zurich, Lausanne–Milan: the comfort of the train, without platforms or timetables." },
      corporate: { name: "MILES Business", desc: "Company accounts, book for others, consolidated monthly billing." },
      hotels: { name: "Hotels & Concierge", desc: "Your front desk books for guests in under a minute, with shared live tracking." },
      events: { name: "Events & Groups", desc: "Coordinated fleets, guest shuttles, a dedicated coordinator on the day." },
    },
  },
  homeSvc: {
    eyebrow: "Our services", h2a: "Seven services.", h2b: "One standard.",
    cta: "Explore all services",
  },
  svc: {
    p2p: {
      name: "Point-to-point ride",
      h1a: "From A to B,", h1b: "without a single detour.",
      sub: "Enter two addresses, see the fixed price instantly, your chauffeur arrives on time. Nothing more — and that is exactly the point.",
      usps: ["Fixed price computed live", "MILES-verified chauffeur", "Shareable live tracking"],
      included: [
        ["Price locked at booking", "The amount shown is the amount charged. Traffic or detours: it never moves."],
        ["Pickup at the exact address", "In front of your door, office or hotel — not around the corner."],
        ["Live tracking", "Chauffeur position, ride status and arrival estimate on your personal link."],
        ["A clear receipt, right away", "The moment the ride ends, your receipt is on your tracking link."],
      ],
      steps: [
        ["You book", "Two addresses, a date, a time. Thirty seconds, no account."],
        ["Your chauffeur is confirmed", "First name, photo, vehicle and plate: you know who is coming before they arrive."],
        ["You follow", "Getting close? You see it. Share the tracking with whoever awaits you."],
        ["You arrive", "Dropped as close as possible, receipt on your tracking link, nothing to sign."],
      ],
      price_v: "from CHF 90.–", price_note: "Exact price computed on your route, shown before any payment.",
      final_cta: "Estimate my ride",
    },
    hourly: {
      name: "Hourly hire",
      h1a: "A chauffeur of your own,", h1b: "by the hour.",
      sub: "Back-to-back meetings, shopping, roadshow, a day of visits: your chauffeur and vehicle stay available between stops, from 2 to 24 hours.",
      usps: ["From 2 to 24 hours", "Unlimited stops", "The same chauffeur all day"],
      included: [
        ["Zero waiting between meetings", "Your chauffeur waits on site, car ready, climate set."],
        ["Free itinerary", "Plans change? Adjust during the day, no new booking needed."],
        ["Guaranteed discretion", "Your meetings and conversations stay confidential — signed commitment."],
        ["Clear hourly billing", "A fixed hourly rate per category, known before you book."],
      ],
      steps: [
        ["Set the duration", "Pickup location, date, start time and number of hours."],
        ["Your chauffeur shows up", "On time, briefed on your first destination."],
        ["Chain your stops", "Each leg is arranged out loud or from the tracking link."],
        ["Finish when you want", "The ride closes at the last stop, detailed receipt on your tracking link."],
      ],
      price_v: "from CHF 85.–/h", price_note: "Hourly rate per category: Business 85, VAN 95, First 100 CHF/h.",
      final_cta: "Book hourly hire",
    },
    long: {
      name: "Long distance",
      h1a: "The city next door,", h1b: "without the hassle.",
      sub: "Geneva–Zurich, Lausanne–Milan, Montreux–Lyon: door to door, on your schedule, with your luggage and your calm. The train without the platforms, the plane without the airport.",
      usps: ["Door to door", "Departure on your time", "Fixed price whatever the traffic"],
      included: [
        ["Leave whenever you want", "5 a.m. or 11 p.m.: you set the time, not a timetable."],
        ["Work or rest on board", "Wi-Fi, chargers, silence or conversation: the ride becomes useful time."],
        ["Stops on demand", "Coffee, photo, emergency: your chauffeur stops where you want."],
        ["Optimised round trip", "A same-day return benefits from an adjusted fare."],
      ],
      steps: [
        ["Enter both cities", "Exact addresses, date, time — the fixed price shows immediately."],
        ["Choose your vehicle", "Business for the meeting, VAN for family and luggage."],
        ["Travel", "Your chauffeur handles road, traffic and tolls. You simply arrive."],
        ["Find your receipt", "Clear, detailed, available on arrival — ready for accounting."],
      ],
      price_v: "from CHF 120.–", price_note: "Fixed price computed on the real distance, shown before any payment.",
      final_cta: "Estimate my long distance",
    },
    hotels: {
      name: "Hotels & Concierge",
      h1a: "Your property's", h1b: "chauffeur service.",
      sub: "Concierge, front desk, guest relations: book a chauffeur for your guests in under a minute, follow the ride live and receive one consolidated invoice.",
      usps: ["Booking in under a minute", "Tracking shared with the guest", "Consolidated monthly billing"],
      included: [
        ["Your guests install nothing", "They receive chauffeur, vehicle and tracking link by email — no account, no app."],
        ["In your house's image", "Impeccably presented chauffeurs, spotless vehicles, a welcome up to your standard."],
        ["A dedicated contact", "One MILES contact for your team, who knows your rides."],
        ["Zero admin", "The month's rides grouped on a single invoice, accounting export included."],
      ],
      steps: [
        ["Express onboarding", "We create your space and train your team in 30 minutes."],
        ["Book for a guest", "Name, phone, route: done — even at 4 a.m."],
        ["The guest is informed", "First name, photo, plate and live tracking, in their language."],
        ["Everything is traced", "Full history, receipts and monthly invoice centralised."],
      ],
      price_v: "On request", price_note: "Partner rates based on volume — reply within 24 business hours.",
      final_cta: "Talk to the Business team",
    },
    events: {
      name: "Events & Groups",
      h1a: "Your guests,", h1b: "all on time.",
      sub: "Weddings, seminars, launches, delegation transfers: we coordinate vehicles, chauffeurs and schedules while you run the event.",
      usps: ["Coordinated fleet", "Dedicated coordinator", "Planned shuttles and rotations"],
      included: [
        ["A complete transport plan", "Pickup points, rotations, vehicle staging: everything orchestrated in advance."],
        ["VANs and sedans combined", "Groups in V-Class, executives in First Class — everyone at their service level."],
        ["A coordinator on site", "On the day, a MILES lead manages chauffeurs and absorbs surprises."],
        ["Centralised tracking", "Every ride visible in real time, for you and for your guests."],
      ],
      steps: [
        ["Tell us about the event", "Date, venues, guest count, constraints: we reply within 24 h."],
        ["Receive the plan", "Vehicles, chauffeurs, schedules and budget — one clear document, one price."],
        ["On the day", "Vehicles rotate, the coordinator manages, you breathe."],
        ["A single invoice", "All rides consolidated, ready for your accounting."],
      ],
      price_v: "On request", price_note: "Every event is quoted individually — reply within 24 business hours.",
      final_cta: "Request a quote",
    },
  },
  bz: {
    auth: {
      panel_eyebrow: "MILES Business",
      panel_h1a: "Every ride in your organisation,", panel_h1b: "in one place.",
      panel_points: ["Book for your guests in a few moments", "Consolidated monthly invoice, accounting-ready PDF", "Admin, Booker and Finance roles"],
      panel_footer: "MILES",
      back: "Back",
      login_t: "Business sign in", login_s: "Access your organisation's workspace.",
      signup_t: "Create your Business workspace", signup_s: "Your organisation, your rides, your invoices — in minutes.",
      org: "Organisation name", name: "Your full name", email: "Work e-mail", password: "Password",
      password_hint: "8 characters minimum",
      submit_login: "Sign in", submit_signup: "Create workspace",
      to_signup: "No workspace yet? Create an account", to_login: "Already have an account? Sign in",
      err_generic: "Something went wrong. Please try again.",
    },
    nav: { overview: "Overview", new_ride: "New ride", rides: "Rides", passengers: "Passengers", invoices: "Invoices", team: "Team", settings: "Settings" },
    roles: { admin: "Admin", booker: "Booker", finance: "Finance" },
    shell: { hello: "Hello, {name}", logout: "Sign out", back_site: "Back to site", demo_badge: "Demo", new_ride_cta: "New ride" },
    ov: {
      today: "Today", today_sub: "rides today", live: "Live", live_sub: "live rides",
      upcoming: "Upcoming", upcoming_sub: "bookings", spend: "Monthly spend", spend_sub: "all rides",
      recent: "Recent bookings", view_all: "View all", series_t: "Spend — last 6 months",
      empty: "No rides yet.", rides_word: "rides", next_t: "Next ride",
    },
    nr: {
      t: "New ride", s: "Book for a passenger — they follow the ride via a link, no account needed.",
      passenger: "Passenger", saved: "Saved", new: "New", choose: "Choose a passenger…",
      from: "Pickup", to: "Destination", date: "Date", time: "Time", duration: "Duration",
      type_one: "One way", type_hourly: "Hourly",
      vehicle: "Vehicle", cc: "Cost center", cc_none: "None",
      flight: "Flight no. (optional)", notes: "Instructions (optional)",
      fname: "First name", lname: "Last name", phone: "Phone", email: "E-mail (optional)",
      estimating: "Computing price…", estimate_hint: "Enter the route to see vehicles and the fixed price.",
      submit: "Confirm ride", done_t: "Ride created.", done_s: "Share this tracking link with the passenger:",
      copy: "Copy link", copied: "Tracking link copied", again: "New ride", view_rides: "View rides",
      map_caption: "{km} km · ~{min} min", price_t: "Fixed price",
      err: "Could not create the ride. Check the addresses.",
    },
    rd: {
      t: "Rides", filters: { all: "All", live: "Live", upcoming: "Upcoming", done: "Completed", cancelled: "Cancelled" },
      cancelled_badge: "Cancelled", cancel: "Cancel", cancel_yes: "Confirm", cancel_done_t: "Ride cancelled",
      share: "Copy tracking link", copied_t: "Link copied", track: "Open tracking",
      search_ph: "Search (passenger, code, route)…", route_t: "Route", contact_t: "Passenger",
      flight_t: "Flight", notes_t: "Instructions", cc_t: "Cost center", by_t: "Booked by", created_t: "Created",
      timeline_t: "History", open_track: "Open tracking", rebook: "Rebook",
      empty: "No rides in this filter.", err: "Action unavailable right now.",
    },
    ps: {
      t: "Passengers", s: "Your passenger directory — book for them in one click.",
      add: "Add a passenger", trips: "{n} rides", book: "Book", del: "Delete",
      empty: "No saved passengers. Add your first passenger.", save: "Save",
      saved_t: "Passenger added", updated_t: "Passenger updated", search_ph: "Search a passenger…",
      edit: "Edit", edit_t: "Edit passenger", err: "Could not save the passenger.",
    },
    inv: {
      t: "Invoices", s: "Automatic monthly consolidation of your rides.",
      trips: "{n} rides", en_cours: "Current", emise: "Issued", payee: "Paid",
      mark_paid: "Mark as paid", mark_issued: "Back to issued",
      empty: "No invoices yet.", lines_t: "Rides of the month", export_csv: "Export lines as CSV",
      err: "Action unavailable right now.",
    },
    tm: {
      t: "Team", s: "Your organisation's members and their roles.",
      add: "Add a member", role: "Role", del: "Remove", you: "you",
      save: "Add", added_t: "Member added", err: "Could not add this member.",
    },
    st: {
      t: "Settings", org_t: "Organisation", org_name: "Organisation name", billing: "Billing e-mail",
      cc_t: "Cost centers", cc_ph: "E.g. Management", cc_empty: "No cost centers — add some to track spend per team.",
      toggles_t: "Preferences",
      toggles: {
        monthly_billing: ["Consolidated monthly billing", "One single invoice per month"],
        whatsapp: ["WhatsApp notifications to passengers", "In addition to SMS and e-mails"],
        flight_tracking: ["Automatic flight tracking", "Adjustment in case of delay"],
        require_cost_center: ["Cost center required", "Internal reference on every booking"],
      },
      save: "Save", saved_t: "Settings saved", err: "Could not save.",
    },
    common: { cancel: "Cancel" },
  },
  tr: {
    eyebrow: "Tracking — {code}",
    booked_by: "Booked for you by",
    eta_live: "Estimated arrival in ~{min} min",
    pos_stale: "Last seen {min} min ago",
    copy: {
      booked: ["We're preparing your ride.", "Your chauffeur is being confirmed — you'll get an email as soon as they're official.", "Departure on schedule"],
      driver_assigned: ["Your chauffeur is confirmed.", "First name, vehicle and plate are below. You'll get an email as soon as they head your way.", "Departure on schedule"],
      driver_on_the_way: ["Your chauffeur is on the way.", "Follow their progress live on the map.", "On the way"],
      driver_arrived: ["Your chauffeur is waiting for you.", "They're at the agreed meeting point.", "They're here"],
      on_board: ["You're on board.", "Route optimized for traffic. Enjoy the ride.", "ETA continuously recalculated"],
      completed: ["You've arrived.", "Thank you for riding with MILES. Your receipt is available below.", "Completed"],
      cancelled: ["This booking has been cancelled.", "No chauffeur will be dispatched. For any question, contact us at info@gomiles.ch.", "Cancelled"],
    },
    pickup_label: "Pickup", arrival_label: "Estimated arrival", completed_label: "Ride completed",
    remaining: "19 min left",
    your_driver: "Your chauffeur", driver_alt: "{name}, MILES chauffeur",
    call_driver: "Call the chauffeur", message_driver: "Message the chauffeur",
    pending: "We're confirming your chauffeur — first name, photo, vehicle and plate will appear here automatically.",
    meeting: "Meeting point: {pickup} — in front of the main entrance{terminal}.",
    terminal_join: ", ",
    timeline_t: "Ride statuses", notif_t: "Notifications sent",
    flight_t: "Flight tracked",
    flight_note: "If you're delayed, we adjust the pickup to the actual landing time.",
    support_t: "A question?", support_b: "A real person answers",
    share: "Share tracking", share_t: "Tracking link copied", share_d: "Share it with anyone who should follow this ride.",
    receipt: "Receipt — {code}", paid_badge: "Paid",
    waiting_line: "Waiting & pickup", included: "included", total: "Total incl. VAT",
    download: "Download receipt", pdf_t: "PDF receipt", pdf_d: "Download coming soon.",
    rebook: "Book again",
    hourly_dest: "Hourly disposal",
    stops_label: "Stops",
    nf_eyebrow: "Tracking link", nf_t: "Booking not found",
    nf_b: "Check the link received by email, or contact support.",
    nf_cta: "Contact support",
    notifs: {
      booked: ["Email", "It's booked. Your ride is confirmed."],
      driver_assigned: ["Email", "Your chauffeur is confirmed — details below."],
      driver_on_the_way: ["Email", "Your chauffeur is on the way to you."],
      driver_arrived: ["Email", "Your chauffeur is waiting for you at the meeting point."],
      on_board: ["Tracking", "Enjoy the ride — the ETA updates live."],
      completed: ["Tracking", "Your receipt is available below."],
    },
  },
  te: {
    eyebrow: "MILES tracking", h1: "Track my ride.",
    p: "Enter your booking code — it's in your confirmation, in ML-XXXXX format. No account needed.",
    ph: "ML-XXXXX", submit: "Track",
  },
  ap: {
    eyebrow: "Airport transfers", img_alt: "Flight above the clouds", h1a: "The airport, handled", h1b: "for you.",
    sub: "Terminal meet & greet, waiting time included, a clear meeting point. And if you share your flight number at booking, pickup follows the real landing time.",
    trust: ["45 min waiting included", "Name-board terminal pickup", "Flight number optional"],
    note_t: "Your flight number?",
    flight_note: "It is optional. Add it at booking: we track the landing and adjust the pickup time if you are delayed — without asking you for anything.",
    h2: "Our airports, mastered.",
    airports: [
      ["GVA", "Geneva Airport", "Meet & greet at Terminal 1, arrivals area. 45 min waiting included.", "15 min from center"],
      ["ZRH", "Zurich Airport", "Meeting point Arrival 2. 45 min waiting included.", "12 min from center"],
      ["BSL", "EuroAirport Basel", "Swiss-side exit, name board. Waiting included.", "15 min from center"],
    ],
    flow_eyebrow: "How it unfolds", flow_h2: "From the jet bridge to the car door.", flow_img_alt: "MILES airport welcome",
    flow: [
      ["You book", "Address, date, time — and your flight number if you like. Optional, but handy."],
      ["Your chauffeur waits", "First name, photo, plate and exact meeting point, sent by email."],
      ["You board", "Luggage handled, route ready, fresh water on board."],
      ["You arrive", "Your receipt is waiting on your tracking link."],
    ],
    band: "A flight soon? We handle the rest.", band_cta: "Book a transfer",
  },
  biz: {
    eyebrow: "MILES Business", h1a: "All your rides.", h1b: "One single organisation.",
    sub: "Book for your team, your executives or your guests. Follow every ride in parallel. One invoice, once a month.",
    cta1: "Create your workspace", cta2: "Sign in",
    shot_alt: "The MILES Business dashboard in real conditions",
    feat_eyebrow: "The workspace, for real",
    f1: {
      tag: "Book", t: "A ride for someone else, in a few moments.",
      d: "Saved passenger or guest entered on the fly: you see the fixed price and the route preview before confirming. The passenger receives a tracking link — no account, no app.",
      points: ["Fixed price computed live on the real route", "Route map before confirmation", "Tracking link to share with the passenger"],
      alt: "New ride with route map and vehicle choice",
    },
    f2: {
      tag: "Manage", t: "All your rides, at a glance.",
      d: "Live statuses, instant search, one-click cancellation and rebooking. Every ride opens in detail: passenger, history, tracking link.",
      points: ["Live statuses, refreshed automatically", "Search by passenger, code or route", "Rebook a past ride in one click"],
      alt: "Ride detail with history and actions",
    },
    f3: {
      tag: "Bill", t: "Billing that runs itself.",
      d: "Rides consolidate automatically by month. Accounting-ready PDF with VAT, CSV export of line items, and payment tracking.",
      points: ["Automatic consolidated monthly invoice", "PDF with 8.1% VAT + CSV export", "Issued / paid statuses"],
      alt: "Monthly invoices with detailed ride lines",
    },
    roles_eyebrow: "The roles", roles_h2a: "Everyone gets", roles_h2b: "their own screen.",
    roles: [
      ["Admin", "Full view: booking, team, settings, billing. You hold the keys."],
      ["Booker", "Front desk, concierge, assistant: book and track rides. Nothing else to manage."],
      ["Finance", "Overview and invoices. Accounting gets its PDFs, period."],
    ],
    flow_eyebrow: "The workflow", flow_h2: "Book for someone, in four moves.",
    flow: [
      ["Choose the passenger", "A saved colleague, or a guest entered in 10 seconds."],
      ["Set the ride", "Saved addresses, flight tracked, right vehicle."],
      ["Send", "The passenger receives everything by email — no account."],
      ["Track in parallel", "You watch the ride progress from your dashboard."],
    ],
    flow_cta: "Create a workspace",
    proof: [
      ["2 min", "to book for someone else, account-free passenger included"],
      ["1 invoice", "consolidated per month, ready for accounting"],
      ["0 expense report", "taxi receipts scattered across inboxes"],
    ],
    proof_note: "Negotiated rates available · Recurring bookings · Multi-user",
    final_eyebrow: "Ready in 2 minutes",
    final_h2a: "Try the demo.", final_h2b: "Then create yours.",
    final_p: "The Maison Démo SA workspace is pre-filled with sample data. Yours starts empty — and belongs to you.",
    final_cta1: "Talk to our team", final_cta2: "Create my workspace",
  },
  dash: {
    hello: "Hello, Maison Démo SA", demo: "Demo mode — sample data", quit: "Exit demo",
    new: "New booking",
    nav: ["Overview", "Bookings", "Trips", "Passengers", "Invoices", "Team", "Settings"],
    plan: "Business plan · 4 users",
    stats: [["Today", "rides"], ["In progress", "live courses"], ["Upcoming", "bookings"], ["Monthly spend", "all teams"]],
    recent: "Recent bookings", view_all: "View all",
    activity: "Recent activity",
    activity_items: [
      ["Chauffeur confirmed", "Yanis B. — Zurich → ZRH", "12 min ago"],
      ["Ride completed", "Sofia M. — Montreux → GVA", "48 min ago"],
      ["Invoice generated", "INV-2026-047 — July", "2 h ago"],
      ["New passenger", "Antoine D. added by the front desk", "yesterday"],
    ],
    quick: "Book for a passenger",
    all_b: "All bookings", all_b_sub: "{n} rides — click to track",
    chart_t: "Rides per day — last 7 days", live_t: "Live & upcoming courses",
    hourly_short: "Hourly",
    invoices_t: "Invoices", invoices_s: "Consolidated monthly billing",
    inv_trips: "{n} rides", inv_paid: "Paid", inv_pending: "Pending",
    pdf_t: "PDF export", pdf_d: "{n} — download coming soon.",
    team_t: "Users & roles",
    team: [
      ["Nathalie Perrot", "Admin — management", "All rights"],
      ["Hotel Front Desk", "Concierge", "Book for clients"],
      ["Marc Olivier", "Office manager", "Booking + invoices"],
      ["Laura Chappuis", "Executive assistant", "Booking only"],
    ],
    settings_t: "Organization settings",
    settings: [
      ["Consolidated monthly billing", "One invoice per month"],
      ["WhatsApp notifications to passengers", "In addition to SMS and emails"],
      ["Automatic flight tracking", "Adjusts in case of delay"],
      ["Mandatory cost centers", "Internal reference on every booking"],
    ],
    book_for: "Book",
  },
  dr: {
    eyebrow: "The MILES chauffeurs", h1a: "Professionals we know", h1b: "by first name.",
    sub: "No white gloves, no ceremony. Punctual, discreet, precise people who treat every ride as a job well done.",
    trust: ["100% verified, trained and insured", "Rated after every ride", "First name, photo and plate shared before departure"],
    moments_eyebrow: "The MILES experience", moments_h2a: "Service,", moments_h2b: "without the ceremony.",
    moments: [
      ["The welcome", "Door opened, luggage stowed, route confirmed. Nothing to explain."],
      ["The ride", "Your tempo, your ambiance: conversation or silence, music or calm. The chauffeur adapts — not the other way around."],
      ["The arrival", "Drop-off as close to the entrance as possible, help with luggage, receipt already sent."],
    ],
    steps_eyebrow: "The confirmation", steps_h2a: "Your chauffeur,", steps_h2b: "identified before departure.",
    steps_sub: "No stranger at the curb: you know who is coming, in which car, and when they arrive.",
    steps: [
      ["Booking confirmed", "Fixed price locked, route secured. Your ride enters the MILES system immediately."],
      ["Confirmation", "For an instant booking, your chauffeur is confirmed within minutes. For a scheduled booking, at the latest 1 h before departure."],
      ["Introduction", "You receive first name, photo, vehicle, colour and plate — by email and on your tracking link."],
      ["Direct contact", "A doubt about the meeting point? Call or message your chauffeur from the tracking page, without exchanging numbers."],
    ],
    h2a: "What we demand.", h2b: "On every ride.",
    commitments: [
      ["Selected one by one", "Interview, background check, driving test: most applications stop along the way."],
      ["Trained in MILES service", "Punctuality, presentation, discretion, knowledge of airports and hotels. The tech, we leave to the app."],
      ["Discreet by principle", "Conversations, routes, passengers: what happens on board stays on board. Every chauffeur signs a confidentiality commitment."],
      ["Local above all", "GVA and ZRH terminals, grand hotels, ski resorts, mountain roads: your chauffeur knows the terrain, not just the GPS."],
      ["Rated on every ride", "Systematic feedback after each trip. A chauffeur whose quality slips is coached — then replaced if needed."],
      ["Paid their fair value", "Well-paid chauffeurs arrive on time, smiling, and stay. It's that simple."],
    ],
    band_eyebrow: "Join MILES",
    band_t: "Are you a professional chauffeur?",
    band_s: "Join a network that respects you — and pays you properly.",
    band_cta: "Become a MILES chauffeur",
    band_alt: "Explore the fleet",
  },
  ct: {
    eyebrow: "Cities & coverage", h1a: "Switzerland, first.", h1b: "Then Europe.",
    sub: "MILES operates in major Swiss cities and their airports, on long-distance corridors and to alpine resorts — with the same fixed price, shown before any payment.",
    trust: ["Guaranteed fixed price", "Verified local chauffeurs", "Instant estimate, no account"],
    cities_eyebrow: "The cities", cities_h2a: "Five bases,", cities_h2b: "all of Switzerland.",
    airport_from: "{code} transfer from {price}",
    book: "Book from {city}",
    cities: [
      ["Geneva", "Private transport Geneva", ["Centre → GVA Airport — 15 min", "Geneva → Lausanne — 50 min", "Geneva → Verbier — 2 h 05"], "GVA", "CHF 90.–"],
      ["Lausanne", "Private chauffeur Lausanne", ["Centre → GVA Airport — 60 min", "Lausanne → Zurich — 2 h 40", "Ouchy → Montreux — 30 min"], "GVA", "CHF 205.–"],
      ["Zurich", "Zurich airport chauffeur", ["Centre → ZRH Airport — 15 min", "Zurich → Basel — 1 h 10", "Zurich → Lucerne — 45 min"], "ZRH", "CHF 90.–"],
      ["Basel", "Private transport Basel", ["Centre → EuroAirport BSL — 15 min", "Basel → Zurich — 1 h 10", "Basel → Bern — 1 h 15"], "BSL", "CHF 90.–"],
      ["Bern", "Private chauffeur Bern", ["Bern → ZRH Airport — 2 h", "Bern → GVA Airport — 2 h 05", "Bern → Interlaken — 50 min"], "ZRH", "CHF 430.–"],
    ],
    ld_eyebrow: "Long-distance corridors", ld_h2a: "City to city,", ld_h2b: "at a fixed price.",
    ld_note: "Estimated Business Class prices on real road distance. The exact price appears in the booking flow, before any payment.",
    corridors: [
      ["Geneva", "Zurich", "279 km", "≈ 3 h 20", "from CHF 905.–"],
      ["Geneva", "Milan", "320 km", "≈ 3 h 55", "from CHF 1,040.–"],
      ["Lausanne", "Lyon", "213 km", "≈ 2 h 30", "from CHF 695.–"],
      ["Geneva", "Lyon", "150 km", "≈ 1 h 55", "from CHF 490.–"],
      ["Zurich", "St. Moritz", "203 km", "≈ 2 h 40", "from CHF 665.–"],
    ],
    corridor_cta: "Estimate",
    ski_eyebrow: "The Alps", ski_h2a: "Straight to", ski_h2b: "the resorts.",
    ski_p: "Skis, bulky luggage, mountain roads: the VAN is built for it. Departures from Geneva, Business Class rates.",
    ski_img_alt: "The MILES VAN, built for the resorts", ski_caption: "Skis & luggage — the VAN",
    ski: [
      ["Verbier", "161 km — from CHF 525.–"],
      ["Zermatt (via Täsch)", "230 km — from CHF 750.–"],
      ["Gstaad", "149 km — from CHF 485.–"],
      ["Crans-Montana", "185 km — from CHF 605.–"],
    ],
    ski_cta: "Book",
    city_pages: {
      back: "All cities", airport_t: "Airport transfer", routes_t: "Popular rides",
      routes_s: "Business Class prices estimated on the real road distance, measured via OSRM.",
      places_t: "Districts & places served", estimate: "Estimate", from_word: "from", others_t: "Also served",
      geneve: {
        tag: "Geneva · GVA Airport",
        h1: "Private transport in Geneva",
        sub: "A MILES-verified chauffeur in Geneva and from GVA: fast pickup in the centre, a fixed fare known before you board, name-board welcome at the terminal.",
        airport_note: "Name-board welcome at Terminal 1, arrivals area. 45 min waiting included.",
        route_labels: [["Centre → GVA Airport", "≈ 15 min"], ["Geneva → Lausanne", "≈ 50 min"], ["Geneva → Montreux", "≈ 1 h 10"], ["Geneva → Verbier", "≈ 2 h 05"]],
        places: ["Old Town", "Rive Gauche", "Cologny", "Carouge", "Palexpo", "UN — international district", "Lakeside hotels"],
      },
      lausanne: {
        tag: "Lausanne · via GVA",
        h1: "Private chauffeur in Lausanne",
        sub: "Lausanne ↔ Geneva Airport transfers all day, city-to-city rides and hourly hire. Fixed price, live tracking, instant receipt.",
        airport_note: "GVA drop-off and pickup with waiting time included — 63 km of Lake Geneva Riviera, no platform involved.",
        route_labels: [["Centre → GVA Airport", "≈ 60 min"], ["Lausanne → Zurich", "≈ 2 h 40"], ["Lausanne → Montreux", "≈ 30 min"]],
        places: ["Ouchy", "CFF station", "Le Flon", "EPFL & UNIL", "Lavaux", "Beau-Rivage Palace"],
      },
      zurich: {
        tag: "Zurich · ZRH Airport",
        h1: "Private chauffeur in Zurich",
        sub: "From Bahnhofstrasse to ZRH in a quarter of an hour: welcome at Arrival 2, waiting time included, fixed fare shown before payment.",
        airport_note: "Meeting point Arrival 2. 45 min waiting included.",
        route_labels: [["Centre → ZRH Airport", "≈ 15 min"], ["Zurich → Basel", "≈ 1 h 10"], ["Zurich → Lucerne", "≈ 45 min"], ["Zurich → St. Moritz", "≈ 2 h 40"]],
        places: ["Bahnhofstrasse", "Zurich West", "Enge & Seefeld", "Kloten", "Zug", "The Circle"],
      },
      bale: {
        tag: "Basel · EuroAirport BSL",
        h1: "Private transport in Basel",
        sub: "EuroAirport on the Swiss side, fast exit, a chauffeur who knows the three borders. Basel, Zurich and Bern at fixed prices.",
        airport_note: "Swiss-side exit, name board. Waiting time included.",
        route_labels: [["Centre → EuroAirport BSL", "≈ 15 min"], ["Basel → Zurich", "≈ 1 h 10"], ["Basel → Bern", "≈ 1 h 15"]],
        places: ["Altstadt", "Messe Basel", "Saint-Louis (FR)", "Mulhouse (FR)", "Novartis & Roche campuses"],
      },
      berne: {
        tag: "Bern · via ZRH & GVA",
        h1: "Private chauffeur in Bern",
        sub: "A capital without a major airport? MILES connects Bern to ZRH and GVA on the clock — and drives on to the Oberland without a train change.",
        airport_note: "Departures synchronised with your ZRH or GVA flights, waiting margin included.",
        route_labels: [["Bern → ZRH Airport", "≈ 2 h"], ["Bern → GVA Airport", "≈ 2 h 05"], ["Bern → Interlaken", "≈ 50 min"]],
        places: ["Altstadt (UNESCO)", "Bundeshaus", "Kirchenfeld", "Wabern", "Belp", "Gurten"],
      },
    },
    final_eyebrow: "Your ride",
    final_h2a: "The exact price,", final_h2b: "in 30 seconds.",
    final_p: "Enter your two addresses: the real estimate — distance, duration, price per category — appears instantly, no account needed.",
    final_cta: "Estimate my ride",
  },
  faq: {
    eyebrow: "FAQ", h1a: "Answers,", h1b: "no detours.",
    sub: "Everything people actually ask us. Missing an answer? A real person replies.",
    band: "A specific question?", band_contact: "Contact support", band_book: "Book",
    items: [
      ["Can I book without an account?", "Yes. Name, phone, email: that's enough. You immediately receive your confirmation and personal tracking link. A MILES account exists, but stays optional."],
      ["Can I book a round trip?", "Yes. Choose 'Round trip' at the first step: you set the return date and time, and we schedule both rides. The return gets an adjusted fare."],
      ["Can I book by the hour?", "Yes, that's the 'By the hour' mode. You choose the pickup location, date, time and duration (2 h minimum). The chauffeur and vehicle stay at your disposal."],
      ["Can I book for someone else?", "Yes. Simply enter the passenger's name and phone at the Details step. They receive their information and tracking link — without needing an account."],
      ["How does tracking work?", "Every booking generates a personal, secure tracking link. Chauffeur position, ride status and estimated arrival update in real time. Share it with anyone."],
      ["When will I receive my chauffeur's details?", "As soon as your chauffeur is confirmed — first name, photo, vehicle, color and plate. For an immediate booking, allow a few minutes. For a scheduled one, at the latest 1 h before pickup."],
      ["What happens if my flight is delayed?", "Enter your flight number when booking: we track the flight and adjust the pickup to the actual landing. Airport waiting is included."],
      ["Can I modify or cancel a booking?", "Yes, free of charge up to 1 h before departure for a simple ride, and up to 24 h for hourly or long-distance. From your tracking link or by contacting support."],
      ["How does payment work?", "The price is set and shown before confirmation. You pay securely by bank card at booking time. The receipt is available right after the ride."],
      ["How does MILES Business work?", "A dedicated space for your organization: book for a colleague, client or guest in seconds, track all rides in parallel, and receive one consolidated invoice at month end."],
      ["How does business billing work?", "All of the month's rides are grouped on a single invoice, with cost centers and internal references if you wish. PDF and accounting export in one click."],
    ],
  },
  co: {
    eyebrow: "Support & Contact", h1a: "Humans,", h1b: "not a chatbot.",
    sub: "A ride in progress, an invoice, a Business account, an event to coordinate — our Geneva team answers fast, and well.",
    rows: [["Email", "info@gomiles.ch"], ["Phone", "+41 79 195 76 06"]],
    topic_l: "Topic",
    topics: ["Question about a booking", "MILES Business", "Become a chauffeur", "Event & groups", "Other request"],
    name: "Full name", email: "Email", company: "Company — optional",
    message: "Describe your need in a few words…",
    submit: "Send", sending: "Sending…",
    note: "Human answer — never a chatbot",
    ok_t: "Well received.", ok_b: "Thank you {name} — a MILES advisor will reply within 2 h during the day.",
    toast_t: "Message sent", toast_d: "A real person replies within 2 h during the day.",
    err_t: "Sending failed", err_d: "Try again or write to info@gomiles.ch",
  },
};

const de = {
  nav: {
    airport: "Flughafentransfers", services: "Services", fleet: "Flotte", business: "Business", drivers: "Fahrer",
    cities: "Städte", faq: "FAQ", track: "Tracking", book: "Buchen", book_ride: "Fahrt buchen", guides: "Ratgeber",
  },
  bar: {
    one_way: "Einfach", round_trip: "Hin und zurück", hourly: "Stundenweise",
    available: "Fahrer verfügbar", from: "Abholung", to: "Ziel", date: "Datum", time: "Uhrzeit",
    from_ph: "Abholadresse", to_ph: "Zieladresse",
    hourly_note: "Ihr Fahrer steht Ihnen 2 bis 24 Stunden zur Verfügung.",
    submit: "Fahrzeuge ansehen",
    ticker: "Live — Alexandre ist in 8 Min. da · EQE · GE 512 408",
    missing: "Adresse fehlt", missing_desc: "Geben Sie eine Abhol- und eine Zieladresse ein.",
  },
  addr: {
    loading: "Adressen werden gesucht…",
    empty: "Keine Vorschläge — Sie können mit Ihrer Adresse fortfahren.",
    unavailable: "Vorschläge nicht verfügbar — Ihre freie Adresse wird weiterhin akzeptiert.",
    attribution: "© OpenStreetMap contributors / Photon",
  },
  hero: {
    img_alt: "MILES-Limousine am Genfer Seeufer",
    t1: "Ihr privater Fahrer,", t2: "ohne Platz für Überraschungen.",
    sub: "Buchen Sie Ihre Fahrt, verfolgen Sie Ihren Fahrer, finden Sie jede Information an einem Ort. Der Preis steht vor der Abfahrt fest — wir kümmern uns um den Rest.",
    sub_short: "Wo sich jeder Kilometer anders anfühlt.",
  },
  manifesto: {
    eyebrow: "Das MILES-Manifest",
    t1: "Ihre Fahrt.", t2: "Unsere Organisation.",
    p: "Eine Buchung genügt, um alles auszulösen: bestätigter Fahrer, Flugverfolgung, echte Route, ein Tracking-Link zum Teilen.",
    cta: "So funktioniert es",
    chapters: [
      ["Sie buchen.", "Zwei Adressen, eine Uhrzeit, ein Fahrzeug. Der Festpreis steht vor der Zahlung — die Bestätigung kommt sofort."],
      ["Sie verfolgen.", "Fahrer, Fahrzeug, Kennzeichen, Ankunftszeit: alles sichtbar auf Ihrem Link, Minute für Minute."],
      ["Sie kommen an.", "Flugverspätung abgefangen, Treffpunkt klar, Quittung bereit. Nichts zu organisieren."],
    ],
    photo_alt: "MILES-Limousine vor einem Schweizer Hotel",
    photo_label: "Genf — Centre → Flughafen GVA",
    photo_status: "Abfahrt bestätigt",
    photo_price: "CHF 74.– · Festpreis",
    card_note: "Fahrer vor Abfahrt bestätigt",
  },
  homeFleet: {
    eyebrow: "Die MILES-Flotte",
    h2a: "Vier Fahrzeuge.", h2b: "Eine einfache Wahl.",
    p: "Jede Kategorie beantwortet einen Zweck: Alltagsfahrt, Geschäftstermin, grosser Empfang, Gruppe. Der genaue Preis erscheint vor jeder Zahlung.",
    cta: "Ganze Flotte entdecken",
    items: {
      standard: { name: "Standard Class", model: "Tesla Model Y", desc: "Dieselbe MILES-Sorgfalt zum besten Preis — für Alltagsfahrten." },
      van: { name: "VAN", model: "Mercedes V-Klasse", desc: "Fürs gemeinsame Reisen — Ski und Gepäck inklusive — ohne Organisationsaufwand." },
      first: { name: "First Class", model: "Mercedes S-Klasse", desc: "Mehr Raum und Ruhe, wenn die Fahrt mehr zählt." },
      business: { name: "Business Class", model: "Mercedes EQE oder E-Klasse", desc: "Für Alltagsfahrten, Termine und Flughafentransfers." },
    },
  },
  statuses: {
    booked: "Bestätigt", driver_assigned: "Fahrer bestätigt", driver_on_the_way: "Unterwegs",
    driver_arrived: "Eingetroffen", on_board: "An Bord", completed: "Abgeschlossen", cancelled: "Storniert",
  },
  homeTrack: {
    eyebrow: "MILES-Tracking", h2: "Sie wissen immer, was passiert.", cta: "Live-Tracking öffnen",
    chip: "Fahrer unterwegs", arrive: "Alexandre ist in 8 Min. da.",
    car: "Mercedes EQE · Obsidianschwarz", call: "Anrufen", message: "Nachricht",
    eta: "Voraussichtliche Ankunft", caption: "Das Statussystem",
    notif_t: "MILES · jetzt", notif_b: "Ihr Fahrer kommt in ca. 8 Min.",
  },
  expect: {
    eyebrow: "Konkret", h2a: "Sie wissen,", h2b: "was Sie erwartet.",
    p: "Jede MILES-Fahrt folgt demselben Faden — von der Buchung bis zur Quittung. Hier ist er, unverkürzt.",
    items: [
      ["Preis vor der Buchung bestätigt", "Der angezeigte Tarif ist der bezahlte Tarif."],
      ["Sofortige Bestätigung", "Zusammenfassung, Fahrtnummer, Tracking-Link."],
      ["Fahrer bestätigt", "Vorname, Foto, Fahrzeug, Kennzeichen."],
      ["Fahrt live verfolgt", "Position, Status, voraussichtliche Ankunft."],
      ["Flug überwacht", "Verspätung? Die Abholung passt sich an."],
      ["Ein echter Mensch antwortet", "Das MILES-Team — kein Chatbot."],
    ],
  },
  interlude: {
    eyebrow: "Stadt zu Stadt", h2a: "Genf → Mailand.", h2b: "Vier Stunden für Sie.",
    cta: "Langstrecke buchen",
  },
  homeAirport: {
    eyebrow: "Flughafentransfers", h2a: "Ihr Flug ändert sich.", h2b: "Ihre Abholung auch.",
    p: "Geben Sie bei der Buchung Ihre Flugnummer an: Bei Verspätung wird die Abholung angepasst und Ihr Fahrer informiert. Ohne dass Sie etwas tun müssen.",
    cta: "Flughafen-Erlebnis ansehen",
    route: "Zürich → Genf", delay: "+22 Min. Verspätung",
    l1: "Geplante Landung", l2: "Tatsächliche Landung", l3: "Abholung",
    adjusted: "Ihre Abholung wurde angepasst.",
    meeting: "Alexandre erwartet Sie — Ankunft 2", meeting_l: "Treffpunkt",
  },
  homeBiz: {
    eyebrow: "MILES Business", h2: "Alle Fahrten des Unternehmens. Eine einzige Organisation.",
    p: "Buchen Sie für Mitarbeitende, Führungskräfte oder Gäste — ohne Konto für diese. Verfolgen Sie jede Fahrt parallel. Eine konsolidierte Rechnung am Monatsende.",
    demo: "Demo anfordern", explore: "Produkt entdecken",
    today: "Heute — Maison Démo SA", new: "Neue Buchung",
    side: ["Overview", "Bookings", "Passengers", "Invoices", "Team"],
    for: ["Für mich", "Mitarbeiter", "Gast"],
    noacct: "Der Fahrgast braucht kein Konto",
    ride_status: ["Unterwegs", "Bestätigt", "Fahrer bestätigt"],
    inv_t: "Rechnung INV-2026-047 bereit", inv_b: "96 konsolidierte Fahrten · Buchhaltungsexport",
  },
  citiesCta: {
    eyebrow: "Abdeckung", h2a: "Genf. Lausanne. Zürich.", h2b: "Basel. Bern. Und morgen weiter.",
    book: "Fahrt buchen", cities: "Abdeckung ansehen",
  },
  footer: {
    tag: "MILES organisiert Ihre Fahrten mit Chauffeur in der ganzen Schweiz — von der Buchung bis zur Ankunft.",
    book: "Buchen", business: "MILES Business",
    cols: ["Produkt", "Unternehmen", "Gesellschaft"],
    links: [
      ["Unsere Services", "Buchen", "Flughafentransfers", "Flotte & Tarife", "Städte & Abdeckung"],
      ["MILES Business", "Dashboard — Demo", "Hotels & Concierge"],
      ["Fahrer", "FAQ", "Kontakt", "MILES Ratgeber"],
    ],
    legal: ["AGB", "Datenschutz", "Cookies"],
    copy: "© 2026 MILES — Alle Rechte vorbehalten",
  },
  bk: {
    steps: ["Fahrt", "Fahrzeug", "Reisender", "Zahlung"],
    questions: ["Wohin und wann fahren Sie?", "Wählen Sie Ihr Fahrzeug", "Für wen ist diese Fahrt?", "Buchung abschliessen"],
    help: "Brauchen Sie Hilfe?", route_step_sub: "Adresse, Zeit und Reisende — Ihre Fahrt entsteht sofort.",
    pax: "Reisende", pax_one: "Person", pax_many: "Personen", bags: "Gepäck", bag_one: "Gepäckstück", bag_many: "Gepäckstücke",
    pax_summary: "{p} Person(en) · {b} Gepäckstück(e)", swap_route: "Route umkehren",
    flight_context: "Reisen Sie mit dem Flugzeug?", flight_context_sub: "Wir verfolgen Ihren Flug und passen die Abholung bei Bedarf an.",
    trip_card_title: "Ihre Fahrt", trip_card_sub: "Sie entsteht während der Buchung.", trip_summary_pending: "Abholung und Ziel hinzufügen",
    edit: "Bearbeiten", cta_vehicles: "Fahrzeuge ansehen", cta_traveler: "Dieses Fahrzeug wählen", cta_payment: "Zur Zahlung",
    vehicle_step_sub: "Festpreis. Fahrer inklusive. Keine versteckten Kosten.",
    multi_hint: "Mehrere Fahrzeuge nötig? Wählen Sie alle aus und passen Sie die Anzahl an.",
    vehicles_word: "Fahrzeuge",
    units_title: "Ein Tracking-Link pro Fahrzeug",
    payment_sub: "Der angezeigte Betrag ist der bestätigte Preis Ihrer Fahrt.", pay_t: "Sichere Zahlung", final_pay: "Für {p} buchen",
    promo_label: "Promo-Code", promo_ph: "Code eingeben", promo_apply: "Anwenden", promo_remove: "Entfernen",
    promo_invalid: "Ungültiger oder abgelaufener Code.", promo_total: "Total nach Rabatt",
    paid_t: "Zahlung bestätigt", paid_b: "Ihre MILES-Buchung wurde erstellt.",
    add_request: "Besondere Anfrage hinzufügen", fname: "Vorname", lname: "Nachname", view_booking: "Meine Buchung ansehen",
    done_sub: "Wir schreiben Ihnen, sobald Ihr Fahrer bestätigt ist.",
    trust_items: ["Festpreis vor Zahlung bestätigt", "Fahrer geprüft und vor Abfahrt bekannt", "Ein echter Mensch antwortet"],
    options_title: "Nützliche Optionen", option_child_seat: "Kindersitz", option_meet_greet: "Empfang mit Namensschild",
    options_note: "Diese Anfragen werden an das MILES-Team übermittelt und vor Abfahrt bestätigt.",
    terms_text: "Ich akzeptiere die MILES-Buchungsbedingungen.", terms_view: "Bedingungen ansehen", terms_hide: "Bedingungen ausblenden",
    terms_items: ["Der angezeigte Preis wird vor der Zahlung bestätigt.", "Ihre Angaben dienen der Fahrtkoordination und dem Tracking.", "Besondere Anfragen werden von MILES vor Abfahrt bestätigt.", "Ein angegebener Flug wird zur Anpassung der Abholung verfolgt."],
    cta_notes: ["Reale Route und Preise werden sofort berechnet.", "Festpreis, Fahrer inklusive, keine versteckten Kosten.", "Ihre Angaben dienen nur der Fahrtkoordination.", "Sie erhalten sofort Bestätigung und Tracking-Link."],
    back: "Zurück", next: "Weiter", to_payment: "Weiter zur Zahlung",
    secure: "Festpreis vor Zahlung bestätigt",
    from: "Abholung", to: "Ziel",
    from_ph: "Z. B. Rue du Rhône 24, Genf", to_ph: "Z. B. Flughafen Genf GVA",
    diff: "Wählen Sie zwei verschiedene Adressen.",
    duration: "Dauer", schedule_t: "Abfahrt & Rückfahrt",
    flight_t: "Flug — wir verfolgen Ihre Maschine",
    flight_ph: "Flugnr. — z. B. LX 318", terminal_ph: "Terminal — optional",
    flight_note: "Bei Verspätung passen wir die Abholzeit an. Die Wartezeit am Flughafen ist inklusive.",
    date: "Datum", time: "Uhrzeit", return_t: "Rückfahrt",
    passengers: "Passagiere", luggage: "Gepäck",
    notes: "Notizen & Sonderwünsche",
    notes_ph: "Kindersitz, Zwischenstopp, besondere Hinweise…",
    est_loading: "Preis wird berechnet…",
    est_line: "{km} km · ca. {min} Min. · garantierter Festpreis",
    est_hourly: "{h} Std. zur Verfügung · Abholung {from}",
    est_unavailable: "Preis nicht verfügbar — zurück zum vorherigen Schritt.",
    info_note: "Kein Konto nötig. Ihre Angaben werden nur für diese Fahrt verwendet. Sie buchen für jemand anderen? Geben Sie hier deren Daten ein.",
    firstname: "Vorname", lastname: "Nachname", email: "E-Mail",
    phone_ph: "Telefon — z. B. +41 79 123 45 67",
    card: "Bankkarte",
    secure_note: "Verschlüsselte Zahlung. Der Betrag wird erst bei Bestätigung abgebucht — nie ein Aufschlag nach der Fahrt.",
    pay_loading: "Sichere Zahlung wird geladen…",
    pay_methods_note: "Sichere Zahlung über SumUp — MILES sieht Ihre Kartendaten nie.",
    pay_online: "Online", pay_online_h: "Karte — sofortige sichere Zahlung (SumUp)",
    pay_card_board: "Karte an Bord", pay_card_board_h: "Zahlungsterminal beim Chauffeur",
    pay_cash: "Bar", pay_cash_h: "Zahlung beim Chauffeur",
    pay_card_note: "Bezahlen Sie per Karte bei Ihrem Chauffeur (SumUp-Terminal). Ihre Buchung ist sofort bestätigt.",
    pay_cash_note: "Bezahlen Sie bar bei Ihrem Chauffeur. Ihre Buchung ist sofort bestätigt.",
    final_board: "Buchung bestätigen — {p} an Bord",
    booked_t: "Buchung bestätigt", booked_b: "Zahlung an Bord bei Ihrem Chauffeur.",
    pay: "{p} bezahlen", paying: "Zahlung läuft…",
    pay_err_t: "Zahlung fehlgeschlagen", pay_err_d: "Prüfen Sie Ihre Angaben und versuchen Sie es erneut.", pay_declined: "Die Zahlung wurde abgelehnt. Prüfen Sie Ihre Angaben oder verwenden Sie eine andere Karte.",
    pay_pending_t: "Zahlung wird geprüft", pay_pending_b: "Ihre Bank schliesst die Autorisierung ab. Sie erhalten die Bestätigung für Fahrt {code} per E-Mail, sobald sie bestätigt ist.",
    duration_km: "{h} Std. Disposition · {km} km inklusive", duration_km_short: "{km} km inklusive", from_hourly: "Abholort",
    hourly_km_note: "{km} km inklusive · darüber: {rate}/km",
    est_err: "Preis nicht verfügbar",
    confirm_title: "Gebucht.", code_prefix: "Buchung",
    l_ride: "Fahrt", l_dt: "Datum & Uhrzeit", l_vehicle: "Fahrzeug", l_amount: "Betrag",
    l_flight: "Flug verfolgt", l_pax: "Passagiere",
    paid: "bezahlt", return_word: "Rückfahrt",
    hourly_line: "{h} Std. zur Verfügung — {from}",
    email_note: "Eine Bestätigungs-E-Mail wird sofort versendet. Sobald Ihr Fahrer bestätigt ist, erhalten Sie Vorname, Fahrzeug und Kennzeichen per E-Mail.",
    track_cta: "Meine Fahrt verfolgen", again: "Weitere Fahrt buchen",
    demand_sent: "{t}-Anfrage gesendet", demand_desc: "Bestätigung per E-Mail in unter 2 Minuten.",
    rail_title: "Ihre Fahrt", rail_from: "Abholadresse", rail_to: "Zieladresse",
    rail_hourly: "{h} Std. zur Verfügung",
    rail_type: "Typ", rail_date: "Datum", rail_time: "Uhrzeit", rail_pax: "Reisende", rail_flight: "Flug verfolgt",
    rail_route: "Route", rail_datetime: "Abfahrt", rail_real: "Reale Route", rail_vehicle: "Fahrzeug",
    rail_price_note: "Der Festpreis erscheint bei der Fahrzeugwahl — vor jeder Zahlung.",
    rail_fixed: "Festpreis", price_guaranteed: "Garantiert",
    fixed_price: "Festpreis", route_map_alt: "Routenvorschau", route_map_pending: "Route ausstehend", route_map_ready: "Abfahrbereit",
    vehicle_route: "{min} Min. Fahrt", vehicle_select: "Wählen", vehicle_selected: "Ausgewählt",
    for_me: "Für mich", for_guest: "Für einen Gast",
    guest_note: "Der Fahrgast erhält Tracking-Link und Fahrerdetails. Sie behalten Kontrolle über Buchung und Zahlung.",
    retry_estimate: "Preis neu berechnen", est_unavailable_hint: "Prüfen Sie die Adresse oder versuchen Sie es erneut. Sie können auch zum Schritt Fahrt zurückkehren.",
    hourly_word: "Stundenweise",
    types: { one_way: "Einfach", round_trip: "Hin und zurück", hourly: "Stundenweise" },
  },
  veh: {
    pax: "{n} Passagiere", bags: "{n} Gepäckstücke",
    standard: { name: "Standard Class", model: "Tesla Model Y oder vergleichbar", editorial: "Derselbe Fahrer und dasselbe Tracking wie in jeder MILES-Klasse — zum besten Preis.", features: ["Bester Preis", "100 % elektrisch", "Leise & neuwertig"] },
    business: { name: "Business Class", model: "Mercedes EQE oder E-Klasse", editorial: "Für Alltagsfahrten, Termine und Flughafentransfers.", features: ["WLAN an Bord", "Wasser & Ladegeräte", "Empfang mit Namensschild"] },
    first: { name: "First Class", model: "Mercedes S-Klasse oder ähnlich", editorial: "Mehr Raum und Ruhe, wenn die Fahrt mehr zählt.", features: ["60 Min. Wartezeit inklusive", "Komfort-Rücksitze", "Tagespresse"] },
    van: { name: "VAN", model: "Mercedes V-Klasse oder ähnlich", editorial: "Fürs gemeinsame Reisen ohne Organisationsaufwand.", features: ["6 Passagiere", "Grosser Kofferraum", "Ideal für Gruppen & Ski"] },
  },
  fleet: {
    eyebrow: "Die MILES-Flotte", h1a: "Das richtige Fahrzeug,", h1b: "für jede Fahrt.",
    sub: "Vier klare Kategorien, neue und makellose Fahrzeuge und auf jeder Fahrt dasselbe Finish. Der genaue Preis erscheint im Buchungsablauf, vor jeder Zahlung.",
    trust: ["Garantierter Festpreis", "Kostenlose Stornierung", "Von MILES geprüfte Fahrer"],
    glance_eyebrow: "Überblick", glance_h2a: "Vier Klassen.", glance_h2b: "Eine klare Wahl.",
    cat: "Kategorie", from: "ab CHF {n}.–", book: "Dieses Fahrzeug buchen",
    models_t: "Beispielmodelle", onboard_t: "An Bord", ideal_t: "Ideal für",
    price_note: "Der genaue Preis wird vor jeder Zahlung angezeigt.",
    classes: {
      standard: {
        tagline: "Das MILES-Essentielle, zum richtigen Preis.",
        editorial: "Leise und neuwertig, mit demselben Fahrer und demselben Tracking wie in jeder MILES-Klasse: der Alltag, gut organisiert.",
        models: ["Tesla Model Y", "Tesla Model 3 oder vergleichbar"],
        onboard: ["Leiser, vollelektrischer Innenraum", "USB-C-Ladegeräte", "2-Zonen-Klimaanlage"],
        ideal: ["Alltagsfahrten", "Flughafentransfers", "Stadt zu Stadt"],
        wait: "45 Min.", hourly: "ab CHF 60.–/Std.",
        interior_alt: "MILES Standard-Class-Limousine unterwegs", interior_caption: "Unterwegs — Lausanne → Genf",
      },
      business: {
        tagline: "Der Standard für Geschäftsreisen.",
        editorial: "Pünktlich und leise, mit WLAN und Ladegeräten: Die Arbeit geht zwischen zwei Terminen weiter — oder bis zum Flughafen.",
        models: ["Mercedes EQE", "Mercedes E-Klasse", "BMW 5er oder vergleichbar"],
        onboard: ["WLAN an Bord", "Mineralwasser & USB-C-Ladegeräte", "Empfang mit Namensschild am Flughafen", "Leiser Innenraum, 2-Zonen-Klimaanlage"],
        ideal: ["Geschäftsreisen", "Flughafentransfers", "Stadt zu Stadt"],
        wait: "45 Min.", hourly: "ab CHF 85.–/Std.",
        interior_alt: "MILES Business-Limousine bei Nacht unterwegs", interior_caption: "Unterwegs — Genf → Zürich",
      },
      first: {
        badge: "MILES Signature",
        tagline: "Wenn die Fahrt mehr zählt.",
        editorial: "Grosszügiger Fond, verstellbare Sitze, erfahrener Fahrer: für Ankünfte, die zählen, und lange Strecken.",
        models: ["Mercedes S-Klasse", "BMW 7er oder vergleichbar"],
        onboard: ["60 Min. Wartezeit am Flughafen inklusive", "Komfort-Fondsitze mit Neigung", "Kühle Getränke & Tagespresse", "Erfahrener MILES-Fahrer"],
        ideal: ["Ankünfte, die zählen", "Langstrecken", "Besondere Anlässe"],
        wait: "60 Min.", hourly: "ab CHF 100.–/Std.",
        interior_alt: "First-Class-Fond", interior_caption: "First-Class-Innenraum",
      },
      van: {
        tagline: "Raum, ohne Kompromisse.",
        editorial: "Sechs echte Plätze und ein Kofferraum für Ski und Gepäck: Teams, Familien und Events reisen zusammen, einfach.",
        models: ["Mercedes V-Klasse", "Mercedes EQV oder vergleichbar"],
        onboard: ["6 Passagiere in realer Konfiguration", "Ski, Velos & sperriges Gepäck", "Zwei Komfort-Sitzreihen", "Ideal für Gruppentransfers"],
        ideal: ["Gruppen & Familien", "Ski-Reisen", "Events & Roadshows"],
        wait: "45 Min.", hourly: "ab CHF 95.–/Std.",
        interior_alt: "Grosszügiger Innenraum des MILES VAN", interior_caption: "Zwei Reihen, sechs Plätze",
      },
    },
    specs: { pax: "Passagiere", bags: "Gepäck", wait: "Wartezeit Flughafen inklusive", hourly: "Stundenweise" },
    included_eyebrow: "Der MILES-Standard", included_h2a: "In jeder Fahrt", included_h2b: "inklusive.",
    included: [
      ["Garantierter Festpreis", "Der bei der Buchung angezeigte Preis ist der bezahlte Preis. Verkehr oder Unvorhergesehenes: nichts ändert sich."],
      ["Flugverfolgung & angepasste Wartezeit", "Flugnummer angegeben? Die Abholung folgt der tatsächlichen Landezeit, nicht dem Plan."],
      ["Kostenlose Stornierung", "Bis 1 Std. vor einer einfachen Fahrt, 24 Std. bei stundenweiser Buchung oder Langstrecke."],
      ["Von MILES geprüfte Fahrer", "Strenge Auswahl, laufende Schulung, absolute Diskretion — derselbe Standard auf jeder Fahrt."],
      ["Neue, makellose Fahrzeuge", "Eine neue Flotte, gewartet und vor jedem Dienst geprüft."],
      ["Ein echter Mensch antwortet", "Das MILES-Team ist vor, während und nach Ihrer Fahrt erreichbar — kein Chatbot."],
    ],
    compare_eyebrow: "Vergleich", compare_h2a: "Die drei Klassen,", compare_h2b: "Seite an Seite.",
    compare_rows: { models: "Beispielmodelle", pax: "Passagiere", bags: "Gepäck", wait: "Wartezeit Flughafen inklusive", hourly: "Stundenweise", from: "Fahrt ab", ideal: "Ideal für" },
    note: "Modelle dienen der Veranschaulichung. Sie buchen eine Kategorie: Ein Fahrzeug dieser oder einer höheren Kategorie ist garantiert.",
    final_eyebrow: "Bereit zur Abfahrt", final_h2a: "Der genaue Preis,", final_h2b: "vor jeder Zahlung.",
    final_p: "Geben Sie Ihre Route ein: Die echte Schätzung erscheint sofort — ohne Konto, ohne Verpflichtung.",
    final_cta: "Meine Fahrt schätzen",
  },
  sv: {
    eyebrow: "MILES Services", h1a: "Ein Fahrer,", h1b: "für jede Situation.",
    sub: "Sieben Services, ein Standard: Festpreis vor jeder Zahlung, geprüfter Fahrer, Live-Tracking. Wählen Sie den passenden für Ihre Fahrt.",
    card_cta: "Entdecken", back: "Alle Services",
    inc_eyebrow: "Inklusive", inc_h2a: "Alles inklusive.", inc_h2b: "Aus Prinzip.",
    steps_eyebrow: "Der Ablauf", steps_h2a: "So", steps_h2b: "funktioniert es.",
    price_t: "Preis", fleet_link: "Die Flotte entdecken",
    final_h2: "Unsicher? Der genaue Preis erscheint in 30 Sekunden.",
    final_p: "Geben Sie Ihre Route in der Buchung ein: Die echte Schätzung erscheint sofort — ohne Konto, ohne Verpflichtung.",
    final_cta: "Meine Fahrt schätzen",
    items: {
      airport: { name: "Flughafentransfers", desc: "GVA, ZRH, BSL: Empfang am Terminal, Wartezeit inklusive, Flug verfolgt, wenn Sie ihn anvertrauen." },
      p2p: { name: "Direktfahrt", desc: "Von A nach B zum angezeigten Preis. Die Einfachheit einer Fahrt, die Sorgfalt eines privaten Fahrers." },
      hourly: { name: "Stundenweise Buchung", desc: "Fahrer und Fahrzeug ganz für Sie, 2 bis 24 Stunden. Termine in Serie, ohne Wartezeit." },
      long: { name: "Langstrecke", desc: "Genf–Zürich, Lausanne–Mailand: der Komfort des Zugs, ohne Perrons und Fahrpläne." },
      corporate: { name: "MILES Business", desc: "Firmenkonten, Buchung für Dritte, konsolidierte Monatsrechnung." },
      hotels: { name: "Hotels & Concierge", desc: "Ihre Rezeption bucht für Gäste in unter einer Minute, mit geteiltem Live-Tracking." },
      events: { name: "Events & Gruppen", desc: "Koordinierte Flotten, Gäste-Shuttles, ein fester Koordinator am Tag X." },
    },
  },
  homeSvc: {
    eyebrow: "Unsere Services", h2a: "Sieben Services.", h2b: "Ein Standard.",
    cta: "Alle Services entdecken",
  },
  svc: {
    p2p: {
      name: "Direktfahrt",
      h1a: "Von A nach B,", h1b: "ohne jeden Umweg.",
      sub: "Zwei Adressen eingeben, den Festpreis sofort sehen, Ihr Fahrer kommt pünktlich. Nichts weiter — und genau das ist der Punkt.",
      usps: ["Festpreis live berechnet", "Von MILES geprüfter Fahrer", "Teilbares Live-Tracking"],
      included: [
        ["Preis bei Buchung fixiert", "Der angezeigte Betrag ist der belastete Betrag. Verkehr oder Umweg: Er bleibt."],
        ["Abholung an der exakten Adresse", "Vor Ihrer Tür, Ihrem Büro oder Hotel — nicht an der nächsten Ecke."],
        ["Live-Tracking", "Fahrerposition, Fahrtstatus und Ankunftszeit auf Ihrem persönlichen Link."],
        ["Eine klare Quittung, sofort", "Kaum ist die Fahrt zu Ende, liegt Ihre Quittung auf Ihrem Tracking-Link bereit."],
      ],
      steps: [
        ["Sie buchen", "Zwei Adressen, ein Datum, eine Uhrzeit. Dreissig Sekunden, ohne Konto."],
        ["Ihr Fahrer wird bestätigt", "Vorname, Foto, Fahrzeug und Kennzeichen: Sie wissen, wer kommt, bevor er eintrifft."],
        ["Sie verfolgen", "Er nähert sich? Sie sehen es. Teilen Sie das Tracking mit allen, die Sie erwarten."],
        ["Sie kommen an", "Ausstieg so nah wie möglich, Quittung auf Ihrem Tracking-Link, nichts zu unterschreiben."],
      ],
      price_v: "ab CHF 90.–", price_note: "Der genaue Preis wird auf Ihrer Route berechnet und vor jeder Zahlung angezeigt.",
      final_cta: "Meine Fahrt schätzen",
    },
    hourly: {
      name: "Stundenweise Buchung",
      h1a: "Ihr eigener Fahrer,", h1b: "stundenweise.",
      sub: "Termine in Serie, Shopping, Roadshow, ein Tag voller Besuche: Ihr Fahrer und Ihr Fahrzeug bleiben zwischen den Stopps verfügbar — von 2 bis 24 Stunden.",
      usps: ["Von 2 bis 24 Stunden", "Unbegrenzte Stopps", "Derselbe Fahrer den ganzen Tag"],
      included: [
        ["Null Wartezeit zwischen Terminen", "Ihr Fahrer wartet vor Ort, Wagen bereit, Klima eingestellt."],
        ["Freie Route", "Pläne ändern sich? Passen Sie unterwegs an, ohne neue Buchung."],
        ["Garantierte Diskretion", "Ihre Termine und Gespräche bleiben vertraulich — unterschriebene Verpflichtung."],
        ["Klare Stundenabrechnung", "Ein fester Stundensatz pro Kategorie, bekannt vor der Buchung."],
      ],
      steps: [
        ["Dauer festlegen", "Startort, Datum, Startzeit und Stundenzahl."],
        ["Ihr Fahrer erscheint", "Pünktlich, über Ihr erstes Ziel informiert."],
        ["Stopps aneinanderreihen", "Jede Etappe wird mündlich oder über den Tracking-Link geregelt."],
        ["Enden, wann Sie wollen", "Die Fahrt schliesst am letzten Stopp, detaillierte Quittung auf Ihrem Tracking-Link."],
      ],
      price_v: "ab CHF 85.–/Std.", price_note: "Stundensatz pro Kategorie: Business 85, VAN 95, First 100 CHF/Std.",
      final_cta: "Stundenweise buchen",
    },
    long: {
      name: "Langstrecke",
      h1a: "Die Nachbarstadt,", h1b: "ohne Umstände.",
      sub: "Genf–Zürich, Lausanne–Mailand, Montreux–Lyon: von Tür zu Tür, zu Ihrer Zeit, mit Ihrem Gepäck und Ihrer Ruhe. Der Zug ohne Perron, das Flugzeug ohne Flughafen.",
      usps: ["Von Tür zu Tür", "Abfahrt zu Ihrer Zeit", "Festpreis bei jedem Verkehr"],
      included: [
        ["Abfahrt, wann Sie wollen", "5 Uhr morgens oder 23 Uhr: Sie bestimmen die Zeit, nicht ein Fahrplan."],
        ["Arbeiten oder ruhen an Bord", "WLAN, Ladegeräte, Stille oder Gespräch: Die Fahrt wird zur nutzbaren Zeit."],
        ["Stopps auf Wunsch", "Kaffee, Foto, Unvorhergesehenes: Ihr Fahrer hält, wo Sie möchten."],
        ["Optimierte Rückfahrt", "Die Rückfahrt am selben Tag profitiert von einem angepassten Tarif."],
      ],
      steps: [
        ["Beide Städte angeben", "Genaue Adressen, Datum, Uhrzeit — der Festpreis erscheint sofort."],
        ["Fahrzeug wählen", "Business fürs Meeting, VAN für Familie und Gepäck."],
        ["Reisen", "Ihr Fahrer kümmert sich um Strasse, Verkehr und Maut. Sie kommen einfach an."],
        ["Quittung abrufen", "Klar, detailliert, bei Ankunft verfügbar — bereit für die Buchhaltung."],
      ],
      price_v: "ab CHF 120.–", price_note: "Festpreis auf die reale Distanz berechnet, vor jeder Zahlung angezeigt.",
      final_cta: "Meine Langstrecke schätzen",
    },
    hotels: {
      name: "Hotels & Concierge",
      h1a: "Der Chauffeurservice", h1b: "Ihres Hauses.",
      sub: "Concierge, Rezeption, Guest Relations: Buchen Sie einen Fahrer für Ihre Gäste in unter einer Minute, verfolgen Sie die Fahrt live und erhalten Sie eine Sammelrechnung.",
      usps: ["Buchung in unter einer Minute", "Tracking mit dem Gast geteilt", "Konsolidierte Monatsrechnung"],
      included: [
        ["Ihre Gäste installieren nichts", "Sie erhalten Fahrer, Fahrzeug und Tracking-Link per E-Mail — ohne Konto, ohne App."],
        ["Im Bild Ihres Hauses", "Gepflegt auftretende Fahrer, makellose Fahrzeuge, ein Empfang auf Ihrem Niveau."],
        ["Ein fester Ansprechpartner", "Ein MILES-Kontakt für Ihr Team, der Ihre Fahrten kennt."],
        ["Null Verwaltung", "Die Fahrten des Monats auf einer einzigen Rechnung, Buchhaltungsexport inklusive."],
      ],
      steps: [
        ["Express-Onboarding", "Wir richten Ihren Bereich ein und schulen Ihr Team in 30 Minuten."],
        ["Für einen Gast buchen", "Name, Telefon, Route: erledigt — selbst um 4 Uhr morgens."],
        ["Der Gast ist informiert", "Vorname, Foto, Kennzeichen und Live-Tracking, in seiner Sprache."],
        ["Alles ist dokumentiert", "Volle Historie, Quittungen und Monatsrechnung zentralisiert."],
      ],
      price_v: "Auf Anfrage", price_note: "Partnerkonditionen je nach Volumen — Antwort innert 24 Geschäftsstunden.",
      final_cta: "Mit dem Business-Team sprechen",
    },
    events: {
      name: "Events & Gruppen",
      h1a: "Ihre Gäste,", h1b: "alle pünktlich.",
      sub: "Hochzeiten, Seminare, Launches, Delegations-Transfers: Wir koordinieren Fahrzeuge, Fahrer und Zeiten, während Sie das Event führen.",
      usps: ["Koordinierte Flotte", "Fester Koordinator", "Geplante Shuttles und Rotationen"],
      included: [
        ["Ein kompletter Transportplan", "Abholpunkte, Rotationen, Fahrzeugstaffeln: alles im Voraus orchestriert."],
        ["VANs und Limousinen kombiniert", "Gruppen in der V-Klasse, Führungskräfte in der First Class — jeder auf seinem Niveau."],
        ["Ein Koordinator vor Ort", "Am Tag X steuert ein MILES-Verantwortlicher die Fahrer und fängt Überraschungen ab."],
        ["Zentrales Tracking", "Jede Fahrt in Echtzeit sichtbar, für Sie und für Ihre Gäste."],
      ],
      steps: [
        ["Erzählen Sie uns vom Event", "Datum, Orte, Gästezahl, Rahmenbedingungen: Wir antworten innert 24 Stunden."],
        ["Den Plan erhalten", "Fahrzeuge, Fahrer, Zeiten und Budget — ein klares Dokument, ein einziger Preis."],
        ["Am Tag X", "Die Fahrzeuge rotieren, der Koordinator steuert, Sie atmen durch."],
        ["Eine einzige Rechnung", "Alle Fahrten konsolidiert, bereit für Ihre Buchhaltung."],
      ],
      price_v: "Auf Anfrage", price_note: "Jedes Event wird individuell offeriert — Antwort innert 24 Geschäftsstunden.",
      final_cta: "Offerte anfordern",
    },
  },
  bz: {
    auth: {
      panel_eyebrow: "MILES Business",
      panel_h1a: "Alle Fahrten Ihrer Organisation,", panel_h1b: "an einem Ort.",
      panel_points: ["Buchung für Ihre Gäste in wenigen Augenblicken", "Konsolidierte Monatsrechnung, PDF für die Buchhaltung", "Rollen Admin, Booker und Finance"],
      panel_footer: "MILES",
      back: "Zurück",
      login_t: "Business-Anmeldung", login_s: "Greifen Sie auf den Bereich Ihrer Organisation zu.",
      signup_t: "Ihren Business-Bereich erstellen", signup_s: "Ihre Organisation, Ihre Fahrten, Ihre Rechnungen — in wenigen Minuten.",
      org: "Name der Organisation", name: "Ihr vollständiger Name", email: "Geschäftliche E-Mail", password: "Passwort",
      password_hint: "Mindestens 8 Zeichen",
      submit_login: "Anmelden", submit_signup: "Bereich erstellen",
      to_signup: "Noch kein Bereich? Konto erstellen", to_login: "Schon ein Konto? Anmelden",
      err_generic: "Ein Fehler ist aufgetreten. Bitte erneut versuchen.",
    },
    nav: { overview: "Übersicht", new_ride: "Neue Fahrt", rides: "Fahrten", passengers: "Passagiere", invoices: "Rechnungen", team: "Team", settings: "Einstellungen" },
    roles: { admin: "Admin", booker: "Booker", finance: "Finance" },
    shell: { hello: "Hallo, {name}", logout: "Abmelden", back_site: "Zurück zur Website", demo_badge: "Demo", new_ride_cta: "Neue Fahrt" },
    ov: {
      today: "Heute", today_sub: "Fahrten heute", live: "Live", live_sub: "Live-Fahrten",
      upcoming: "Bevorstehend", upcoming_sub: "Buchungen", spend: "Ausgaben im Monat", spend_sub: "alle Fahrten",
      recent: "Letzte Buchungen", view_all: "Alle ansehen", series_t: "Ausgaben — letzte 6 Monate",
      empty: "Noch keine Fahrten.", rides_word: "Fahrten", next_t: "Nächste Fahrt",
    },
    nr: {
      t: "Neue Fahrt", s: "Buchen Sie für einen Passagier — er verfolgt die Fahrt über einen Link, ohne Konto.",
      passenger: "Passagier", saved: "Gespeichert", new: "Neu", choose: "Passagier wählen…",
      from: "Abholung", to: "Ziel", date: "Datum", time: "Uhrzeit", duration: "Dauer",
      type_one: "Einfache Fahrt", type_hourly: "Stundenweise",
      vehicle: "Fahrzeug", cc: "Kostenstelle", cc_none: "Keine",
      flight: "Flugnr. (optional)", notes: "Anweisungen (optional)",
      fname: "Vorname", lname: "Nachname", phone: "Telefon", email: "E-Mail (optional)",
      estimating: "Preis wird berechnet…", estimate_hint: "Geben Sie die Route ein, um Fahrzeuge und Festpreis zu sehen.",
      submit: "Fahrt bestätigen", done_t: "Fahrt erstellt.", done_s: "Teilen Sie diesen Tracking-Link mit dem Passagier:",
      copy: "Link kopieren", copied: "Tracking-Link kopiert", again: "Neue Fahrt", view_rides: "Fahrten ansehen",
      map_caption: "{km} km · ~{min} Min.", price_t: "Festpreis",
      err: "Fahrt konnte nicht erstellt werden. Prüfen Sie die Adressen.",
    },
    rd: {
      t: "Fahrten", filters: { all: "Alle", live: "Live", upcoming: "Bevorstehend", done: "Abgeschlossen", cancelled: "Storniert" },
      cancelled_badge: "Storniert", cancel: "Stornieren", cancel_yes: "Bestätigen", cancel_done_t: "Fahrt storniert",
      share: "Tracking-Link kopieren", copied_t: "Link kopiert", track: "Tracking öffnen",
      search_ph: "Suchen (Passagier, Code, Route)…", route_t: "Route", contact_t: "Passagier",
      flight_t: "Flug", notes_t: "Anweisungen", cc_t: "Kostenstelle", by_t: "Gebucht von", created_t: "Erstellt am",
      timeline_t: "Verlauf", open_track: "Tracking öffnen", rebook: "Erneut buchen",
      empty: "Keine Fahrten in diesem Filter.", err: "Aktion momentan nicht möglich.",
    },
    ps: {
      t: "Passagiere", s: "Ihr Passagier-Verzeichnis — buchen Sie per Klick für sie.",
      add: "Passagier hinzufügen", trips: "{n} Fahrten", book: "Buchen", del: "Löschen",
      empty: "Keine gespeicherten Passagiere. Fügen Sie den ersten hinzu.", save: "Speichern",
      saved_t: "Passagier hinzugefügt", updated_t: "Passagier aktualisiert", search_ph: "Passagier suchen…",
      edit: "Bearbeiten", edit_t: "Passagier bearbeiten", err: "Passagier konnte nicht gespeichert werden.",
    },
    inv: {
      t: "Rechnungen", s: "Automatische monatliche Konsolidierung Ihrer Fahrten.",
      trips: "{n} Fahrten", en_cours: "Laufend", emise: "Ausgestellt", payee: "Bezahlt",
      mark_paid: "Als bezahlt markieren", mark_issued: "Zurück auf ausgestellt",
      empty: "Noch keine Rechnungen.", lines_t: "Fahrten des Monats", export_csv: "Zeilen als CSV exportieren",
      err: "Aktion momentan nicht möglich.",
    },
    tm: {
      t: "Team", s: "Die Mitglieder Ihrer Organisation und ihre Rollen.",
      add: "Mitglied hinzufügen", role: "Rolle", del: "Entfernen", you: "Sie",
      save: "Hinzufügen", added_t: "Mitglied hinzugefügt", err: "Mitglied konnte nicht hinzugefügt werden.",
    },
    st: {
      t: "Einstellungen", org_t: "Organisation", org_name: "Name der Organisation", billing: "Rechnungs-E-Mail",
      cc_t: "Kostenstellen", cc_ph: "Z. B. Direktion", cc_empty: "Keine Kostenstellen — fügen Sie welche hinzu, um Ausgaben pro Team zu verfolgen.",
      toggles_t: "Präferenzen",
      toggles: {
        monthly_billing: ["Konsolidierte Monatsabrechnung", "Eine einzige Rechnung pro Monat"],
        whatsapp: ["WhatsApp-Benachrichtigungen an Passagiere", "Zusätzlich zu SMS und E-Mails"],
        flight_tracking: ["Automatische Flugverfolgung", "Anpassung bei Verspätung"],
        require_cost_center: ["Kostenstelle obligatorisch", "Interne Referenz bei jeder Buchung"],
      },
      save: "Speichern", saved_t: "Einstellungen gespeichert", err: "Speichern nicht möglich.",
    },
    common: { cancel: "Abbrechen" },
  },
  tr: {
    eyebrow: "Tracking — {code}",
    booked_by: "Gebucht für Sie von",
    eta_live: "Ankunft voraussichtlich in ~{min} Min.",
    pos_stale: "Zuletzt gesehen vor {min} Min.",
    copy: {
      booked: ["Wir bereiten Ihre Fahrt vor.", "Ihr Fahrer wird bestätigt — Sie erhalten eine E-Mail, sobald er feststeht.", "Abfahrt wie geplant"],
      driver_assigned: ["Ihr Fahrer ist bestätigt.", "Vorname, Fahrzeug und Kennzeichen stehen unten. Sie erhalten eine E-Mail, sobald er zu Ihnen unterwegs ist.", "Abfahrt wie geplant"],
      driver_on_the_way: ["Ihr Fahrer ist unterwegs.", "Verfolgen Sie seine Anfahrt live auf der Karte.", "Unterwegs"],
      driver_arrived: ["Ihr Fahrer wartet auf Sie.", "Er ist am vereinbarten Treffpunkt.", "Er ist da"],
      on_board: ["Sie sind an Bord.", "Route verkehrsoptimiert. Gute Fahrt.", "Ankunftszeit wird laufend neu berechnet"],
      completed: ["Sie sind angekommen.", "Danke, dass Sie mit MILES gefahren sind. Ihre Quittung finden Sie unten.", "Abgeschlossen"],
      cancelled: ["Diese Buchung wurde storniert.", "Es wird kein Fahrer entsandt. Bei Fragen: info@gomiles.ch.", "Storniert"],
    },
    pickup_label: "Abholung", arrival_label: "Voraussichtliche Ankunft", completed_label: "Fahrt abgeschlossen",
    remaining: "noch 19 Min.",
    your_driver: "Ihr Fahrer", driver_alt: "{name}, MILES-Fahrer",
    call_driver: "Fahrer anrufen", message_driver: "Fahrer kontaktieren",
    pending: "Wir bestätigen Ihren Fahrer — Vorname, Foto, Fahrzeug und Kennzeichen erscheinen hier automatisch.",
    meeting: "Treffpunkt: {pickup} — vor dem Haupteingang{terminal}.",
    terminal_join: ", ",
    timeline_t: "Fahrtstatus", notif_t: "Gesendete Mitteilungen",
    flight_t: "Flug verfolgt",
    flight_note: "Bei Verspätung passen wir die Abholung an die tatsächliche Landezeit an.",
    support_t: "Eine Frage?", support_b: "Ein echter Mensch antwortet",
    share: "Tracking teilen", share_t: "Tracking-Link kopiert", share_d: "Teilen Sie ihn mit allen, die diese Fahrt verfolgen sollen.",
    receipt: "Quittung — {code}", paid_badge: "Bezahlt",
    waiting_line: "Wartezeit & Abholung", included: "inklusive", total: "Total inkl. MwSt.",
    download: "Quittung herunterladen", pdf_t: "PDF-Quittung", pdf_d: "Download bald verfügbar.",
    rebook: "Erneut buchen",
    hourly_dest: "Stundenweise",
    stops_label: "Zwischenstopps",
    nf_eyebrow: "Tracking-Link", nf_t: "Buchung nicht gefunden",
    nf_b: "Prüfen Sie den per E-Mail erhaltenen Link oder kontaktieren Sie den Support.",
    nf_cta: "Support kontaktieren",
    notifs: {
      booked: ["E-Mail", "Gebucht. Ihre Fahrt ist bestätigt."],
      driver_assigned: ["E-Mail", "Ihr Fahrer ist bestätigt — Details unten."],
      driver_on_the_way: ["E-Mail", "Ihr Fahrer ist zu Ihnen unterwegs."],
      driver_arrived: ["E-Mail", "Ihr Fahrer wartet am Treffpunkt auf Sie."],
      on_board: ["Tracking", "Gute Fahrt — die Ankunftszeit wird live aktualisiert."],
      completed: ["Tracking", "Ihre Quittung finden Sie unten."],
    },
  },
  te: {
    eyebrow: "MILES-Tracking", h1: "Meine Fahrt verfolgen.",
    p: "Geben Sie Ihren Buchungscode ein — er steht in Ihrer Bestätigung, im Format ML-XXXXX. Kein Konto nötig.",
    ph: "ML-XXXXX", submit: "Verfolgen",
  },
  ap: {
    eyebrow: "Flughafentransfer", img_alt: "Flug über den Wolken", h1a: "Der Flughafen, geregelt", h1b: "für Sie.",
    sub: "Empfang am Terminal, Wartezeit inklusive, klarer Treffpunkt. Und wenn Sie uns bei der Buchung Ihre Flugnummer anvertrauen, folgt die Abholung der tatsächlichen Landezeit.",
    trust: ["45 Min. Wartezeit inklusive", "Empfang mit Namensschild am Terminal", "Flugnummer optional"],
    note_t: "Ihre Flugnummer?",
    flight_note: "Sie ist optional. Geben Sie sie bei der Buchung an: Wir verfolgen die Landung und passen die Abholzeit bei Verspätung an — ohne dass Sie etwas tun müssen.",
    h2: "Unsere Flughäfen, perfekt beherrscht.",
    airports: [
      ["GVA", "Flughafen Genf", "Empfang am Terminal 1, Ankunftsbereich. 45 Min. Wartezeit inklusive.", "15 Min. vom Zentrum"],
      ["ZRH", "Flughafen Zürich", "Treffpunkt Arrival 2. 45 Min. Wartezeit inklusive.", "12 Min. vom Zentrum"],
      ["BSL", "EuroAirport Basel", "Ausgang Schweizer Seite, Namensschild. Wartezeit inklusive.", "15 Min. vom Zentrum"],
    ],
    flow_eyebrow: "Der Ablauf", flow_h2: "Vom Flugsteig bis zur Wagentür.", flow_img_alt: "MILES-Empfang am Flughafen",
    flow: [
      ["Sie buchen", "Adresse, Datum, Uhrzeit — und Ihre Flugnummer, wenn Sie möchten. Optional, aber praktisch."],
      ["Ihr Fahrer wartet", "Vorname, Foto, Kennzeichen und genauer Treffpunkt, per E-Mail gesendet."],
      ["Sie steigen ein", "Gepäck verstaut, Route bereit, frisches Wasser an Bord."],
      ["Sie kommen an", "Ihre Quittung wartet auf Ihrem Tracking-Link."],
    ],
    band: "Bald ein Flug? Wir kümmern uns um den Rest.", band_cta: "Transfer buchen",
  },
  biz: {
    eyebrow: "MILES Business", h1a: "Alle Ihre Fahrten.", h1b: "Eine einzige Organisation.",
    sub: "Buchen Sie für Mitarbeitende, Führungskräfte oder Gäste. Verfolgen Sie jede Fahrt parallel. Eine Rechnung, einmal im Monat.",
    cta1: "Ihren Bereich erstellen", cta2: "Anmelden",
    shot_alt: "Das MILES Business Dashboard im realen Einsatz",
    feat_eyebrow: "Der Bereich, in echt",
    f1: {
      tag: "Buchen", t: "Eine Fahrt für Dritte, in wenigen Augenblicken.",
      d: "Gespeicherter Passagier oder spontan erfasster Gast: Sie sehen Festpreis und Routenvorschau vor der Bestätigung. Der Passagier erhält einen Tracking-Link — ohne Konto, ohne App.",
      points: ["Festpreis live auf der realen Route berechnet", "Routenkarte vor der Bestätigung", "Tracking-Link zum Teilen mit dem Passagier"],
      alt: "Neue Fahrt mit Routenkarte und Fahrzeugwahl",
    },
    f2: {
      tag: "Steuern", t: "Alle Ihre Fahrten auf einen Blick.",
      d: "Live-Status, Sofortsuche, Stornierung und Neubuchung per Klick. Jede Fahrt öffnet sich im Detail: Passagier, Verlauf, Tracking-Link.",
      points: ["Live-Status, automatisch aktualisiert", "Suche nach Passagier, Code oder Route", "Vergangene Fahrt per Klick erneut buchen"],
      alt: "Fahrtdetail mit Verlauf und Aktionen",
    },
    f3: {
      tag: "Abrechnen", t: "Die Abrechnung läuft von allein.",
      d: "Fahrten konsolidieren sich automatisch pro Monat. PDF mit MwSt. für die Buchhaltung, CSV-Export der Zeilen und Zahlungsverfolgung.",
      points: ["Automatische konsolidierte Monatsrechnung", "PDF mit 8,1 % MwSt. + CSV-Export", "Status ausgestellt / bezahlt"],
      alt: "Monatsrechnungen mit detaillierten Fahrtzeilen",
    },
    roles_eyebrow: "Die Rollen", roles_h2a: "Jeder hat", roles_h2b: "seinen Bildschirm.",
    roles: [
      ["Admin", "Volle Sicht: Buchung, Team, Einstellungen, Abrechnung. Sie halten die Schlüssel."],
      ["Booker", "Rezeption, Concierge, Assistenz: Fahrten buchen und verfolgen. Nichts weiter zu verwalten."],
      ["Finance", "Übersicht und Rechnungen. Die Buchhaltung holt ihre PDFs, Punkt."],
    ],
    flow_eyebrow: "Der Ablauf", flow_h2: "Für jemanden buchen, in vier Schritten.",
    flow: [
      ["Passagier wählen", "Ein gespeicherter Mitarbeiter oder ein Gast, in 10 Sekunden erfasst."],
      ["Fahrt festlegen", "Gespeicherte Adressen, Flug verfolgt, passendes Fahrzeug."],
      ["Senden", "Der Fahrgast erhält alles per E-Mail — ohne Konto."],
      ["Parallel verfolgen", "Sie sehen den Fortschritt der Fahrt in Ihrem Dashboard."],
    ],
    flow_cta: "Bereich erstellen",
    proof: [
      ["2 Min.", "um für Dritte zu buchen, Passagier ohne Konto inklusive"],
      ["1 Rechnung", "konsolidiert pro Monat, bereit für die Buchhaltung"],
      ["0 Spesen", "Taxibelege verstreut in Posteingängen"],
    ],
    proof_note: "Verhandelte Tarife möglich · Wiederkehrende Buchungen · Mehrere Nutzer",
    final_eyebrow: "Bereit in 2 Minuten",
    final_h2a: "Testen Sie die Demo.", final_h2b: "Dann erstellen Sie Ihre.",
    final_p: "Der Bereich Maison Démo SA ist mit Beispieldaten vorbefüllt. Ihrer startet leer — und gehört Ihnen.",
    final_cta1: "Mit unserem Team sprechen", final_cta2: "Meinen Bereich erstellen",
  },
  dash: {
    hello: "Guten Tag, Maison Démo SA", demo: "Demo-Modus — Beispieldaten", quit: "Demo verlassen",
    new: "Neue Buchung",
    nav: ["Übersicht", "Buchungen", "Fahrten", "Passagiere", "Rechnungen", "Team", "Einstellungen"],
    plan: "Business-Plan · 4 Nutzer",
    stats: [["Heute", "Fahrten"], ["In Arbeit", "Live-Kurse"], ["Bevorstehend", "Buchungen"], ["Monatsausgaben", "alle Teams"]],
    recent: "Letzte Buchungen", view_all: "Alle ansehen",
    activity: "Letzte Aktivität",
    activity_items: [
      ["Fahrer bestätigt", "Yanis B. — Zürich → ZRH", "vor 12 Min."],
      ["Fahrt abgeschlossen", "Sofia M. — Montreux → GVA", "vor 48 Min."],
      ["Rechnung erstellt", "INV-2026-047 — Juli", "vor 2 Std."],
      ["Neuer Passagier", "Antoine D. von der Rezeption hinzugefügt", "gestern"],
    ],
    quick: "Für einen Passagier buchen",
    all_b: "Alle Buchungen", all_b_sub: "{n} Fahrten — klicken zum Verfolgen",
    chart_t: "Fahrten pro Tag — letzte 7 Tage", live_t: "Laufende & bevorstehende Kurse",
    hourly_short: "Stundenw.",
    invoices_t: "Rechnungen", invoices_s: "Konsolidierte Monatsabrechnung",
    inv_trips: "{n} Fahrten", inv_paid: "Bezahlt", inv_pending: "Ausstehend",
    pdf_t: "PDF-Export", pdf_d: "{n} — Download bald verfügbar.",
    team_t: "Nutzer & Rollen",
    team: [
      ["Nathalie Perrot", "Admin — Geschäftsleitung", "Alle Rechte"],
      ["Hotelrezeption", "Concierge", "Buchung für Kunden"],
      ["Marc Olivier", "Office Manager", "Buchung + Rechnungen"],
      ["Laura Chappuis", "Executive Assistant", "Nur Buchung"],
    ],
    settings_t: "Organisationseinstellungen",
    settings: [
      ["Konsolidierte Monatsabrechnung", "Eine Rechnung pro Monat"],
      ["WhatsApp-Benachrichtigungen an Passagiere", "Zusätzlich zu SMS und E-Mails"],
      ["Automatisches Flug-Tracking", "Anpassung bei Verspätung"],
      ["Pflicht-Kostenstellen", "Interne Referenz bei jeder Buchung"],
    ],
    book_for: "Buchen",
  },
  dr: {
    eyebrow: "Die MILES-Fahrer", h1a: "Profis, die wir", h1b: "beim Vornamen kennen.",
    sub: "Keine weissen Handschuhe, keine Zeremonie. Pünktliche, diskrete, präzise Menschen, die jede Fahrt als gut gemachte Arbeit betrachten.",
    trust: ["100 % geprüft, geschult und versichert", "Nach jeder Fahrt bewertet", "Vorname, Foto und Kennzeichen vor der Abfahrt mitgeteilt"],
    moments_eyebrow: "Das MILES-Erlebnis", moments_h2a: "Service,", moments_h2b: "ohne Zeremonie.",
    moments: [
      ["Der Empfang", "Tür geöffnet, Gepäck verstaut, Route bestätigt. Sie müssen nichts erklären."],
      ["Die Fahrt", "Ihr Tempo, Ihre Atmosphäre: Gespräch oder Stille, Musik oder Ruhe. Der Fahrer passt sich an — nicht umgekehrt."],
      ["Die Ankunft", "Ausstieg so nah wie möglich am Eingang, Hilfe mit dem Gepäck, Quittung bereits gesendet."],
    ],
    steps_eyebrow: "Die Bestätigung", steps_h2a: "Ihr Fahrer,", steps_h2b: "vor der Abfahrt bekannt.",
    steps_sub: "Kein Unbekannter am Strassenrand: Sie wissen, wer kommt, in welchem Wagen und wann er ankommt.",
    steps: [
      ["Buchung bestätigt", "Festpreis fixiert, Route gesichert. Ihre Fahrt geht sofort ins MILES-System."],
      ["Bestätigung", "Bei Sofortbuchung wird Ihr Fahrer innert Minuten bestätigt. Bei geplanter Buchung spätestens 1 Std. vor der Abfahrt."],
      ["Vorstellung", "Sie erhalten Vorname, Foto, Fahrzeug, Farbe und Kennzeichen — per E-Mail und auf Ihrem Tracking-Link."],
      ["Direkter Kontakt", "Unsicher beim Treffpunkt? Rufen Sie Ihren Fahrer an oder schreiben Sie ihm im Tracking — ohne Nummernaustausch."],
    ],
    h2a: "Was wir verlangen.", h2b: "Bei jeder Fahrt.",
    commitments: [
      ["Einzeln ausgewählt", "Gespräch, Hintergrundprüfung, Fahrprobe: Die meisten Bewerbungen scheiden unterwegs aus."],
      ["Im MILES-Service geschult", "Pünktlichkeit, Auftreten, Diskretion, Kenntnis der Flughäfen und Hotels. Die Technik überlassen wir der App."],
      ["Diskret aus Prinzip", "Gespräche, Routen, Fahrgäste: Was an Bord passiert, bleibt an Bord. Jeder Fahrer unterschreibt eine Vertraulichkeitserklärung."],
      ["Lokal vor allem", "Terminals von GVA und ZRH, Grand-Hotels, Skigebiete, Bergstrassen: Ihr Fahrer kennt das Gelände, nicht nur das GPS."],
      ["Bei jeder Fahrt bewertet", "Systematisches Feedback nach jeder Fahrt. Ein Fahrer mit sinkender Qualität wird begleitet — und nötigenfalls ersetzt."],
      ["Fair bezahlt", "Gut bezahlte Fahrer kommen pünktlich, lächelnd — und bleiben. So einfach ist das."],
    ],
    band_eyebrow: "MILES beitreten",
    band_t: "Sind Sie Berufsfahrer?",
    band_s: "Treten Sie einem Netzwerk bei, das Sie respektiert — und fair bezahlt.",
    band_cta: "MILES-Fahrer werden",
    band_alt: "Die Flotte entdecken",
  },
  ct: {
    eyebrow: "Städte & Abdeckung", h1a: "Zuerst die Schweiz.", h1b: "Dann Europa.",
    sub: "MILES ist in den grossen Schweizer Städten und ihren Flughäfen unterwegs, auf Langstrecken-Korridoren und in die Alpenorte — mit demselben Festpreis, angezeigt vor jeder Zahlung.",
    trust: ["Garantierter Festpreis", "Geprüfte lokale Fahrer", "Sofortige Schätzung, ohne Konto"],
    cities_eyebrow: "Die Städte", cities_h2a: "Fünf Standorte,", cities_h2b: "die ganze Schweiz.",
    airport_from: "Transfer {code} ab {price}",
    book: "Ab {city} buchen",
    cities: [
      ["Genf", "Privattransport Genf", ["Zentrum → Flughafen GVA — 15 Min.", "Genf → Lausanne — 50 Min.", "Genf → Verbier — 2 Std. 05"], "GVA", "CHF 90.–"],
      ["Lausanne", "Privatfahrer Lausanne", ["Zentrum → Flughafen GVA — 60 Min.", "Lausanne → Zürich — 2 Std. 40", "Ouchy → Montreux — 30 Min."], "GVA", "CHF 205.–"],
      ["Zürich", "Flughafenfahrer Zürich", ["Zentrum → Flughafen ZRH — 15 Min.", "Zürich → Basel — 1 Std. 10", "Zürich → Luzern — 45 Min."], "ZRH", "CHF 90.–"],
      ["Basel", "Privattransport Basel", ["Zentrum → EuroAirport BSL — 15 Min.", "Basel → Zürich — 1 Std. 10", "Basel → Bern — 1 Std. 15"], "BSL", "CHF 90.–"],
      ["Bern", "Privatfahrer Bern", ["Bern → Flughafen ZRH — 2 Std.", "Bern → Flughafen GVA — 2 Std. 05", "Bern → Interlaken — 50 Min."], "ZRH", "CHF 430.–"],
    ],
    ld_eyebrow: "Langstrecken-Korridore", ld_h2a: "Stadt zu Stadt,", ld_h2b: "zum Festpreis.",
    ld_note: "Geschätzte Business-Class-Preise auf realer Strassendistanz. Der genaue Preis erscheint in der Buchung, vor jeder Zahlung.",
    corridors: [
      ["Genf", "Zürich", "279 km", "≈ 3 Std. 20", "ab CHF 905.–"],
      ["Genf", "Mailand", "320 km", "≈ 3 Std. 55", "ab CHF 1'040.–"],
      ["Lausanne", "Lyon", "213 km", "≈ 2 Std. 30", "ab CHF 695.–"],
      ["Genf", "Lyon", "150 km", "≈ 1 Std. 55", "ab CHF 490.–"],
      ["Zürich", "St. Moritz", "203 km", "≈ 2 Std. 40", "ab CHF 665.–"],
    ],
    corridor_cta: "Berechnen",
    ski_eyebrow: "Die Alpen", ski_h2a: "Direkt in", ski_h2b: "die Skigebiete.",
    ski_p: "Ski, sperriges Gepäck, Bergstrassen: Der VAN ist dafür gebaut. Abfahrten ab Genf, Business-Class-Tarife.",
    ski_img_alt: "Der MILES VAN, gebaut für die Skigebiete", ski_caption: "Ski & Gepäck — der VAN",
    ski: [
      ["Verbier", "161 km — ab CHF 525.–"],
      ["Zermatt (via Täsch)", "230 km — ab CHF 750.–"],
      ["Gstaad", "149 km — ab CHF 485.–"],
      ["Crans-Montana", "185 km — ab CHF 605.–"],
    ],
    ski_cta: "Buchen",
    city_pages: {
      back: "Alle Städte", airport_t: "Flughafentransfer", routes_t: "Beliebte Fahrten",
      routes_s: "Business-Class-Preise, geschätzt auf der realen Strassendistanz, gemessen via OSRM.",
      places_t: "Quartiere & Orte", estimate: "Berechnen", from_word: "ab", others_t: "Ebenfalls bedient",
      geneve: {
        tag: "Genf · Flughafen GVA",
        h1: "Privattransport in Genf",
        sub: "Geprüfter MILES-Fahrer in Genf und ab GVA: schnelle Abholung im Zentrum, Festpreis vor dem Einstieg bekannt, Empfang mit Namensschild am Terminal.",
        airport_note: "Empfang mit Namensschild am Terminal 1, Ankunftsbereich. 45 Min. Wartezeit inklusive.",
        route_labels: [["Zentrum → Flughafen GVA", "≈ 15 Min."], ["Genf → Lausanne", "≈ 50 Min."], ["Genf → Montreux", "≈ 1 Std. 10"], ["Genf → Verbier", "≈ 2 Std. 05"]],
        places: ["Altstadt", "Rive Gauche", "Cologny", "Carouge", "Palexpo", "UNO — internationales Quartier", "Hotels am See"],
      },
      lausanne: {
        tag: "Lausanne · via GVA",
        h1: "Privatfahrer in Lausanne",
        sub: "Transfers Lausanne ↔ Flughafen Genf den ganzen Tag, Fahrten von Stadt zu Stadt und stundenweise Buchung. Festpreis, Live-Tracking, sofortige Quittung.",
        airport_note: "GVA-Abgabe und -Abholung mit Wartezeit inklusive — 63 km entlang der Genfersee-Riviera, ganz ohne Perron.",
        route_labels: [["Zentrum → Flughafen GVA", "≈ 60 Min."], ["Lausanne → Zürich", "≈ 2 Std. 40"], ["Lausanne → Montreux", "≈ 30 Min."]],
        places: ["Ouchy", "SBB-Bahnhof", "Le Flon", "EPFL & UNIL", "Lavaux", "Beau-Rivage Palace"],
      },
      zurich: {
        tag: "Zürich · Flughafen ZRH",
        h1: "Privatfahrer in Zürich",
        sub: "Von der Bahnhofstrasse an den ZRH in einer Viertelstunde: Empfang bei Arrival 2, Wartezeit inklusive, Festpreis vor jeder Zahlung.",
        airport_note: "Treffpunkt Arrival 2. 45 Min. Wartezeit inklusive.",
        route_labels: [["Zentrum → Flughafen ZRH", "≈ 15 Min."], ["Zürich → Basel", "≈ 1 Std. 10"], ["Zürich → Luzern", "≈ 45 Min."], ["Zürich → St. Moritz", "≈ 2 Std. 40"]],
        places: ["Bahnhofstrasse", "Zürich West", "Enge & Seefeld", "Kloten", "Zug", "The Circle"],
      },
      bale: {
        tag: "Basel · EuroAirport BSL",
        h1: "Privattransport in Basel",
        sub: "EuroAirport auf Schweizer Seite, schneller Ausgang, ein Fahrer, der die drei Grenzen kennt. Basel, Zürich und Bern zum Festpreis.",
        airport_note: "Ausgang Schweizer Seite, Namensschild. Wartezeit inklusive.",
        route_labels: [["Zentrum → EuroAirport BSL", "≈ 15 Min."], ["Basel → Zürich", "≈ 1 Std. 10"], ["Basel → Bern", "≈ 1 Std. 15"]],
        places: ["Altstadt", "Messe Basel", "Saint-Louis (FR)", "Mülhausen (FR)", "Novartis- & Roche-Campus"],
      },
      berne: {
        tag: "Bern · via ZRH & GVA",
        h1: "Privatfahrer in Bern",
        sub: "Die Hauptstadt ohne grossen Flughafen? MILES verbindet Bern auf die Minute mit ZRH und GVA — und fährt weiter ins Oberland, ohne Zugwechsel.",
        airport_note: "Abfahrten synchron mit Ihren ZRH- oder GVA-Flügen, Wartezeit inklusive.",
        route_labels: [["Bern → Flughafen ZRH", "≈ 2 Std."], ["Bern → Flughafen GVA", "≈ 2 Std. 05"], ["Bern → Interlaken", "≈ 50 Min."]],
        places: ["Altstadt (UNESCO)", "Bundeshaus", "Kirchenfeld", "Wabern", "Belp", "Gurten"],
      },
    },
    final_eyebrow: "Ihre Fahrt",
    final_h2a: "Der genaue Preis,", final_h2b: "in 30 Sekunden.",
    final_p: "Geben Sie Ihre zwei Adressen ein: Die echte Schätzung — Distanz, Dauer, Preis pro Kategorie — erscheint sofort, ohne Konto.",
    final_cta: "Meine Fahrt schätzen",
  },
  faq: {
    eyebrow: "FAQ", h1a: "Antworten,", h1b: "ohne Umwege.",
    sub: "Alles, was man uns wirklich fragt. Eine Antwort fehlt? Ein echter Mensch antwortet.",
    band: "Eine konkrete Frage?", band_contact: "Support kontaktieren", band_book: "Buchen",
    items: [
      ["Kann ich ohne Konto buchen?", "Ja. Name, Telefon, E-Mail: Das genügt. Sie erhalten sofort Ihre Bestätigung und Ihren persönlichen Tracking-Link. Ein MILES-Konto existiert, bleibt aber optional."],
      ["Kann ich eine Hin- und Rückfahrt buchen?", "Ja. Wählen Sie im ersten Schritt «Hin und zurück»: Sie geben Datum und Uhrzeit der Rückfahrt an, und wir planen beide Fahrten. Die Rückfahrt profitiert von einem angepassten Tarif."],
      ["Kann ich stundenweise buchen?", "Ja, das ist der Modus «Stundenweise». Sie wählen Abholort, Datum, Uhrzeit und Dauer (mindestens 2 Std.). Fahrer und Fahrzeug bleiben zu Ihrer Verfügung."],
      ["Kann ich für jemand anderen buchen?", "Ja. Geben Sie einfach Name und Telefon des Fahrgasts im Schritt «Angaben» ein. Er erhält seine Informationen und den Tracking-Link — ohne Konto."],
      ["Wie funktioniert das Tracking?", "Jede Buchung erzeugt einen persönlichen, sicheren Tracking-Link. Fahrerposition, Fahrtstatus und voraussichtliche Ankunft werden in Echtzeit aktualisiert. Sie können ihn mit jedem teilen."],
      ["Wann erhalte ich die Angaben zu meinem Fahrer?", "Sobald Ihr Fahrer bestätigt ist — Vorname, Foto, Fahrzeug, Farbe und Kennzeichen. Bei sofortiger Buchung rechnen Sie mit wenigen Minuten. Bei geplanter Buchung spätestens 1 Std. vor Abholung."],
      ["Was passiert bei Flugverspätung?", "Geben Sie bei der Buchung Ihre Flugnummer an: Wir verfolgen den Flug und passen die Abholung an die tatsächliche Landung an. Die Wartezeit am Flughafen ist inklusive."],
      ["Kann ich eine Buchung ändern oder stornieren?", "Ja, kostenlos bis 1 Std. vor Abfahrt bei einer einfachen Fahrt, und bis 24 Std. bei stundenweiser oder Langstrecken-Buchung. Über Ihren Tracking-Link oder den Support."],
      ["Wie funktioniert die Zahlung?", "Der Preis wird vor der Bestätigung festgelegt und angezeigt. Sie zahlen sicher per Bankkarte zum Buchungszeitpunkt. Die Quittung ist direkt nach der Fahrt verfügbar."],
      ["Wie funktioniert MILES Business?", "Ein eigener Bereich für Ihre Organisation: Buchen Sie für Mitarbeiter, Kunden oder Gäste in Sekunden, verfolgen Sie alle Fahrten parallel und erhalten Sie eine konsolidierte Rechnung am Monatsende."],
      ["Wie funktioniert die Unternehmensabrechnung?", "Alle Fahrten des Monats werden auf einer einzigen Rechnung gruppiert, mit Kostenstellen und internen Referenzen auf Wunsch. PDF- und Buchhaltungsexport mit einem Klick."],
    ],
  },
  co: {
    eyebrow: "Support & Kontakt", h1a: "Menschen,", h1b: "kein Chatbot.",
    sub: "Eine laufende Fahrt, eine Rechnung, ein Business-Konto, ein Event zu koordinieren — unser Genfer Team antwortet schnell und gut.",
    rows: [["E-Mail", "info@gomiles.ch"], ["Telefon", "+41 79 195 76 06"]],
    topic_l: "Thema",
    topics: ["Frage zu einer Buchung", "MILES Business", "Fahrer werden", "Event & Gruppen", "Andere Anfrage"],
    name: "Vollständiger Name", email: "E-Mail", company: "Firma — optional",
    message: "Beschreiben Sie Ihr Anliegen in wenigen Worten…",
    submit: "Senden", sending: "Wird gesendet…",
    note: "Menschliche Antwort — nie ein Chatbot",
    ok_t: "Gut angekommen.", ok_b: "Danke, {name} — ein MILES-Berater antwortet tagsüber innert 2 Std.",
    toast_t: "Nachricht gesendet", toast_d: "Ein echter Mensch antwortet tagsüber innert 2 Std.",
    err_t: "Senden fehlgeschlagen", err_d: "Versuchen Sie es erneut oder schreiben Sie an info@gomiles.ch",
  },
};

// Dictionnaires PARTIELS : russe et chinois disponibles uniquement sur la page
// de suivi client (demande explicite) — les clés manquantes retombent sur le FR.
const ru = {
  statuses: {
    booked: "Подтверждена", driver_assigned: "Водитель подтверждён", driver_on_the_way: "В пути",
    driver_arrived: "На месте", on_board: "В машине", completed: "Завершена", cancelled: "Отменена",
  },
  tr: {
    eyebrow: "Отслеживание — {code}",
    booked_by: "Забронировано для вас:",
    pos_stale: "Последняя позиция {min} мин. назад",
    copy: {
      booked: ["Мы готовим вашу поездку.", "Водитель подтверждается — вы получите e-mail, как только всё будет официально.", "Подача в запланированное время"],
      driver_assigned: ["Ваш водитель подтверждён.", "Имя, автомобиль и номер указаны ниже. Мы напишем вам, как только он выедет к вам.", "Подача в запланированное время"],
      driver_on_the_way: ["Ваш водитель в пути.", "Следите за его передвижением на карте в реальном времени.", "В пути"],
      driver_arrived: ["Ваш водитель ждёт вас.", "Он находится в согласованном месте встречи.", "Он на месте"],
      on_board: ["Вы в пути.", "Маршрут оптимизирован с учётом дорожной обстановки. Хорошей поездки.", "Время прибытия обновляется в реальном времени"],
      completed: ["Вы прибыли.", "Благодарим за поездку с MILES. Ваша квитанция доступна ниже.", "Завершена"],
      cancelled: ["Эта поездка была отменена.", "Водитель не будет отправлен. По любым вопросам пишите на info@gomiles.ch.", "Отменена"],
    },
    pickup_label: "Подача", arrival_label: "Прибытие (оценка)", completed_label: "Поездка завершена",
    your_driver: "Ваш водитель", driver_alt: "{name}, водитель MILES",
    call_driver: "Позвонить водителю", message_driver: "Написать водителю",
    pending: "Мы подтверждаем вашего водителя — имя, фото, автомобиль и номер появятся здесь автоматически.",
    meeting: "Место встречи: {pickup} — у главного входа{terminal}.",
    timeline_t: "Статусы поездки",
    flight_t: "Рейс отслеживается",
    flight_note: "В случае задержки рейса мы скорректируем подачу к реальному времени прилёта.",
    support_t: "Вопрос?", support_b: "Вам ответит живой человек",
    share: "Поделиться отслеживанием", share_t: "Ссылка скопирована", share_d: "Отправьте её тем, кто должен следить за поездкой.",
    receipt: "Квитанция — {code}", paid_badge: "Оплачено",
    waiting_line: "Ожидание и подача", included: "включено", total: "Итого (вкл. налоги)",
    rebook: "Заказать снова",
    hourly_dest: "Почасовая аренда",
    stops_label: "Остановки",
    nf_eyebrow: "Ссылка отслеживания", nf_t: "Бронирование не найдено",
    nf_b: "Проверьте ссылку из письма или обратитесь в поддержку.",
    nf_cta: "Связаться с поддержкой",
  },
};

const zh = {
  statuses: {
    booked: "已确认", driver_assigned: "司机已确认", driver_on_the_way: "司机在路上",
    driver_arrived: "司机已到达", on_board: "已上车", completed: "已完成", cancelled: "已取消",
  },
  tr: {
    eyebrow: "行程跟踪 — {code}",
    booked_by: "为您预订：",
    pos_stale: "最后位置更新于 {min} 分钟前",
    copy: {
      booked: ["我们正在为您准备行程。", "司机确认中——确认后您将收到电子邮件。", "按预定时间接驾"],
      driver_assigned: ["您的司机已确认。", "姓名、车辆和车牌信息如下。司机出发接您时，我们会通过电子邮件通知您。", "按预定时间接驾"],
      driver_on_the_way: ["您的司机已在路上。", "您可以在地图上实时查看他的位置。", "在路上"],
      driver_arrived: ["您的司机正在等您。", "他已到达约定的上车地点。", "已到达"],
      on_board: ["您已上车。", "路线已根据实时路况优化。祝您旅途愉快。", "预计到达时间实时更新"],
      completed: ["您已到达目的地。", "感谢您选择 MILES。您的收据如下。", "已完成"],
      cancelled: ["此预订已取消。", "不会派遣司机。如有疑问，请联系 info@gomiles.ch。", "已取消"],
    },
    pickup_label: "接驾时间", arrival_label: "预计到达", completed_label: "行程已完成",
    your_driver: "您的司机", driver_alt: "{name}，MILES 司机",
    call_driver: "致电司机", message_driver: "给司机发短信",
    pending: "我们正在确认您的司机——姓名、照片、车辆和车牌将自动显示在这里。",
    meeting: "上车地点：{pickup} — 主入口前{terminal}。",
    timeline_t: "行程状态",
    flight_t: "航班跟踪中",
    flight_note: "如航班延误，我们将根据实际到达时间调整接驾时间。",
    support_t: "有疑问？", support_b: "真人客服为您解答",
    share: "分享行程跟踪", share_t: "链接已复制", share_d: "可分享给需要关注此行程的人。",
    receipt: "收据 — {code}", paid_badge: "已支付",
    waiting_line: "等候与接驾服务", included: "已包含", total: "总计（含税）",
    rebook: "再次预订",
    hourly_dest: "按时包车",
    stops_label: "途经点",
    nf_eyebrow: "跟踪链接", nf_t: "未找到预订",
    nf_b: "请检查邮件中的链接，或联系客服。",
    nf_cta: "联系客服",
  },
};

const ar = {
  statuses: {
    booked: "مؤكد", driver_assigned: "تم تعيين السائق", driver_on_the_way: "السائق في الطريق",
    driver_arrived: "وصل السائق", on_board: "على متن السيارة", completed: "اكتملت", cancelled: "أُلغيت",
  },
  tr: {
    eyebrow: "تتبّع الرحلة — {code}",
    booked_by: "تم الحجز لك بواسطة",
    pos_stale: "آخر موقع قبل {min} دقيقة",
    copy: {
      booked: ["نُجهّز رحلتك.", "يجري تأكيد سائقك — ستصلك رسالة بريد إلكتروني بمجرد أن يصبح رسميًا.", "الانطلاق في الوقت المحدد"],
      driver_assigned: ["تم تأكيد سائقك.", "الاسم والمركبة ولوحة السيارة موضّحة أدناه. ستصلك رسالة بمجرد انطلاقه إليك.", "الانطلاق في الوقت المحدد"],
      driver_on_the_way: ["سائقك في الطريق إليك.", "تابِع تقدّمه مباشرةً على الخريطة.", "في الطريق"],
      driver_arrived: ["سائقك في انتظارك.", "إنه عند نقطة اللقاء المتفق عليها.", "لقد وصل"],
      on_board: ["أنت على متن السيارة.", "تم تحسين المسار حسب حركة المرور. رحلة سعيدة.", "يُعاد حساب وقت الوصول باستمرار"],
      completed: ["لقد وصلت.", "شكرًا لسفرك مع MILES. إيصالك متاح أدناه.", "اكتملت"],
      cancelled: ["تم إلغاء هذا الحجز.", "لن يتم إرسال أي سائق. لأي استفسار، تواصل معنا عبر info@gomiles.ch.", "أُلغيت"],
    },
    pickup_label: "الاصطحاب", arrival_label: "الوصول المتوقع", completed_label: "انتهت الرحلة",
    your_driver: "سائقك", driver_alt: "{name}، سائق MILES",
    call_driver: "الاتصال بالسائق", message_driver: "مراسلة السائق",
    pending: "نُؤكّد سائقك — سيظهر هنا تلقائيًا الاسم والصورة والمركبة ولوحة السيارة.",
    meeting: "نقطة اللقاء: {pickup} — أمام المدخل الرئيسي{terminal}.",
    terminal_join: "، ",
    timeline_t: "حالات الرحلة",
    flight_t: "تتبّع الرحلة الجوية",
    flight_note: "في حال تأخّر الرحلة، نُعدّل موعد الاصطحاب حسب وقت الهبوط الفعلي.",
    support_t: "هل لديك سؤال؟", support_b: "شخص حقيقي يردّ عليك",
    share: "مشاركة التتبّع", share_t: "تم نسخ رابط التتبّع", share_d: "شاركه مع من يريد متابعة هذه الرحلة.",
    receipt: "إيصال — {code}", paid_badge: "مدفوع",
    waiting_line: "الانتظار والاصطحاب", included: "مشمول", total: "الإجمالي شامل الضريبة",
    rebook: "احجز مرة أخرى",
    hourly_dest: "خدمة بالساعة",
    stops_label: "التوقفات",
    nf_eyebrow: "رابط التتبّع", nf_t: "الحجز غير موجود",
    nf_b: "تحقّق من الرابط الذي وصلك بالبريد الإلكتروني، أو تواصل مع الدعم.",
    nf_cta: "التواصل مع الدعم",
  },
};

const DICTS = { fr, en, de, ru, zh, ar };
export const LANGS = ["fr", "en", "de"];
export const TRACKING_LANGS = ["fr", "en", "de", "ru", "zh", "ar"];
export const LOCALES = { fr: "fr-CH", en: "en-GB", de: "de-CH", ru: "ru-RU", zh: "zh-CN", ar: "ar-AE" };

// Page chauffeur (/chauffeur/:token) — FR/EN/DE complets (page autonome,
// dictionnaire dédié pour ne pas alourdir les dictionnaires du site)
export const DRIVER_LANGS = ["fr", "en", "de"];
export const DRIVER_LANG_NAMES = { fr: "Français", en: "English", de: "Deutsch" };
export const DRIVER_I18N = {
  fr: {
    invalid: "Lien invalide ou course annulée.",
    status: {
      booked: "Course confirmée",
      driver_assigned: "Vous êtes attribué à cette course",
      driver_on_the_way: "En route vers le client",
      driver_arrived: "Arrivé au point de prise en charge",
      on_board: "Client à bord",
      completed: "Course terminée",
    },
    next: {
      driver_on_the_way: "En route",
      driver_arrived: "Arrivé",
      on_board: "Client à bord",
      completed: "Terminer la course",
    },
    pickup: "Prise en charge", dest: "Destination", hourly: "Mise à disposition {h} h",
    nav: "Ouvrir la navigation",
    share_t: "Partager ma position",
    share_on: "Position partagée — le client voit votre avancée en direct.",
    share_off: "Pendant la course, le client suit votre arrivée sur la carte.",
    sent: "Position envoyée à {time}",
    err_geo: "La localisation n'est pas disponible sur cet appareil.",
    err_denied: "Position refusée — autorisez la localisation pour partager votre avancée.",
    passenger: "Passager", pax: "{p} passager(s) · {l} bagage(s)",
    flight: "Vol {f}",
    stalled: "Position interrompue — gardez cette page ouverte au premier plan pour le suivi client.",
  },
  en: {
    invalid: "Invalid link or cancelled ride.",
    status: {
      booked: "Ride confirmed",
      driver_assigned: "You are assigned to this ride",
      driver_on_the_way: "On your way to the passenger",
      driver_arrived: "Arrived at the pickup point",
      on_board: "Passenger on board",
      completed: "Ride completed",
    },
    next: {
      driver_on_the_way: "On my way",
      driver_arrived: "Arrived",
      on_board: "Passenger on board",
      completed: "Complete the ride",
    },
    pickup: "Pickup", dest: "Destination", hourly: "Hourly hire {h} h",
    nav: "Open navigation",
    share_t: "Share my location",
    share_on: "Location shared — the passenger sees your progress live.",
    share_off: "During the ride, the passenger follows your arrival on the map.",
    sent: "Location sent at {time}",
    err_geo: "Location is not available on this device.",
    err_denied: "Location denied — allow location access to share your progress.",
    passenger: "Passenger", pax: "{p} passenger(s) · {l} bag(s)",
    flight: "Flight {f}",
    stalled: "Location interrupted — keep this page open in the foreground for live tracking.",
  },
  de: {
    invalid: "Ungültiger Link oder stornierte Fahrt.",
    status: {
      booked: "Fahrt bestätigt",
      driver_assigned: "Sie sind dieser Fahrt zugeteilt",
      driver_on_the_way: "Unterwegs zum Kunden",
      driver_arrived: "Am Abholort angekommen",
      on_board: "Kunde an Bord",
      completed: "Fahrt abgeschlossen",
    },
    next: {
      driver_on_the_way: "Unterwegs",
      driver_arrived: "Angekommen",
      on_board: "Kunde an Bord",
      completed: "Fahrt beenden",
    },
    pickup: "Abholung", dest: "Ziel", hourly: "Disposition {h} Std.",
    nav: "Navigation öffnen",
    share_t: "Meinen Standort teilen",
    share_on: "Standort geteilt — der Kunde sieht Ihren Fortschritt live.",
    share_off: "Während der Fahrt verfolgt der Kunde Ihre Ankunft auf der Karte.",
    sent: "Position gesendet um {time}",
    err_geo: "Standort ist auf diesem Gerät nicht verfügbar.",
    err_denied: "Standort verweigert — erlauben Sie den Standortzugriff, um Ihren Fortschritt zu teilen.",
    passenger: "Fahrgast", pax: "{p} Fahrgast/Fahrgäste · {l} Gepäckstück(e)",
    flight: "Flug {f}",
    stalled: "Position unterbrochen — lassen Sie diese Seite im Vordergrund geöffnet für das Live-Tracking.",
  },
};

const resolve = (obj, path) => path.split(".").reduce((o, k) => (o != null && o[k] !== undefined ? o[k] : undefined), obj);

// accès direct aux dictionnaires (prerender build : headFor sans React)
export const translate = (lang, key) => resolve(DICTS[lang], key) ?? resolve(fr, key) ?? key;

const LangContext = createContext({ lang: "fr", setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children, initialLang }) {
  const [lang, setLangState] = useState(() => {
    if (initialLang && LANGS.includes(initialLang)) return initialLang;
    // l'URL prime (pages localisées /fr /en /de), puis la préférence stockée
    try {
      const fromUrl = window.location.pathname.match(/^\/(fr|en|de)(\/|$)/)?.[1];
      if (fromUrl) return fromUrl;
      return localStorage.getItem("miles-lang") || "fr";
    } catch { return "fr"; }
  });

  const setLang = (l) => {
    if (!LANGS.includes(l)) return;
    setLangState(l);
    try { localStorage.setItem("miles-lang", l); } catch { /* noop */ }
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key) => resolve(DICTS[lang], key) ?? resolve(fr, key) ?? key, [lang]);

  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
